import React, { useEffect, useState, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import {
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import DeleteIcon from "@material-ui/icons/Delete";
import { Remove } from "@material-ui/icons";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import StopIcon from "@material-ui/icons/Stop";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import EditIcon from "@material-ui/icons/Edit";
import FolderIcon from "@material-ui/icons/Folder";
import DialogImageDelete from "./dialogImageDelete";
import DialogSoundDelete from "./dialogSoundDelete";
import DialogVideoDelete from "./dialogVideoDelete";
import DialogFontDelete from "./dialogFontDelete";
import DialogAtlasDelete from "./dialogAtlasDelete";
import DialogBitmapFontDelete from "./dialogBitmapFontDelete";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import CallMergeIcon from "@mui/icons-material/CallMerge";
import InnerComponentIcon from "../../images/gameConfig/Inner-component-icon.svg";
import { getServerUri } from "../modules/uriUtility";
import { sendCommand } from "../modules/playEngineBridge";
import * as previewQueue from "../modules/playEnginePreviewQueue";
import { switchPlayEngineLanguage } from "../modules/playEngineLocalization";
import { DEFAULT_BOX_SHADOW, formatBoxShadow, parseBoxShadow } from "../modules/boxShadowValue";
import {
  genAssetKey,
  mongoIdFromAssetEntry,
  resolveBoundAssetKey,
  resolvePlayEngineConfig,
  getAssetMapFromConfig,
  resolveAssetEntryLiveSource,
  collectBuckets,
  countBucketsAssets,
  normalizeBucket,
  collectAssetKeyUsages,
  ASSET_TYPES,
  NO_BUCKET,
  uploadFontAndGenerateBitmapFont,
} from "../modules/playEngineAssetUtils";
import {
  buildIdleAnimationComponentMap,
  getIdleAnimationEnabled,
  shouldShowIdleAnimationToggle,
} from "../modules/playEngineIdleAnimationUtils";
import BucketPicker from "./playEngineBucketPicker";
import {
  orderKeyForParent,
  orderKeyForCategory,
  orderKeyForCategoryFolderSiblings,
  ROOT_LAYOUT_SCOPE,
  rootLayoutEntryForComponent,
  rootLayoutEntryForFolder,
  parseRootLayoutEntry,
  splitRootLayoutOrder,
  resolveRootLayoutOrder,
  remapRootLayoutOrder,
  pruneRootLayoutOrder,
  sortSiblings,
} from "../modules/playEngineComponentOrderUtils";
import {
  buildCategoryTree,
  normalizeCategoryName,
  validateNewCategoryName,
  resolveTopLevelCategoryFolderOrder,
  parseCategoryPath,
  validateRenameCategoryName,
  buildRenamedCategoryPath,
  renameCategoryInMeta,
  deleteCategoryFromMeta,
  countComponentsInCategory,
  getSiblingCategoryNames,
} from "../modules/playEngineComponentCategoryUtils";
import { usePlayEngineSortListFlip, computePreviewIdsFromPointer, resetPlayEngineSortListFlip } from "../modules/playEngineComponentListFlip";
import { perfStart } from "../modules/playEnginePerf";
import _ from "lodash";
import PlayEngineTabSearch, { matchesTabSearch } from "./playEngineTabSearch";

function noopPerfEnd() {}

const CARD_BG = "#474747";

// Properties (bottom) panel — Unity/Blender-style inspector design tokens.
const PANEL_BG = "#2b2b2e";          // properties panel surface
const SECTION_DIVIDER = "#3a3a3f";   // hairline under section headers
const FIELD_LABEL = "#b8bcc4";       // property label text
const BAR_BG = "#3a3a41";            // title / toolbar bars (header, language, category)

// Debounce window for version/DB persist. Local UI and iframe preview update
// immediately; color fields debounce locally in ColorConfigField before commit.
const PLAY_ENGINE_SYNC_DEBOUNCE_MS = 200;

function DropdownRemoveButton({ onClick }) {
  return (
    <div style={{ marginLeft: "auto", flexShrink: 0 }}>
      <IconButton
        size="small"
        style={{ padding: 0, color: "#949494" }}
        onClick={onClick}
      >
        <Remove fontSize="small" />
      </IconButton>
    </div>
  );
}

/** Inspector section header — uppercase title with a hairline divider. */
function SectionHeader({ title, right }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px", margin: "12px 6px 6px" }}>
      <span style={{ flex: 1, height: "1px", backgroundColor: SECTION_DIVIDER }} />
      <span
        style={{
          fontSize: "11px",
          letterSpacing: "0.6px",
          textTransform: "uppercase",
          color: "#8a8d93",
          fontWeight: 600,
          flexShrink: 0,
        }}
      >
        {title}
      </span>
      <span style={{ flex: 1, height: "1px", backgroundColor: SECTION_DIVIDER }} />
      {right}
    </div>
  );
}

/**
 * Fields the dashboard sections on its own, whatever the engine's schema says.
 *
 * `group` is a schema field and the schema is read out of the engine bundle at
 * runtime, so a component still declaring `boxShadow` without one — every one
 * of them until an engine that says otherwise is deployed — would drop a
 * three-line control into the middle of the one-line Config fields. Named here
 * it gets its own section straight away, and agrees with the schema once that
 * catches up.
 */
const CONFIG_GROUP_BY_KEY = { boxShadow: "Shadow" };

function configGroupOf(key, configSchema) {
  return configSchema?.[key]?.group || CONFIG_GROUP_BY_KEY[key] || null;
}

/**
 * Splits config keys into the sections declared by `group` in the component's
 * configSchema. Ungrouped keys come first, so a component whose schema omits
 * `group` yields a single unsectioned list — exactly what it rendered before
 * grouping existed. Section order follows first appearance in the schema;
 * `keys` must already be sorted, since bucketing preserves their relative order.
 */
function groupConfigKeys(keys, configSchema) {
  const buckets = new Map();
  if (configSchema) {
    Object.keys(configSchema).forEach((k) => {
      const group = configSchema[k]?.group;
      if (group && !buckets.has(group)) buckets.set(group, []);
    });
  }

  const ungrouped = [];
  keys.forEach((key) => {
    const group = configGroupOf(key, configSchema);
    if (!group) {
      ungrouped.push(key);
      return;
    }
    // A group the schema did not declare lands after the ones it did.
    if (!buckets.has(group)) buckets.set(group, []);
    buckets.get(group).push(key);
  });

  const groups = [];
  buckets.forEach((groupKeys, title) => {
    if (groupKeys.length > 0) groups.push({ title, keys: groupKeys });
  });

  return { ungrouped, groups };
}

/**
 * Renders already-sorted config keys as top-level inspector sections, siblings
 * of Transform: ungrouped fields under "Config", then one section per group.
 *
 * A component whose schema declares no groups therefore renders exactly one
 * "Config" section, as it always did. Renders nothing when there are no keys,
 * so the caller does not have to gate on it.
 */
function renderConfigSections(keys, configSchema, renderOne) {
  const { ungrouped, groups } = groupConfigKeys(keys, configSchema);
  return (
    <>
      {ungrouped.length > 0 && (
        <>
          <SectionHeader title="Config" />
          <div>{ungrouped.map(renderOne)}</div>
        </>
      )}
      {groups.map((group) => (
        <React.Fragment key={group.title}>
          <SectionHeader title={group.title} />
          <div>{group.keys.map(renderOne)}</div>
        </React.Fragment>
      ))}
    </>
  );
}

// ─── emitter-op field (particle emitter polymorphic values) ────────────────

const EMITTER_OP_MODES = [
  { value: "constant", label: "Constant" },
  { value: "range", label: "Random range" },
  { value: "curve", label: "Over lifetime" },
  { value: "list", label: "Pick from list" },
];

/** Common GSAP eases. The stored value is added on the fly if it isn't listed. */
const EMITTER_OP_EASES = [
  "none",
  "sine.in", "sine.out", "sine.inOut",
  "power1.in", "power1.out", "power1.inOut",
  "power2.in", "power2.out", "power2.inOut",
  "power3.out", "power4.out",
  "expo.out", "circ.out",
  "back.out", "bounce.out", "elastic.out",
];

function emitterOpMode(value) {
  if (Array.isArray(value)) return "list";
  if (value && typeof value === "object") {
    if ("min" in value || "max" in value) return "range";
    if ("start" in value || "end" in value) return "curve";
    // Unrecognised object — show it as a constant so the mode picker can repair it.
    return "constant";
  }
  return "constant";
}

/** Best-effort low/high numbers for a value in any op form, used when switching modes. */
function emitterOpBounds(value) {
  if (typeof value === "number") return [value, value];
  if (Array.isArray(value)) {
    const nums = value.filter((v) => typeof v === "number");
    return nums.length > 0 ? [nums[0], nums[nums.length - 1]] : [0, 0];
  }
  if (value && typeof value === "object") {
    const lo = typeof value.min === "number" ? value.min
      : typeof value.start === "number" ? value.start : 0;
    const hi = typeof value.max === "number" ? value.max
      : typeof value.end === "number" ? value.end : lo;
    return [lo, hi];
  }
  return [0, 0];
}

/**
 * Rewrites a value into another op form, carrying over whatever numbers it can.
 *
 * Every optional key (`int`, `ease`, `random`) is always written out: the engine
 * merges incoming config into a default of the same shape key-by-key, so a
 * partial object would silently lose the keys the default does not declare.
 */
function convertEmitterOp(value, mode) {
  const [lo, hi] = emitterOpBounds(value);
  const obj = value && typeof value === "object" && !Array.isArray(value) ? value : {};

  switch (mode) {
    case "constant":
      return lo;
    case "range":
      return { min: lo, max: hi, int: obj.int === true };
    case "curve":
      return {
        start: lo,
        end: hi,
        ease: typeof obj.ease === "string" ? obj.ease : "none",
        random: obj.random === true,
      };
    case "list":
      return Array.isArray(value) ? value : [lo];
    default:
      return value;
  }
}

// ─── box-shadow field ──────────────────────────────────────────────────────

/**
 * A switch that turns the shadow on and off, four numbers, a colour with its
 * own alpha, and `inset` — over a preview that shows what they add up to, which
 * is the part a CSS string could never do.
 *
 * The parsing lives in modules/boxShadowValue.js, and refuses anything it
 * cannot round-trip exactly; this falls back to the plain text box for those.
 */
function BoxShadowConfigField({ value, disabled, onChange, action = null, tag = null }) {
  const raw = String(value ?? "").trim();
  const isNone = !raw || raw.toLowerCase() === "none";
  const shadow = isNone ? null : parseBoxShadow(raw);

  // Understood by the engine, not by this editor. Left exactly as it is.
  if (!isNone && !shadow) {
    return (
      <FieldRow label="CSS" readOnly={disabled} action={action}>
        {tag}
        <StringConfigField value={value} disabled={disabled} onChange={onChange} />
        <Typography variant="caption" style={{ color: "#7f838b", fontSize: "10px" }}>
          Kept as text — this editor only handles a single shadow with px offsets.
        </Typography>
      </FieldRow>
    );
  }

  const current = shadow || DEFAULT_BOX_SHADOW;
  const patch = (changes) => onChange(formatBoxShadow({ ...current, ...changes }));

  // One FieldRow per property, exactly like every other field in the panel:
  // same 96px label column, same control width, same right edge. The section
  // header is what says these six rows are one field.
  return (
    <>
      <FieldRow label="Enabled" readOnly={disabled} action={action}>
        {tag}
        <Switch
          size="small"
          color="primary"
          checked={!isNone}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked ? formatBoxShadow(current) : "")}
        />
      </FieldRow>

      {!isNone && (
        <>
          {/* Paired the way a vec2 is, and for the same reason: two numbers
              that are read together. */}
          <FieldRow label="Offset" readOnly={disabled}>
            <div style={{ display: "flex", gap: "6px" }}>
              <NumberConfigField
                value={current.x}
                disabled={disabled}
                label="x"
                onChange={(num) => patch({ x: num })}
              />
              <NumberConfigField
                value={current.y}
                disabled={disabled}
                label="y"
                onChange={(num) => patch({ y: num })}
              />
            </div>
          </FieldRow>

          <FieldRow label="Blur" readOnly={disabled}>
            <NumberConfigField
              value={current.blur}
              disabled={disabled}
              onChange={(num) => patch({ blur: num })}
            />
          </FieldRow>

          <FieldRow label="Spread" readOnly={disabled}>
            <NumberConfigField
              value={current.spread}
              disabled={disabled}
              onChange={(num) => patch({ spread: num })}
            />
          </FieldRow>

          {/* Alpha rides with the colour: it is part of the same value, and
              CSS only separates them because rgba() has to. */}
          <FieldRow label="Color" readOnly={disabled}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "6px" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ColorConfigField
                  value={current.color}
                  disabled={disabled}
                  onCommit={(hex) => patch({ color: hex })}
                />
              </div>
              <div style={{ width: "84px", flexShrink: 0 }}>
                <NumberConfigField
                  value={current.alpha}
                  disabled={disabled}
                  label="alpha"
                  onChange={(num) => patch({ alpha: Math.min(1, Math.max(0, num)) })}
                />
              </div>
            </div>
          </FieldRow>

          <FieldRow label="Inset" readOnly={disabled}>
            <Tooltip title="Draw the shadow inside the shape instead of around it">
              <Switch
                size="small"
                color="primary"
                checked={current.inset === true}
                disabled={disabled}
                onChange={(event) => patch({ inset: event.target.checked })}
              />
            </Tooltip>
          </FieldRow>
        </>
      )}
    </>
  );
}

/**
 * Aligned inspector row: [label] [control] [action]. Must live outside the
 * parent component — defining it inline remounts every input on each keystroke.
 */
function FieldRow({ label, readOnly = false, action = null, mixed = false, children }) {
  return (
    <div
      style={{
        display: "grid",
        // Capped control track keeps inputs from over-stretching and gives every
        // input the same right edge; the action track is `auto` so the split /
        // "mixed" indicators sit to the RIGHT of the input without ever
        // overlapping it.
        gridTemplateColumns: "96px minmax(0, 280px) auto",
        columnGap: "8px",
        alignItems: "center",
        minHeight: "30px",
        padding: "4px 6px",
      }}
    >
      <div
        title={typeof label === "string" ? label : undefined}
        style={{
          fontSize: "12px",
          color: readOnly ? "#6f7278" : FIELD_LABEL,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
      {/* Action cell: aligned to the bottom 24px (the input line) so the
          "mixed" tag / split button sit level with the control, not the
          (taller, for responsive fields) row's vertical center. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          alignSelf: "end",
          height: "24px",
          gap: "4px",
          whiteSpace: "nowrap",
        }}
      >
        {action}
        {mixed && (
          <Tooltip title="Values differ across the selected components" arrow>
            <span
              style={{
                fontSize: "9px",
                fontWeight: 600,
                letterSpacing: "0.4px",
                textTransform: "uppercase",
                color: "#c9a25b",
                cursor: "default",
              }}
            >
              mixed
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
}

function renderMediaSelectValue(value, records, folder, emptyLabel) {
  if (!value) return <em style={{ color: "#888" }}>{emptyLabel}</em>;
  const record = records.find((m) => String(m._id) === String(value));
  if (!record) return value;
  const name = record.uploadedFileName || record.fontFamily || record.name || record.file;
  return (
    <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      {record.thumbFile && (
        <img
          src={`${folder}/${record.thumbFile}`}
          alt=""
          style={{ height: "30px", maxWidth: "60px", objectFit: "contain", marginRight: "8px" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      )}
      <Typography variant="body2" noWrap style={{ maxWidth: "180px" }}>
        {name}
      </Typography>
    </div>
  );
}

function renderPairSelectValue(value, groups, pairConfig, emptyLabel) {
  if (!value) return <em style={{ color: "#888" }}>{emptyLabel}</em>;
  const group = groups.find((g) => g[pairConfig.groupRecordKey] === value);
  if (!group) return value;
  const record = group.imageRecord;
  const name = record?.uploadedFileName || record?.file || value;
  return (
    <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      {record?.thumbFile && (
        <img
          src={`${pairConfig.staticFolder}/${record.thumbFile}`}
          alt=""
          style={{ height: "30px", maxWidth: "60px", objectFit: "contain", marginRight: "8px" }}
          onError={(e) => { e.target.style.display = "none"; }}
        />
      )}
      <Typography variant="body2" noWrap style={{ maxWidth: "180px" }}>
        {name}
      </Typography>
    </div>
  );
}

/**
 * Humanize a camelCase / snake_case config key for display, like Unity's
 * NicifyVariableName: "splitMode" → "Split Mode", "fontURL" → "Font URL".
 * Display only — the raw key is still used for all logic.
 */
function humanizeKey(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")       // camelCase boundary
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")     // ACRONYMWord → ACRONYM Word
    .replace(/[_-]+/g, " ")                          // snake / kebab
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

/**
 * Multi-edit merge: given a component's current value (`cur`), the representative
 * old value the control was editing (`oldRep`), and the control's new value
 * (`newVal`), return `cur` with only the sub-values that actually CHANGED
 * overwritten. This lets editing one axis of a vec2 (or one orientation of a
 * responsive value) apply to every selected component while preserving each
 * component's own untouched sub-values.
 */
function applyConfigDelta(cur, oldRep, newVal) {
  const isObj = (v) => v && typeof v === "object" && !Array.isArray(v);
  if (!isObj(newVal) || !isObj(oldRep)) {
    // Leaf (or type change): take newVal only where it differs from the old
    // representative; otherwise keep this component's own value.
    if (_.isEqual(newVal, oldRep)) return cur === undefined ? newVal : cur;
    return newVal;
  }
  const base = isObj(cur) ? { ...cur } : {};
  for (const k of Object.keys(newVal)) {
    base[k] = applyConfigDelta(isObj(cur) ? cur[k] : undefined, oldRep[k], newVal[k]);
  }
  return base;
}

/** Returns a file-input `accept` attribute appropriate for the asset type */
function getAcceptString(depType) {
  switch (depType) {
    case "image": return ".png,.jpg,.jpeg,.gif,.webp";
    case "audio": return ".mp3,.ogg,.wav,.m4a,.aac";
    case "video": return ".mp4,.webm";
    case "font": return ".ttf,.otf,.woff,.woff2";
    case "atlas": return ".json,.png,.jpg,.jpeg";
    case "bitmap-font": return ".xml,.png,.jpg,.jpeg";
    default: return "*";
  }
}


/**
 * Sends a postMessage command to the playEngine iframe and waits for the
 * matching response (identified by requestId).
 *
 * @param {string} iFrameId  - DOM id of the game preview iframe
 * @param {object} payload   - message payload (without requestId)
 * @param {number} [timeout] - ms before the promise rejects (default 5000)
 * @returns {Promise<object>}
 */
/**
 * Version Settings panel rendered exclusively for PlayEngine bases.
 *
 * Fetches the live component list from the playEngine iframe via
 * `playengine:get-dependencies` and renders asset selectors for each
 * dependency. Uses the same server-side media system as regular games.
 * 
 * 
 *
 * Workflow per dependency:
 *   1. Resolve bucket via `playengine:get-bucket` (fallback "nobucket")
 *   2. List existing assets from props.media.images filtered by bucket
 *   3. User selects existing asset OR uploads a new one
 *   4. `playengine:set-component-asset` creates/reuses handle + wires to component
 *   5. DB config updated with @server: sourceRef on the dependency (no global assets[])
 *
 * @param props.selectedVersion              {Object}
 * @param props.selectedBase                 {Object}
 * @param props.selectedGame                 {Object}
 * @param props.iFrameId                     {string}
 * @param props.setSelectedVersionPathValue  {function}
 * @param props.userPermissions              {Object}
 * @param props.createSnackbar               {function}
 * @param props.media                        {Object}   { images: [...] }
 * @param props.insertMediaPathValue         {function}
 * @param props.setIsDialogCircularProgressOpen {function}
 */

/**
 * Controlled string text-field with local state so cursor position is
 * preserved on every keystroke. Fires `onChange` on every change (no
 * debounce — strings are always valid).
 */
function StringConfigField({ value, disabled, onChange }) {
  const [localValue, setLocalValue] = useState(String(value ?? ""));
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(String(value ?? ""));
  }, [value]);

  return (
    <TextField
      multiline
      minRows={1}
      maxRows={5}
      size="small"
      fullWidth
      disabled={disabled}
      value={localValue}
      inputRef={inputRef}
      InputProps={{ style: { color: "#fff", fontSize: "12px" } }}
      onKeyDown={(e) => e.stopPropagation()}
      onChange={(e) => {
        const raw = e.target.value;
        // Convert literally typed \n (backslash + n) into a real newline
        if (raw.includes("\\n")) {
          const cursor = e.target.selectionStart;
          const replaced = raw.replace(/\\n/g, "\n");
          const newCursor = raw.slice(0, cursor).replace(/\\n/g, "\n").length;
          setLocalValue(replaced);
          onChange(replaced);
          requestAnimationFrame(() => {
            if (inputRef.current) inputRef.current.setSelectionRange(newCursor, newCursor);
          });
        } else {
          setLocalValue(raw);
          onChange(raw);
        }
      }}
    />
  );
}

/**
 * Color picker + hex field with local state for instant display while dragging
 * or typing. Persist and continuous live-preview are debounced together so
 * rapid color changes never block the UI thread.
 */
function ColorConfigField({ value, disabled, onCommit }) {
  const [localValue, setLocalValue] = useState(String(value ?? "#000000"));
  const debounceRef = useRef(null);
  const latestRef = useRef(localValue);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  useEffect(() => {
    const v = String(value ?? "#000000");
    setLocalValue(v);
    latestRef.current = v;
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        onCommitRef.current(latestRef.current);
      }
    };
  }, []);

  function swatchHex(hex) {
    if (/^#[0-9a-f]{6}$/i.test(hex)) return hex;
    if (/^#[0-9a-f]{3}$/i.test(hex)) {
      const h = hex.slice(1);
      return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
    }
    return "#000000";
  }

  function scheduleCommit(hex) {
    latestRef.current = hex;
    setLocalValue(hex);
    if (disabled) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      onCommitRef.current(latestRef.current);
    }, PLAY_ENGINE_SYNC_DEBOUNCE_MS);
  }

  function handleHexChange(e) {
    const v = e.target.value;
    if (!/^#[0-9a-f]{0,6}$/i.test(v)) return;
    latestRef.current = v;
    setLocalValue(v);
    if (!/^#[0-9a-f]{3,6}$/i.test(v)) return;
    scheduleCommit(v);
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
      <input
        type="color"
        value={swatchHex(localValue)}
        disabled={disabled}
        style={{ width: "32px", height: "24px", border: "none", padding: 0, cursor: disabled ? "default" : "pointer" }}
        onChange={(e) => scheduleCommit(e.target.value)}
      />
      <TextField
        size="small"
        value={localValue}
        disabled={disabled}
        InputProps={{ style: { color: "#aaa", fontSize: "12px", fontFamily: "monospace" } }}
        style={{ width: "90px" }}
        onKeyDown={(e) => e.stopPropagation()}
        onChange={handleHexChange}
      />
    </div>
  );
}

const SCRUB_STEP = 0.01;
const SCRUB_STEP_SHIFT = 0.1;
const ARROW_STEP = 0.01;
const ARROW_STEP_SHIFT = 0.05;
const ARROW_REPEAT_DELAY_MS = 400;
const ARROW_REPEAT_INTERVAL_MS = 60;
const STEP_PIXEL = 14;
/** Shown instead of a number when the selected components disagree on it. */
const MIXED_VALUE_DASH = "—";


function roundToScrubStep(num, step) {
  const inv = 1 / step;
  return Math.round(num * inv) / inv;
}

/** Drop the tail binary floats grow when offsets are added up, keep the value. */
function roundFloatNoise(num) {
  return Math.round(num * 1e4) / 1e4;
}

/**
 * Controlled number text-field that lets the user type freely (delete all,
 * type "1.", etc.) but only fires `onChange` when the string is a complete,
 * valid, finite number — not empty, not a bare "-", not a trailing dot.
 *
 * Horizontal click-drag inside the field scrubs the value (Shift = step 0.1).
 * Drag sensitivity: STEP_PIXEL horizontal pixels ≈ one step increment.
 *
 * `mixed` says the selected components do not agree on this number. The field
 * then shows a dash rather than the first component's value, which is not the
 * value of anything the user selected — but it keeps that number underneath as
 * the base a drag moves from, and shows it again while the drag is happening so
 * the gesture has something to read.
 */
function NumberConfigField({ value, disabled, onChange, label, mixed = false }) {
  // Local display string so the user can type intermediary states freely.
  const [localValue, setLocalValue] = useState(String(value ?? ""));
  const [focused, setFocused] = useState(false);
  const [gesturing, setGesturing] = useState(false);
  const inputRef = useRef(null);
  const currentNumRef = useRef(0);
  const shiftHeldRef = useRef(false);
  const arrowRepeatRef = useRef(null);

  const showsDash = mixed && !focused && !gesturing;

  const inputCursor = disabled ? "not-allowed" : focused ? "text" : "ew-resize";

  function setInputCursor(cursor) {
    if (inputRef.current) inputRef.current.style.cursor = cursor;
  }

  // Sync when the prop value changes from outside (e.g. version switch).
  //
  // Not while the field is being typed into or scrubbed: the value arriving is
  // this field's own edit coming back around, and redrawing from it throws away
  // whatever has been typed since — "0.30" collapsing to "0.3" under the cursor.
  // Both the scrub and the keystroke handler keep currentNumRef current, so
  // skipping here leaves nothing stale behind.
  useEffect(() => {
    if (focused || gesturing) return;
    setLocalValue(String(value ?? ""));
    const n = Number(value);
    currentNumRef.current = isFinite(n) ? n : 0;
  }, [value, focused, gesturing]);

  useEffect(() => {
    setInputCursor(inputCursor);
  }, [inputCursor]);

  function clearArrowRepeat() {
    const active = arrowRepeatRef.current;
    if (!active) return;
    if (active.timeout) clearTimeout(active.timeout);
    if (active.interval) clearInterval(active.interval);
    if (active.cleanupListeners) active.cleanupListeners();
    arrowRepeatRef.current = null;
  }

  function parseScrubBase() {
    const trimmed = localValue.trim();
    if (trimmed === "" || trimmed === "-" || trimmed.endsWith(".")) {
      const fallback = Number(value);
      return isFinite(fallback) ? fallback : 0;
    }
    const num = Number(trimmed);
    return isFinite(num) ? num : 0;
  }

  /**
   * Scrubbing and the arrow buttons are gestures — "move it by this much". The
   * flag says so, which is what lets a multi-component edit shift each one from
   * where it already was. Typing a number is not a gesture: it means "be this",
   * and every selected component takes it as written.
   */
  function commitScrubValue(num, step) {
    const rounded = roundToScrubStep(num, step);
    currentNumRef.current = rounded;
    setLocalValue(String(rounded));
    onChange(rounded, { gesture: true, step });
  }

  function stepByArrow(direction, shiftKey) {
    const step = shiftKey ? ARROW_STEP_SHIFT : ARROW_STEP;
    commitScrubValue(currentNumRef.current + direction * step, step);
  }

  function handleArrowPointerDown(direction, e) {
    if (disabled || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();

    clearArrowRepeat();
    setGesturing(true);
    shiftHeldRef.current = e.shiftKey;
    stepByArrow(direction, shiftHeldRef.current);

    const trackShift = (ev) => {
      shiftHeldRef.current = ev.shiftKey;
    };
    document.addEventListener("keydown", trackShift);
    document.addEventListener("keyup", trackShift);

    const cleanupListeners = () => {
      document.removeEventListener("keydown", trackShift);
      document.removeEventListener("keyup", trackShift);
      document.removeEventListener("mouseup", onPointerUp);
    };

    function onPointerUp() {
      setGesturing(false);
      clearArrowRepeat();
    }

    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        stepByArrow(direction, shiftHeldRef.current);
      }, ARROW_REPEAT_INTERVAL_MS);
      if (arrowRepeatRef.current) arrowRepeatRef.current.interval = interval;
    }, ARROW_REPEAT_DELAY_MS);

    document.addEventListener("mouseup", onPointerUp);
    arrowRepeatRef.current = { timeout, interval: null, cleanupListeners };
  }

  useEffect(() => () => clearArrowRepeat(), []);

  function handleScrubMouseDown(e) {
    if (disabled || e.button !== 0) return;

    const startX = e.clientX;
    const startValue = parseScrubBase();
    let scrubbing = false;

    function onMouseMove(moveEvent) {
      const deltaX = moveEvent.clientX - startX;
      if (!scrubbing && Math.abs(deltaX) < 3) return;

      if (!scrubbing) {
        scrubbing = true;
        setGesturing(true);
        document.body.style.cursor = "ew-resize";
        document.body.style.userSelect = "none";
        setInputCursor("ew-resize");
        inputRef.current?.blur();
      }

      moveEvent.preventDefault();
      const step = moveEvent.shiftKey ? SCRUB_STEP_SHIFT : SCRUB_STEP;
      commitScrubValue(startValue + (deltaX / STEP_PIXEL) * step, step);
    }

    function onMouseUp() {
      setGesturing(false);
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      const isFocused = document.activeElement === inputRef.current;
      setInputCursor(disabled ? "not-allowed" : isFocused ? "text" : "ew-resize");
    }

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  function handleChange(e) {
    const str = e.target.value;
    setLocalValue(str);

    const trimmed = str.trim();
    if (trimmed === "") return;             // empty — don't fire
    if (trimmed.endsWith(".")) return;      // incomplete decimal like "1."
    if (trimmed === "-") return;            // bare minus
    const num = Number(trimmed);
    if (!isFinite(num)) return;             // NaN or ±Infinity
    currentNumRef.current = num;
    onChange(num);
  }

  return (
    <TextField
      type="text"
      inputMode="decimal"
      size="small"
      fullWidth
      disabled={disabled}
      value={showsDash ? MIXED_VALUE_DASH : localValue}
      label={label}
      inputRef={inputRef}
      InputProps={{
        style: { color: showsDash ? "#8a8d93" : "#fff", fontSize: "12px" },
        endAdornment: !disabled ? (
          <InputAdornment
            position="end"
            style={{ alignSelf: "center", marginRight: 6, height: "auto" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                lineHeight: 0,
              }}
            >
              <IconButton
                size="small"
                tabIndex={-1}
                title="Increment (+0.01, Shift +0.05)"
                style={{ padding: 0, width: 16, height: 10, color: "#aaa" }}
                onMouseDown={(e) => handleArrowPointerDown(1, e)}
              >
                <KeyboardArrowUpIcon style={{ fontSize: 13 }} />
              </IconButton>
              <IconButton
                size="small"
                tabIndex={-1}
                title="Decrement (−0.01, Shift −0.05)"
                style={{ padding: 0, width: 16, height: 10, color: "#aaa" }}
                onMouseDown={(e) => handleArrowPointerDown(-1, e)}
              >
                <KeyboardArrowDownIcon style={{ fontSize: 13 }} />
              </IconButton>
            </div>
          </InputAdornment>
        ) : undefined,
      }}
      inputProps={{
        readOnly: !focused,
        onMouseDown: handleScrubMouseDown,
        style: { cursor: inputCursor },
      }}
      InputLabelProps={label ? { style: { color: "#888", fontSize: "11px" } } : undefined}
      style={label ? { flex: 1 } : undefined}
      onKeyDown={(e) => e.stopPropagation()}
      onFocus={() => {
        setFocused(true);
        // Typing into a dash would produce "-5" out of a "5" keystroke, and the
        // first component's number is not what the field was showing either —
        // so an edit starts from empty and whatever is typed lands on them all.
        if (mixed) setLocalValue("");
      }}
      onBlur={() => {
        setFocused(false);
        // Half-typed text ("1.", "-") never reached the components; the field
        // goes back to reporting them rather than keeping it on screen.
        setLocalValue(String(value ?? ""));
      }}
      onChange={handleChange}
    />
  );
}

export default function containerPlayEngineVersionSettings(props) {
  // Profiling only: time from render start to committed DOM for this panel.
  const endPanelRenderRef = useRef(noopPerfEnd);
  endPanelRenderRef.current = perfStart("panel:render+commit");
  useLayoutEffect(() => {
    endPanelRenderRef.current();
  });

  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const retryTimerRef = useRef(null);

  // Per-field debounce timers for persist only.
  const configPersistTimersRef = useRef(new Map());
  // Latest pending value per field — read when a debounced timer fires.
  const pendingConfigRef = useRef(new Map());

  // Tracks the version _id for which we have already successfully fetched from
  // the iframe. Used to skip re-fetching on playengine:ready (manual refresh)
  // when neither the version nor the preset has changed.
  const fetchedForVersionRef = useRef(null);

  // Armed to true when a preset is applied so the next playengine:ready
  // triggers a fresh panel fetch even though the version _id is unchanged.
  const pendingRefetchRef = useRef(false);

  // Fallback timer armed on playengine:ready. The panel prefers to fetch on the
  // engine's true-ready signal (playengine:playable-ready, fired after the engine
  // installs its command listener). playengine:ready arrives BEFORE that, so a
  // fetch issued on ready would hit a 5s timeout + 2s retry. This timer only
  // fires for older engine builds that never emit playable-ready; playable-ready
  // cancels it.
  const readyFallbackTimerRef = useRef(null);

  // Always holds the CURRENT selectedVersion._id — updated synchronously in
  // the version-change effect so that async callbacks (fetchComponents.then)
  // can detect stale responses without relying on a stale props closure.
  const currentVersionIdRef = useRef(_.get(props, "selectedVersion._id"));

  // componentId → bucket name (resolved from playEngine or "nobucket")
  const [bucketMap, setBucketMap] = useState({});

  // Track which dependency is currently being processed (uploading / setting)
  const [busyDeps, setBusyDeps] = useState({});

  // Staged files for atlas two-step upload: keyed by "componentId:depKey"
  const [stagedAtlasFiles, setStagedAtlasFiles] = useState({});

  // Sound preview: tracks which "componentId:depKey" is currently playing
  const [playingDepKey, setPlayingDepKey] = useState(null);
  const audioRef = useRef(null);

  // Track which tree items are expanded so they survive re-renders
  const [expandedItems, setExpandedItems] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");

  // Blender-style layout: the component tree (top panel) drives which components'
  // settings show in the properties (bottom) panel. `selectedComponentIds` holds
  // the selected tree items (multi-select via Ctrl/Cmd/Shift); `treeRatio` is the
  // draggable split ratio.
  const [selectedComponentIds, setSelectedComponentIds] = useState([]);
  const containerRef = useRef(null);
  const [treeRatio, setTreeRatio] = useState(() => {
    const raw = Number(window.localStorage.getItem("playEngineVersionSettingsSplit"));
    return isFinite(raw) && raw >= 0.2 && raw <= 0.8 ? raw : 0.5;
  });
  // The tab content is wrapped in an auto-height <Box>/<span>, so a percentage
  // height collapses. Measure a definite pixel height down to the viewport
  // bottom so the two flex panels (flex-basis:0) can split it.
  const [availHeight, setAvailHeight] = useState(0);

  // Component list reorder — live preview while dragging; persisted on pointer up
  const [dragSession, setDragSession] = useState(null);
  const dragSessionRef = useRef(null);
  const dragGhostRef = useRef(null);
  const skipFlipOnDropRef = useRef(false);
  const isDraggingSortRef = useRef(false);
  /** Holds final order between pointer-up and props sync — prevents snap + stray FLIP. */
  const [pendingComponentOrder, setPendingComponentOrder] = useState(null);
  const [pendingCategoryOrder, setPendingCategoryOrder] = useState(null);
  const [pendingRootLayoutOrder, setPendingRootLayoutOrder] = useState(null);
  dragSessionRef.current = dragSession;

  const [categoryDropTarget, setCategoryDropTarget] = useState(null);
  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [renameCategoryState, setRenameCategoryState] = useState(null);
  const [renameCategoryName, setRenameCategoryName] = useState("");
  const [deleteCategoryState, setDeleteCategoryState] = useState(null);

  const componentCategoriesMap = _.get(props, "selectedVersion.dashboardMeta.componentCategories", {}) || {};
  const componentCategoryFolders = _.get(props, "selectedVersion.dashboardMeta.componentCategoryFolders", []) || [];
  const componentCategoryChildOrder = _.get(props, "selectedVersion.dashboardMeta.componentCategoryChildOrder", {}) || {};
  const rootLayoutOrder = _.get(props, "selectedVersion.dashboardMeta.rootLayoutOrder", null);
  const topLevelCategoryNames = useMemo(
    () => resolveTopLevelCategoryFolderOrder(componentCategoriesMap, componentCategoryFolders),
    [componentCategoriesMap, componentCategoryFolders]
  );

  const uncategorizedComponentIds = useMemo(
    () => components
      .filter((c) => !c.parentId && !componentCategoriesMap[c.componentId])
      .map((c) => c.componentId),
    [components, componentCategoriesMap]
  );

  const baseRootLayoutOrder = useMemo(
    () => resolveRootLayoutOrder(
      rootLayoutOrder,
      uncategorizedComponentIds,
      topLevelCategoryNames,
      componentOrderMap?.root
    ),
    [rootLayoutOrder, uncategorizedComponentIds, topLevelCategoryNames, componentOrderMap]
  );

  const effectiveRootLayoutOrder = useMemo(() => {
    if (dragSession?.scopeKey === ROOT_LAYOUT_SCOPE && dragSession.previewIds) {
      return dragSession.previewIds;
    }
    if (pendingRootLayoutOrder) return pendingRootLayoutOrder;
    return baseRootLayoutOrder;
  }, [baseRootLayoutOrder, dragSession, pendingRootLayoutOrder]);

  const effectiveTopLevelFolderOrder = useMemo(
    () => splitRootLayoutOrder(effectiveRootLayoutOrder).folderNames,
    [effectiveRootLayoutOrder]
  );

  function updateDragGhostTop(clientY) {
    const session = dragSessionRef.current;
    const ghost = dragGhostRef.current;
    if (!session || !ghost) return;
    ghost.style.top = `${clientY - session.grabOffsetY}px`;
  }

  const componentOrderMap = _.get(props, "selectedVersion.dashboardMeta.componentOrder", null);

  const effectiveOrderMap = useMemo(() => {
    const base = componentOrderMap || {};
    if (dragSession?.previewIds?.length && dragSession.scopeKey) {
      return { ...base, [dragSession.scopeKey]: dragSession.previewIds };
    }
    if (pendingComponentOrder) {
      return { ...base, ...pendingComponentOrder };
    }
    return componentOrderMap;
  }, [componentOrderMap, dragSession, pendingComponentOrder]);

  const effectiveCategoryChildOrder = useMemo(() => {
    const base = componentCategoryChildOrder || {};
    if (
      dragSession?.dragKind === "category" &&
      dragSession.scopeKey?.startsWith("categoryFolder:") &&
      dragSession.scopeKey !== "categoryFolder:root" &&
      dragSession.previewIds
    ) {
      const parentPath = dragSession.scopeKey.slice("categoryFolder:".length);
      return { ...base, [parentPath]: dragSession.previewIds };
    }
    if (pendingCategoryOrder) {
      return { ...base, ...pendingCategoryOrder };
    }
    return componentCategoryChildOrder;
  }, [componentCategoryChildOrder, dragSession, pendingCategoryOrder]);

  const categoryChildOrderScopeMap = useMemo(() => {
    const scoped = {};
    Object.entries(effectiveCategoryChildOrder || {}).forEach(([parentPath, names]) => {
      scoped[orderKeyForCategoryFolderSiblings(parentPath)] = names;
    });
    return scoped;
  }, [effectiveCategoryChildOrder]);

  const sortAnimationKey = useMemo(() => {
    if (dragSession?.previewIds) {
      return `drag:${dragSession.scopeKey}:${dragSession.previewIds.join(",")}`;
    }
    if (pendingComponentOrder) {
      return `pending:${Object.entries(pendingComponentOrder).map(([k, v]) => `${k}:${v.join(",")}`).join("|")}`;
    }
    const entries = Object.entries(componentOrderMap || {}).sort(([a], [b]) => a.localeCompare(b));
    const orderPart = entries.map(([k, v]) => `${k}:${(v || []).join(",")}`).join("|");
    const folderPart = effectiveTopLevelFolderOrder.join(",");
    const rootLayoutPart = (effectiveRootLayoutOrder || []).join(",");
    const childPart = Object.entries(effectiveCategoryChildOrder || {})
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${(v || []).join(",")}`)
      .join("|");
    return `${orderPart}::root:${rootLayoutPart}::folders:${folderPart}::child:${childPart}`;
  }, [dragSession, pendingComponentOrder, componentOrderMap, effectiveTopLevelFolderOrder, effectiveRootLayoutOrder, effectiveCategoryChildOrder]);

  usePlayEngineSortListFlip(sortAnimationKey, skipFlipOnDropRef, isDraggingSortRef);

  useEffect(() => {
    if (!pendingComponentOrder) return;
    const synced = Object.entries(pendingComponentOrder).every(([key, ids]) =>
      _.isEqual(_.get(props, "selectedVersion.dashboardMeta.componentOrder", {})?.[key], ids)
    );
    if (synced) setPendingComponentOrder(null);
  }, [componentOrderMap, pendingComponentOrder, props.selectedVersion?.dashboardMeta?.componentOrder]);

  useEffect(() => {
    if (!pendingCategoryOrder) return;
    const synced = Object.entries(pendingCategoryOrder).every(([parentPath, ids]) =>
      _.isEqual(componentCategoryChildOrder?.[parentPath], ids)
    );
    if (synced) setPendingCategoryOrder(null);
  }, [componentCategoryChildOrder, pendingCategoryOrder]);

  useEffect(() => {
    if (!pendingRootLayoutOrder) return;
    if (_.isEqual(rootLayoutOrder, pendingRootLayoutOrder)) {
      setPendingRootLayoutOrder(null);
    }
  }, [rootLayoutOrder, pendingRootLayoutOrder]);

  useEffect(() => {
    if (!dragSession) return undefined;
    const prev = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.userSelect = prev;
    };
  }, [dragSession]);

  useEffect(() => {
    if (!dragSession) return undefined;

    function onPointerMove(e) {
      const session = dragSessionRef.current;
      if (!session) return;

      updateDragGhostTop(e.clientY);

      const catTarget = session.canCategorize
        ? resolveCategoryDropTarget(e.clientX, e.clientY)
        : null;
      setCategoryDropTarget((prev) => (prev === catTarget ? prev : catTarget));

      const nextBase = { ...session, pointerX: e.clientX, pointerY: e.clientY };

      if (shouldPreviewReorderDuringDrag(session, catTarget, componentCategoriesMap)) {
        const newIds = computePreviewIdsFromPointer(
          e.clientY,
          session.scopeKey,
          session.draggedId,
          session.previewIds
        );

        if (!_.isEqual(newIds, session.previewIds)) {
          resetPlayEngineSortListFlip();
          dragSessionRef.current = { ...nextBase, previewIds: newIds };
          setDragSession(dragSessionRef.current);
        } else {
          dragSessionRef.current = nextBase;
        }
      } else {
        dragSessionRef.current = nextBase;
      }
    }

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    return () => window.removeEventListener("pointermove", onPointerMove);
  }, [dragSession?.draggedId, dragSession?.scopeKey, componentCategoriesMap]);

  useLayoutEffect(() => {
    if (!dragSession) return;
    updateDragGhostTop(dragSession.pointerY);
  }, [dragSession?.draggedId]);

  useEffect(() => {
    if (!dragSession) return undefined;

    function commitDragSession() {
      const session = dragSessionRef.current;
      if (!session) return;
      if (session.captureTarget?.releasePointerCapture) {
        try {
          session.captureTarget.releasePointerCapture(session.pointerId);
        } catch (_) { /* ignore */ }
      }

      const { scopeKey, previewIds, initialIds, draggedId, canCategorize, pointerX, pointerY, dragKind, componentId: sessionComponentId } = session;
      const orderChanged = previewIds && !_.isEqual(previewIds, initialIds);
      const componentDragId = sessionComponentId
        || parseRootLayoutEntry(draggedId)?.id
        || draggedId;

      const catIntent = canCategorize && dragKind === "component"
        ? getCategoryDropIntent(
          componentDragId,
          resolveCategoryDropTarget(pointerX, pointerY),
          componentCategoriesMap
        )
        : null;

      dragSessionRef.current = null;
      isDraggingSortRef.current = false;
      skipFlipOnDropRef.current = true;
      resetPlayEngineSortListFlip();
      setCategoryDropTarget(null);
      setDragSession(null);

      if (catIntent) {
        if (catIntent === "uncategorized") {
          assignComponentCategory(componentDragId, null);
        } else {
          assignComponentCategory(componentDragId, catIntent);
          const segments = parseCategoryPath(catIntent);
          if (segments.length > 0) {
            const idsToExpand = segments.map((_, i) => `cat-${segments.slice(0, i + 1).join("-")}`);
            setExpandedItems((prev) => [...new Set([...prev, ...idsToExpand])]);
          }
        }
        return;
      }

      if (!orderChanged) return;

      if (props.userPermissions?.update === false) {
        setPendingComponentOrder(null);
        setPendingCategoryOrder(null);
        setPendingRootLayoutOrder(null);
        props.createSnackbar?.(
          "You do not have permission to update, contact your administrator.",
          "warning",
          false
        );
        return;
      }

      if (scopeKey === ROOT_LAYOUT_SCOPE) {
        persistRootLayoutOrder(previewIds);
        return;
      }

      if (dragKind === "category") {
        const parentPath = scopeKey.slice("categoryFolder:".length);
        const nextChildOrder = _.cloneDeep(componentCategoryChildOrder || {});
        nextChildOrder[parentPath] = previewIds;
        setPendingCategoryOrder({ [parentPath]: previewIds });
        props.setSelectedVersionPathValue({
          path: "dashboardMeta.componentCategoryChildOrder",
          value: nextChildOrder,
        });
        return;
      }

      setPendingComponentOrder({ [scopeKey]: previewIds });
      const nextOrder = _.cloneDeep(componentOrderMap || {});
      nextOrder[scopeKey] = previewIds;
      props.setSelectedVersionPathValue({
        path: "dashboardMeta.componentOrder",
        value: nextOrder,
      });
    }

    window.addEventListener("pointerup", commitDragSession);
    window.addEventListener("pointercancel", commitDragSession);
    return () => {
      window.removeEventListener("pointerup", commitDragSession);
      window.removeEventListener("pointercancel", commitDragSession);
    };
  }, [
    dragSession?.draggedId,
    componentOrderMap,
    componentCategoriesMap,
    componentCategoryChildOrder,
    props.userPermissions?.update,
    props.createSnackbar,
    props.setSelectedVersionPathValue,
  ]);

  function resolveCategoryDropTarget(clientX, clientY) {
    const el = document.elementFromPoint(clientX, clientY);
    const dropEl = el?.closest("[data-category-drop]");
    if (dropEl) return dropEl.getAttribute("data-category-drop");

    const rootZone = document.querySelector("[data-playengine-root-zone]");
    if (rootZone && rootZone.contains(el)) return "uncategorized";

    return null;
  }

  function getCategoryDropIntent(draggedId, catTarget, categoriesMap) {
    if (!catTarget) return null;
    const current = categoriesMap[draggedId] || "";
    if (catTarget === "uncategorized") return current ? "uncategorized" : null;
    if (catTarget !== current) return catTarget;
    return null;
  }

  function shouldPreviewReorderDuringDrag(session, catTarget, categoriesMap) {
    if (!session?.canCategorize) return true;
    return !getCategoryDropIntent(session.draggedId, catTarget, categoriesMap);
  }

  function assignComponentCategory(componentId, categoryPath) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const next = _.cloneDeep(componentCategoriesMap);
    if (!categoryPath) {
      delete next[componentId];
    } else {
      const normalized = parseCategoryPath(categoryPath).join(":");
      if (!normalized) {
        delete next[componentId];
      } else {
        next[componentId] = normalized;
      }
    }
    props.setSelectedVersionPathValue({
      path: "dashboardMeta.componentCategories",
      value: next,
    });
  }

  function persistRootLayoutOrder(previewIds) {
    const { componentIds, folderNames } = splitRootLayoutOrder(previewIds);
    const nextOrder = _.cloneDeep(componentOrderMap || {});
    nextOrder.root = componentIds;
    const meta = _.cloneDeep(_.get(props, "selectedVersion.dashboardMeta", {}) || {});
    meta.rootLayoutOrder = previewIds;
    meta.componentOrder = nextOrder;
    meta.componentCategoryFolders = folderNames;
    setPendingRootLayoutOrder(previewIds);
    props.setSelectedVersionPathValue({ path: "dashboardMeta", value: meta });
  }

  function persistCategoryMetaBatch({ categories, folders, order, childOrder, rootLayout }) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const meta = _.cloneDeep(_.get(props, "selectedVersion.dashboardMeta", {}) || {});
    if (categories !== undefined) meta.componentCategories = categories;
    if (folders !== undefined) meta.componentCategoryFolders = folders;
    if (order !== undefined) meta.componentOrder = order;
    if (childOrder !== undefined) meta.componentCategoryChildOrder = childOrder;
    if (rootLayout !== undefined) meta.rootLayoutOrder = rootLayout;
    props.setSelectedVersionPathValue({ path: "dashboardMeta", value: meta });
  }

  function handleCreateCategory() {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const existing = new Set(topLevelCategoryNames);
    const err = validateNewCategoryName(newCategoryName, existing);
    if (err) {
      props.createSnackbar?.(err, "warning", false);
      return;
    }
    const name = normalizeCategoryName(newCategoryName);
    const folders = [...componentCategoryFolders];
    if (!folders.includes(name)) folders.push(name);
    props.setSelectedVersionPathValue({
      path: "dashboardMeta.componentCategoryFolders",
      value: folders,
    });
    setExpandedItems((prev) => (prev.includes(`cat-${name}`) ? prev : [...prev, `cat-${name}`]));
    setCreateCategoryOpen(false);
    setNewCategoryName("");
  }

  function handleRenameCategory() {
    if (!renameCategoryState) return;
    const { categoryPath, name } = renameCategoryState;
    const newPath = buildRenamedCategoryPath(categoryPath, renameCategoryName);
    if (newPath === categoryPath) {
      setRenameCategoryState(null);
      setRenameCategoryName("");
      return;
    }
    const siblings = getSiblingCategoryNames(categoryTree, categoryPath);
    const siblingSet = new Set(siblings);
    const err = validateRenameCategoryName(renameCategoryName, siblingSet, name);
    if (err) {
      props.createSnackbar?.(err, "warning", false);
      return;
    }
    const { categories, folders, order, childOrder } = renameCategoryInMeta(
      componentCategoriesMap,
      componentCategoryFolders,
      componentOrderMap,
      componentCategoryChildOrder,
      categoryPath,
      newPath
    );
    persistCategoryMetaBatch({
      categories,
      folders,
      order,
      childOrder,
      rootLayout: remapRootLayoutOrder(rootLayoutOrder, categoryPath, newPath),
    });

    const oldItemId = `cat-${categoryPath.replace(/:/g, "-")}`;
    const newItemId = `cat-${newPath.replace(/:/g, "-")}`;
    setExpandedItems((prev) => prev.map((id) => (id === oldItemId ? newItemId : id)));
    setRenameCategoryState(null);
    setRenameCategoryName("");
  }

  function handleDeleteCategory() {
    if (!deleteCategoryState) return;
    const { categoryPath } = deleteCategoryState;
    const { categories, folders, order, childOrder } = deleteCategoryFromMeta(
      componentCategoriesMap,
      componentCategoryFolders,
      componentOrderMap,
      componentCategoryChildOrder,
      categoryPath
    );
    persistCategoryMetaBatch({ categories, folders, order, childOrder, rootLayout: pruneRootLayoutOrder(rootLayoutOrder, categoryPath) });
    const itemId = `cat-${categoryPath.replace(/:/g, "-")}`;
    setExpandedItems((prev) => prev.filter((id) => id !== itemId && !id.startsWith(`${itemId}-`)));
    setDeleteCategoryState(null);
  }


  const [mediaDeleteDialog, setMediaDeleteDialog] = useState({
    kind: null,
    open: false,
    record: null,
    groupId: null,
    selectedId: null,
  });
  const mediaDeleteRebindRef = useRef(null);

  // Selected localization language — restore the last active language from the saved snapshot,
  // falling back to the first supported language (usually "en").
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const savedLang = _.get(props, "selectedVersion.playEngineConfig.localization.currentLanguage");
    if (savedLang) return savedLang;
    const langs = _.get(props, "selectedBase.supportedLanguages", {});
    return Object.keys(langs)[0] || "";
  });

  useEffect(() => {
    const savedLang = _.get(props, "selectedVersion.playEngineConfig.localization.currentLanguage");
    const lang = savedLang || Object.keys(_.get(props, "selectedBase.supportedLanguages", {}))[0] || "en";
    setSelectedLanguage(lang);
    props.setLocalizationLanguage?.(lang);
  }, [_.get(props, "selectedBase._id"), _.get(props, "selectedVersion._id")]);

  // An embedder that shows several playEngine panels side by side (the snapshot
  // editor) owns the language for all of them, so a switch made in another tab
  // arrives here as a prop. The engine has already been switched by whoever
  // made the change — only this panel's own view has to catch up.
  useEffect(() => {
    const external = props.localizationLanguage;
    if (!external || external === selectedLanguage) return;
    setSelectedLanguage(external);
    const timer = setTimeout(fetchComponents, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.localizationLanguage]);

  const playEngineConfig = useMemo(
    () => resolvePlayEngineConfig(props.selectedVersion, props.versions),
    [props.selectedVersion, props.versions]
  );

  // ─── Where assets come from ──────────────────────────────────────────────
  //
  // This panel was written against the company media library: pick an existing
  // record, or upload one and bind the dependency to it by "@server:<id>".
  // The snapshot editor has no library — a snapshot carries its assets
  // inline, and its export path reads the file directly and cannot resolve an
  // "@server:" reference. It therefore passes an `assetProvider` that reports no
  // library and inlines uploads instead of storing them.
  //
  // Absent prop = the dashboard behaviour, unchanged.
  const hasAssetLibrary = _.get(props, "assetProvider.hasLibrary", true);
  const canDeleteFromLibrary = _.get(props, "assetProvider.canDeleteFromLibrary", true);
  const providerUploadAsset = _.get(props, "assetProvider.uploadAsset", null);
  const providerUploadAssetPair = _.get(props, "assetProvider.uploadAssetPair", null);
  // Optional: an embedder can offer its own asset library (the snapshot
  // editor points this at the scraped library). Absent in the version editor,
  // which has the mongo media pickers above instead — so nothing renders there.
  const providerPickFromLibrary = _.get(props, "assetProvider.pickFromLibrary", null);

  // Company-wide buckets (all asset types) for the per-component bucket picker.
  const allBuckets = useMemo(
    () => collectBuckets(props, playEngineConfig),
    [props.media, props.atlases, props.bitmapFonts, props.sounds, props.fonts, props.videos, playEngineConfig]
  );
  // Per-type, per-bucket pickable-asset counts. Each component's picker then
  // shows only the counts for the asset types it actually depends on.
  const bucketCountsByType = useMemo(() => {
    const names = [...allBuckets, NO_BUCKET];
    const res = {};
    for (const type of ASSET_TYPES) res[type] = countBucketsAssets(props, names, { type });
    return res;
  }, [props.media, props.atlases, props.bitmapFonts, props.sounds, props.fonts, props.videos, allBuckets]);
  // Keep the displayed component buckets in sync with playEngineConfig. The
  // Assets tab can re-bucket a component (it persists comp.bucket on the shared
  // version state); without this, Version Settings would keep showing the stale
  // engine-fetched bucket until a full reload.
  useEffect(() => {
    const comps = _.get(playEngineConfig, "components");
    if (!Array.isArray(comps) || comps.length === 0) return;
    setBucketMap((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const c of comps) {
        if (!c || !c.id || c.bucket == null) continue;
        const b = normalizeBucket(c.bucket);
        if (next[c.id] !== b) {
          next[c.id] = b;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [playEngineConfig]);

  const assetsMap = useMemo(() => getAssetMapFromConfig(playEngineConfig), [playEngineConfig]);

  const idleAnimationByComponent = useMemo(
    () => buildIdleAnimationComponentMap(_.get(playEngineConfig, "eventBindings") || []),
    [playEngineConfig]
  );

  const assetsByType = useMemo(() => {
    const map = {};
    for (const [assetKey, entry] of Object.entries(assetsMap)) {
      const type = entry?.type;
      if (!type) continue;
      if (!map[type]) map[type] = [];
      map[type].push({ assetKey, entry });
    }
    for (const type of Object.keys(map)) {
      map[type].sort((a, b) => a.assetKey.localeCompare(b.assetKey));
    }
    return map;
  }, [assetsMap]);

  // ─── Language switch handler ─────────────────────────────────────────────

  /**
   * Called when the user changes the language dropdown.
   *   1. If the language isn't in the version yet, add it (playEngine + DB)
   *   2. Switch to that language in the playEngine
   *   3. Persist currentLanguage + availableLanguages to DB
   *   4. Re-fetch components so the UI reflects any overrides
   */
  async function handleLanguageSwitch(newLang) {
    if (newLang === selectedLanguage) return;

    // Queued preview edits belong to the language being left, so they go out
    // before the switch rather than after it.
    previewQueue.flush(props.iFrameId);

    // Registering, switching and persisting the language is shared with the
    // Custom Variables panel — see modules/playEngineLocalization.
    await switchPlayEngineLanguage({
      iFrameId: props.iFrameId,
      selectedVersion: props.selectedVersion,
      setSelectedVersionPathValue: props.setSelectedVersionPathValue,
      language: newLang,
    });

    // Update local state + re-fetch to reflect overridden visuals
    setSelectedLanguage(newLang);
    props.setLocalizationLanguage?.(newLang);
    setTimeout(fetchComponents, 300);
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────

  function getPairDepConfig(depType) {
    if (depType === "bitmap-font") {
      return {
        groupField: "fontSet",
        groupRecordKey: "fontSet",
        metaVariant: "xml",
        metaKey: "xml",
        metaRecordKey: "xmlRecord",
        metaLabel: "XML",
        metaAccept: ".xml",
        staticFolder: "bitmapFonts",
        uploadEndpoint: "bitmapFont/uploadBitmapFont",
        uploadGroupIdKey: "fontSetID",
        insertRecords: (records) => props.insertBitmapFontPathValue?.(records),
        rewriteMeta: (url) => url,
        metaFileKey: "xmlFile",
        uploadMetaVariant: "xml",
      };
    }
    return {
      groupField: "animationSet",
      groupRecordKey: "animationSet",
      metaVariant: "json",
      metaKey: "json",
      metaRecordKey: "jsonRecord",
      metaLabel: "JSON",
      metaAccept: ".json",
      staticFolder: "atlases",
      uploadEndpoint: "atlas/uploadAtlas",
      uploadGroupIdKey: "animationSetID",
      insertRecords: (records) => props.insertAtlasPathValue?.(records),
      rewriteMeta: rewriteAsJsonDataUrl,
      metaFileKey: "jsonFile",
      uploadMetaVariant: "JSON",
    };
  }

  function isPairDepType(depType) {
    return depType === "atlas" || depType === "bitmap-font";
  }

  /**
   * Build a lightweight source reference for DB storage.
   * - Simple types → "@server:<mongoId>"
   * - Atlas → { image, json }
   * - Bitmap font → { image, xml }
   */
  function buildServerSource(depType, mongoId, _base64Data, pairGroup = null) {
    // No library means nothing to reference: the asset's bytes are the source.
    // This is what keeps a snapshot self-contained and exportable.
    if (!hasAssetLibrary) return _base64Data;

    if (depType === "atlas") {
      if (pairGroup?.imageRecord && pairGroup?.jsonRecord) {
        return {
          image: `@server:${String(pairGroup.imageRecord._id)}`,
          json: `@server:${String(pairGroup.jsonRecord._id)}`,
        };
      }
      return {
        image: `@server:${mongoId}`,
        json: (typeof _base64Data === "object" && _base64Data !== null)
          ? _base64Data.json
          : null,
      };
    }
    if (depType === "bitmap-font") {
      if (pairGroup?.imageRecord && pairGroup?.xmlRecord) {
        return {
          image: `@server:${String(pairGroup.imageRecord._id)}`,
          xml: `@server:${String(pairGroup.xmlRecord._id)}`,
        };
      }
      return {
        image: `@server:${mongoId}`,
        xml: (typeof _base64Data === "object" && _base64Data !== null)
          ? _base64Data.xml
          : null,
      };
    }
    return `@server:${mongoId}`;
  }

  // ─── Sound preview helpers ───────────────────────────────────────────────

  function stopCurrentAudio() {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    setPlayingDepKey(null);
  }

  function handleToggleSoundPreview(componentId, dep, record) {
    const key = `${componentId}:${dep.key}`;
    if (playingDepKey === key) {
      stopCurrentAudio();
      return;
    }
    stopCurrentAudio();
    const audio = new Audio(`sounds/${record.file}`);
    audio.onended = () => setPlayingDepKey(null);
    audio.onerror = () => setPlayingDepKey(null);
    audioRef.current = audio;
    setPlayingDepKey(key);
    audio.play().catch(() => setPlayingDepKey(null));
  }

  /** Mark a dependency key as busy / not busy */
  function setDepBusy(componentId, depKey, busy) {
    setBusyDeps((prev) => ({ ...prev, [`${componentId}:${depKey}`]: busy }));
  }

  function isDepBusy(componentId, depKey) {
    return !!busyDeps[`${componentId}:${depKey}`];
  }

  /** Map dep type to the static folder where its files are served */
  function getAssetFolder(depType) {
    const map = {
      image: "images",
      atlas: "atlases",
      "bitmap-font": "bitmapFonts",
      audio: "sounds",
      font: "fonts",
      video: "videos",
    };
    return map[depType] || "images";
  }

  /**
   * Return assets that belong to {bucket} AND match {depType}.
   * Routes to the correct collection based on asset type.
   * Also guards by file extension so records mis-uploaded to the wrong
   * collection (e.g. a font sent to media/uploadImage before the routing
   * fix) never appear in the wrong dropdown.
   */
  const VALID_EXTS = {
    image: ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"],
    audio: ["mp3", "ogg", "wav", "aac", "m4a", "flac"],
    font: ["ttf", "otf", "woff", "woff2"],
    video: ["mp4", "webm", "mov", "avi", "mkv"],
    atlas: ["png", "jpg", "jpeg"],   // intentionally unused — both image+json needed; see filter below
  };
  function getExt(item) {
    return (_.get(item, "file", "") || "").split(".").pop().toLowerCase();
  }
  function getAssetsForBucketAndType(bucket, depType) {
    let collection;
    switch (depType) {
      case "image": collection = _.get(props, "media.images", []); break;
      case "atlas": collection = _.get(props, "atlases", []); break;
      case "bitmap-font": collection = _.get(props, "bitmapFonts", []); break;
      case "audio": collection = _.get(props, "sounds", []); break;
      case "font": collection = _.get(props, "fonts", []); break;
      case "video": collection = _.get(props, "videos", []); break;
      default: collection = []; break;
    }
    // For atlas, skip extension filtering — buildAtlasGroups needs BOTH
    // image and json records present to form a complete pair.
    const validExts = isPairDepType(depType) ? null : VALID_EXTS[depType];
    return collection.filter((item) => {
      if (_.get(item, "bucket") !== bucket) return false;
      if (validExts && !validExts.includes(getExt(item))) return false;
      return true;
    });
  }

  /**
   * Convert a server-stored asset file to a base64 data-URL.
   * Fetches from the correct static folder based on asset type.
   */
  async function assetFileToBase64(filename, depType) {
    const folder = getAssetFolder(depType);
    const resp = await fetch(`${folder}/${filename}`);
    const blob = await resp.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * Convert a local File object to a base64 data-URL.
   */
  function localFileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Rewrite any data URL to use 'data:application/json;base64,...' MIME.
   * AtlasLoader.loadJSON only accepts that specific prefix.
   * FileReader.readAsDataURL on a .json file may produce text/plain or
   * application/octet-stream, so we normalise it here.
   */
  function rewriteAsJsonDataUrl(dataUrl) {
    if (!dataUrl) return dataUrl;
    if (dataUrl.startsWith("data:application/json;base64,")) return dataUrl;
    const commaIdx = dataUrl.indexOf(",");
    if (commaIdx === -1) return dataUrl;
    const base64Part = dataUrl.slice(commaIdx + 1);
    return `data:application/json;base64,${base64Part}`;
  }

  // ─── Fetch components + buckets ──────────────────────────────────────────

  const fetchComponents = useCallback((onEngineReady) => {
    if (!props.iFrameId) return;

    // Stamp the version this fetch is for. If the selection changes while the
    // async work is in-flight we discard the stale response.
    const expectedVersionId = currentVersionIdRef.current;

    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }

    setLoading(true);
    setError(null);

    function attempt(retriesLeft) {
      sendCommand(props.iFrameId, { type: "playengine:get-dependencies" })
        .then(async (response) => {
          // Discard result if the user switched to a different version while we
          // were awaiting — prevents stale/reversed data in the dropdowns.
          if (currentVersionIdRef.current !== expectedVersionId) return;

          const comps = _.get(response, "data", []).map((comp) => {
            const { config } = normalizeComponentConfig(comp.config || {});
            return { ...comp, config };
          });
          setComponents(comps);

          // Resolve bucket for each component
          const newBucketMap = {};
          await Promise.all(
            comps.map(async (comp) => {
              try {
                const bucketResp = await sendCommand(props.iFrameId, {
                  type: "playengine:get-bucket",
                  componentId: comp.componentId,
                });
                newBucketMap[comp.componentId] = bucketResp.bucket || "nobucket";
              } catch {
                newBucketMap[comp.componentId] = "nobucket";
              }
            })
          );

          // Second stale-check after the bucket awaits.
          if (currentVersionIdRef.current !== expectedVersionId) return;

          setBucketMap(newBucketMap);
          // Mark this version as fully loaded so playengine:ready skips re-fetching
          fetchedForVersionRef.current = expectedVersionId;
          setLoading(false);
          // The engine just answered a command, so DashboardBridge.connect is
          // definitely installed.  Notify the caller so it can issue engine-
          // sound fetch now that we know the engine is ready.
          onEngineReady?.();
        })
        .catch((err) => {
          if (retriesLeft > 0) {
            retryTimerRef.current = setTimeout(() => attempt(retriesLeft - 1), 2000);
          } else {
            if (currentVersionIdRef.current !== expectedVersionId) return;
            setError(err.message);
            setComponents([]);
            setLoading(false);
          }
        });
    }

    attempt(3);
  }, [props.iFrameId]);


  useEffect(() => {
    const vId = _.get(props, "selectedVersion._id");
    // Keep the always-fresh ref up to date so fetchComponents's stale-check works.
    currentVersionIdRef.current = vId;
    // Reset the "already fetched" marker so the next playengine:ready triggers
    // a fresh fetch from the newly loaded iframe.
    fetchedForVersionRef.current = null;
    // Clear stale component/bucket data immediately so the panel shows a
    // loading spinner instead of the previous version's assets.
    setComponents([]);
    setBucketMap({});
    // Stop any playing audio preview when the version changes.
    stopCurrentAudio();
    // Do NOT call fetchComponents here: the iframe hasn't loaded the new
    // snapshot yet at this point, so any get-dependencies command would
    // return stale data. playengine:ready (fired once the iframe finishes
    // loading) will trigger the actual fetch at the right time.
    return () => {
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      // Flush pending persists so edits are not lost on version switch.
      configPersistTimersRef.current.forEach((id, timerKey) => {
        clearTimeout(id);
        const pending = pendingConfigRef.current.get(timerKey);
        if (pending) {
          persistConfigChange(
            pending.componentId,
            pending.key,
            pending.value,
            pending.isOverride,
            pending.language
          );
        }
      });
      configPersistTimersRef.current.clear();
      pendingConfigRef.current.clear();
    };
  }, [_.get(props, "selectedVersion._id")]);

  // Clear outstanding timers on unmount; flush pending persists.
  useEffect(() => {
    const persistTimers = configPersistTimersRef.current;
    const pending = pendingConfigRef.current;
    return () => {
      persistTimers.forEach((id, timerKey) => {
        clearTimeout(id);
        const entry = pending.get(timerKey);
        if (entry) {
          persistConfigChange(
            entry.componentId,
            entry.key,
            entry.value,
            entry.isOverride,
            entry.language
          );
        }
      });
      persistTimers.clear();
      pending.clear();
    };
  }, []);

  // Tracks component ids last reflected in the panel (from playEngineConfig).
  const configComponentIdsRef = useRef("");

  // Arm pendingRefetchRef whenever the parent signals a preset was applied.
  // Note: presetApplyCount alone can lose the race — playengine:ready may fire
  // before this effect runs. agent-reload / panel-refetch-arm listeners below
  // arm the ref synchronously on the same tick as the iframe reload.
  useEffect(() => {
    if (props.presetApplyCount > 0) {
      pendingRefetchRef.current = true;
    }
  }, [props.presetApplyCount]);

  // Arm refetch synchronously when agent/preset triggers an iframe reload.
  useEffect(() => {
    function armPanelRefetch() {
      pendingRefetchRef.current = true;
    }
    window.addEventListener("playengine:agent-reload", armPanelRefetch);
    window.addEventListener("playengine:panel-refetch-arm", armPanelRefetch);
    return () => {
      window.removeEventListener("playengine:agent-reload", armPanelRefetch);
      window.removeEventListener("playengine:panel-refetch-arm", armPanelRefetch);
    };
  }, []);

  // When playEngineConfig gains/loses components (agent add/remove) on the same
  // version, arm a refetch even if the ready event was missed.
  useEffect(() => {
    const comps = _.get(playEngineConfig, "components", []);
    const idsKey = Array.isArray(comps)
      ? comps.map((c) => String(c.id)).sort().join(",")
      : "";
    if (!idsKey) return;
    if (
      configComponentIdsRef.current &&
      configComponentIdsRef.current !== idsKey &&
      fetchedForVersionRef.current === currentVersionIdRef.current
    ) {
      pendingRefetchRef.current = true;
      fetchedForVersionRef.current = null;
    }
    configComponentIdsRef.current = idsKey;
  }, [playEngineConfig]);

  /**
   * The stored config was rewritten by something other than this panel — undo /
   * redo in the snapshot editor, which bumps `externalConfigRevision`.
   *
   * The fields render from `components`, read from the engine when the snapshot
   * opened and kept up to date by the panel's own edits, so nothing about a
   * restored value reaches them on its own: the panel would keep showing the
   * value that was just undone. Only the keys the stored config actually carries
   * are replaced — the engine reports defaults the file never had, and dropping
   * those would blank fields that are perfectly editable.
   */
  useEffect(() => {
    if (!props.externalConfigRevision) return;

    // Anything typed just before the undo has been superseded by it; letting a
    // queued persist fire would write the undone value straight back out.
    configPersistTimersRef.current.forEach((timerId) => clearTimeout(timerId));
    configPersistTimersRef.current.clear();
    pendingConfigRef.current.clear();

    const storedById = new Map(
      (_.get(playEngineConfig, "components") || []).map((comp) => [String(comp.id), comp.config])
    );
    setComponents((prev) =>
      prev.map((comp) => {
        const stored = storedById.get(String(comp.componentId));
        if (!stored || typeof stored !== "object") return comp;
        const { config } = normalizeComponentConfig({ ...comp.config, ...stored });
        return _.isEqual(config, comp.config) ? comp : { ...comp, config };
      })
    );
    // Deliberately keyed off the revision alone: `playEngineConfig` changes on
    // every edit made here too, and re-running then would fight this panel's own
    // in-flight state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.externalConfigRevision]);

  useEffect(() => {
    // Decide whether the panel needs to (re)fetch from the iframe. Re-fetch when:
    //   (a) a preset was just applied (pendingRefetchRef armed), OR
    //   (b) this is the first time we've seen this version (no cached data), OR
    //   (c) template-bound config fields exist — resolved values may have changed
    //       if global variables were edited since the last fetch.
    function fetchWanted() {
      const currentVersionId = currentVersionIdRef.current;
      const isNewVersion = fetchedForVersionRef.current !== currentVersionId || components.length === 0;
      const hasTemplateBound = Object.keys(templateMap).length > 0;
      return pendingRefetchRef.current || isNewVersion || hasTemplateBound;
    }

    function clearFallbackTimer() {
      if (readyFallbackTimerRef.current) {
        clearTimeout(readyFallbackTimerRef.current);
        readyFallbackTimerRef.current = null;
      }
    }

    function runFetch() {
      clearFallbackTimer();
      pendingRefetchRef.current = false;
      // fetchComponents succeeds; the eager call covers builds where the engine
      // is already connected.
      fetchComponents();
    }

    function isFromIframe(event) {
      const iframe = document.getElementById(props.iFrameId);
      return iframe && event.source === iframe.contentWindow;
    }

    function handleMessage(event) {
      if (!isFromIframe(event)) return;
      const type = event.data?.type;

      // Primary path: the engine's command listener is installed (connect()) and
      // it can answer get-dependencies immediately — no wasted timeout.
      if (type === "playengine:playable-ready") {
        clearFallbackTimer();
        if (!fetchWanted()) return;
        runFetch();
        return;
      }

      // Fallback path: playengine:ready fires before the engine can answer
      // commands. Newer builds also emit playable-ready (which cancels this
      // timer). For older builds that never emit it, this timer ensures the
      // panel still fetches.
      if (type === "playengine:ready") {
        if (!fetchWanted()) return;
        if (readyFallbackTimerRef.current) return;
        readyFallbackTimerRef.current = setTimeout(() => {
          readyFallbackTimerRef.current = null;
          if (!fetchWanted()) return;
          runFetch();
        }, 4000);
      }
    }

    window.addEventListener("message", handleMessage);
    // The fallback timer deliberately survives this cleanup. These deps change
    // whenever the config object identity does — which, in a snapshot editor
    // where the config arrives from a file fetch, routinely happens inside the
    // fallback window. Clearing it here would drop the only fetch trigger an
    // engine build that never emits playable-ready ever gives us, and the panel
    // would sit empty forever. It is cleared on unmount and once a fetch runs.
    return () => window.removeEventListener("message", handleMessage);
  }, [props.iFrameId, fetchComponents, components.length, templateMap]);

  useEffect(() => {
    return () => {
      if (readyFallbackTimerRef.current) {
        clearTimeout(readyFallbackTimerRef.current);
        readyFallbackTimerRef.current = null;
      }
    };
  }, []);

  // ─── Asset Flows ─────────────────────────────────────────────────────────

  /**
   * Applies an asset to a dependency using the global asset-map model.
   *
   * Picking an asset from the Version Settings dropdown always FORKS a new
   * asset: a fresh `playEngineConfig.assets[<assetKey>]` entry is created and
   * the dependency is bound to it. This guarantees that editing one
   * component's asset never affects another component that happens to share
   * the same underlying media. Sharing is set up from the Assets tab instead.
   *
   *   - "en" language:
   *       1. Create assets[assetKey] entry + set dependency.assetKey (DB)
   *       2. set-asset (load handle) → changeDependency (wire) for live preview
   *   - Other languages:
   *       1. Create assets[assetKey] entry + store the asset key string in the
   *          localization dependencyOverrides (DB)
   *       2. set-asset → addLocalizationOverrideDependency for live preview
   *
   * The DB only ever stores "@server:<mongoId>" references (never base64). The
   * dashboard hydrates them to base64 at load/build time.
   */
  async function applyAssetToDependency(componentId, depKey, depType, mongoId, base64Data, atlasGroup = null) {
    const isOverride = selectedLanguage && selectedLanguage !== "en";
    const assetKey = genAssetKey(depType, Object.keys(assetsMap));
    const assetId = `${depType}:${assetKey}`;
    const serverSource = buildServerSource(depType, mongoId, base64Data, atlasGroup);

    // 1. Persist FIRST so a live-preview timeout never loses the config.
    const config = _.cloneDeep(
      _.get(props, "selectedVersion.playEngineConfig") || {}
    );

    // Asset map entry (one per fork). source mirrors sourceRef (@server ref);
    // it is hydrated to base64 at load/build time.
    if (!config.assets || Array.isArray(config.assets)) config.assets = {};
    config.assets[assetKey] = {
      type: depType,
      bucket: bucketMap[componentId] || "nobucket",
      // sourceRef points back at the library record; with no library there is
      // nothing to point at, which is exactly how agent-authored snapshots look.
      sourceRef: hasAssetLibrary ? serverSource : null,
      source: serverSource,
    };

    if (isOverride) {
      // Persist as a localization dependency override → asset key string
      if (!config.localization) {
        config.localization = { currentLanguage: selectedLanguage, availableLanguages: ["en", selectedLanguage], overrides: {} };
      }
      if (!config.localization.overrides) config.localization.overrides = {};
      if (!config.localization.overrides[selectedLanguage]) config.localization.overrides[selectedLanguage] = {};
      if (!config.localization.overrides[selectedLanguage][componentId]) {
        config.localization.overrides[selectedLanguage][componentId] = {};
      }
      const compOverride = config.localization.overrides[selectedLanguage][componentId];
      if (!compOverride.dependencyOverrides) compOverride.dependencyOverrides = {};
      compOverride.dependencyOverrides[depKey] = assetKey;
    } else {
      // Standard path: point the dependency at the new asset key.
      if (config.components) {
        const comp = config.components.find((c) => c.id === componentId);
        if (comp) {
          const dep = (comp.dependencies || []).find((d) => d.key === depKey);
          if (dep) {
            dep.assetKey = assetKey;
            // Clean up transitional / legacy fields if present
            delete dep.source;
            delete dep.sourceRef;
            delete dep.assetId;
            delete dep.isSatisfied;
          }
        }
      }
    }

    props.setSelectedVersionPathValue({
      path: "playEngineConfig",
      value: config,
    });

    // 2. Live preview: load the handle, then wire the component/override.
    //    Atlas loading (image + JSON parse) can be slow, so use a generous
    //    timeout and wrap in try/catch — config is already saved above.
    const previewTimeout = isPairDepType(depType) ? 15000 : 5000;
    try {
      await sendCommand(props.iFrameId, {
        type: "playengine:set-asset",
        assetId,
        source: base64Data,
      }, previewTimeout);

      if (isOverride) {
        await sendCommand(props.iFrameId, {
          type: "playengine:dispatch-action",
          action: {
            target: "engine",
            name: "addLocalizationOverrideDependency",
            args: { language: selectedLanguage, componentId, dependencyKey: depKey, handleKey: assetId },
          },
        }, previewTimeout);
      } else {
        await sendCommand(props.iFrameId, {
          type: "playengine:dispatch-action",
          action: {
            target: componentId,
            name: "changeDependency",
            args: { key: depKey, handleKey: assetId },
          },
        }, previewTimeout);
      }
    } catch (err) {
      console.warn(
        `[applyAssetToDependency] live-preview command failed (config already saved): ${err.message}`
      );
    }

    // 3. Update local component state so the dependency UI reflects the new
    //    binding immediately, without re-fetching from the iframe.
    setComponents((prev) =>
      prev.map((c) => {
        if (c.componentId !== componentId) return c;
        return {
          ...c,
          dependencies: (c.dependencies || []).map((d) =>
            d.key === depKey
              ? { ...d, assetId, assetKey, source: base64Data, sourceRef: null, isSatisfied: true }
              : d
          ),
        };
      })
    );
  }

  /**
   * Bind a dependency to an existing asset-map key (no fork / no new entry).
   * Sharing is explicit: multiple components can point at the same key.
   */
  async function bindDependencyToExistingAssetKey(componentId, dep, assetKey) {
    if (!assetKey) return;

    const entry = assetsMap[assetKey];
    if (!entry) {
      props.createSnackbar?.(`Asset key "${assetKey}" not found.`, "warning", false);
      return;
    }
    if (entry.type !== dep.type) {
      props.createSnackbar?.(
        `Asset "${assetKey}" is type "${entry.type}", not "${dep.type}".`,
        "warning",
        false
      );
      return;
    }

    const currentKey = resolveBoundAssetKey(
      playEngineConfig,
      componentId,
      dep.key,
      dep,
      selectedLanguage
    );
    if (currentKey === assetKey) return;

    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }

    setDepBusy(componentId, dep.key, true);
    const isOverride = selectedLanguage && selectedLanguage !== "en";
    const assetId = `${dep.type}:${assetKey}`;
    const previewTimeout = isPairDepType(dep.type) ? 15000 : 5000;

    try {
      const config = _.cloneDeep(
        _.get(props, "selectedVersion.playEngineConfig") || {}
      );

      if (isOverride) {
        if (!config.localization) {
          config.localization = {
            currentLanguage: selectedLanguage,
            availableLanguages: ["en", selectedLanguage],
            overrides: {},
          };
        }
        if (!config.localization.overrides) config.localization.overrides = {};
        if (!config.localization.overrides[selectedLanguage]) {
          config.localization.overrides[selectedLanguage] = {};
        }
        if (!config.localization.overrides[selectedLanguage][componentId]) {
          config.localization.overrides[selectedLanguage][componentId] = {};
        }
        const compOverride = config.localization.overrides[selectedLanguage][componentId];
        if (!compOverride.dependencyOverrides) compOverride.dependencyOverrides = {};
        compOverride.dependencyOverrides[dep.key] = assetKey;
      } else if (config.components) {
        const comp = config.components.find((c) => c.id === componentId);
        const savedDep = (comp?.dependencies || []).find((d) => d.key === dep.key);
        if (savedDep) {
          savedDep.assetKey = assetKey;
          delete savedDep.source;
          delete savedDep.sourceRef;
          delete savedDep.assetId;
          delete savedDep.isSatisfied;
        }
      }

      props.setSelectedVersionPathValue({
        path: "playEngineConfig",
        value: config,
      });

      const liveSource = await resolveAssetEntryLiveSource(
        props,
        props.iFrameId,
        assetKey,
        dep.type,
        entry,
        sendCommand
      );

      try {
        if (liveSource) {
          await sendCommand(
            props.iFrameId,
            { type: "playengine:set-asset", assetId, source: liveSource },
            previewTimeout
          );
        }

        if (isOverride) {
          await sendCommand(
            props.iFrameId,
            {
              type: "playengine:dispatch-action",
              action: {
                target: "engine",
                name: "addLocalizationOverrideDependency",
                args: {
                  language: selectedLanguage,
                  componentId,
                  dependencyKey: dep.key,
                  handleKey: assetId,
                },
              },
            },
            previewTimeout
          );
        } else {
          await sendCommand(
            props.iFrameId,
            {
              type: "playengine:dispatch-action",
              action: {
                target: componentId,
                name: "changeDependency",
                args: { key: dep.key, handleKey: assetId },
              },
            },
            previewTimeout
          );
        }
      } catch (err) {
        console.warn(
          `[bindDependencyToExistingAssetKey] live-preview failed (config saved): ${err.message}`
        );
      }

      setComponents((prev) =>
        prev.map((c) => {
          if (c.componentId !== componentId) return c;
          return {
            ...c,
            dependencies: (c.dependencies || []).map((d) =>
              d.key === dep.key
                ? {
                  ...d,
                  assetId,
                  assetKey,
                  source: liveSource ?? d.source,
                  sourceRef: entry.sourceRef ?? null,
                  isSatisfied: true,
                }
                : d
            ),
          };
        })
      );
    } catch (err) {
      console.error("bindDependencyToExistingAssetKey failed:", err);
      props.createSnackbar?.(`Failed to bind asset key: ${err.message}`, "error", false);
    } finally {
      setDepBusy(componentId, dep.key, false);
    }
  }

  /**
   * Clear an asset slot.
   *
   * What "clear" means depends on the language, because the two store the
   * binding in different places:
   *
   *   "en"    — the component's own `assetKey` is the binding, so clearing it
   *             leaves the slot unassigned and the preview goes to `type:empty`.
   *   other   — the binding is an entry in `dependencyOverrides`, and the
   *             component's own key is still pointing at the English asset.
   *             Clearing therefore means "stop translating this slot": the
   *             override goes, and the slot falls back to English rather than
   *             to nothing.
   */
  async function handleClearComponentAsset(componentId, dep) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    setDepBusy(componentId, dep.key, true);
    try {
      const isOverride = selectedLanguage && selectedLanguage !== "en";
      // 1. Persist to DB
      const config = _.cloneDeep(_.get(props, "selectedVersion.playEngineConfig") || {});
      if (isOverride) {
        const overrideDeps = _.get(
          config,
          `localization.overrides.${selectedLanguage}.${componentId}.dependencyOverrides`
        );
        if (overrideDeps) delete overrideDeps[dep.key];
      } else {
        if (config.components) {
          const comp = config.components.find((c) => c.id === componentId);
          if (comp) {
            const d = (comp.dependencies || []).find((d) => d.key === dep.key);
            if (d) {
              delete d.assetKey;
              delete d.source;
              delete d.sourceRef;
              delete d.assetId;
              delete d.isSatisfied;
            }
          }
        }
      }
      props.setSelectedVersionPathValue({ path: "playEngineConfig", value: config });

      // The English binding the slot falls back to once the override is gone.
      // Null is a real answer: English may never have had an asset here.
      const englishAssetKey = isOverride
        ? (config.components || [])
          .find((c) => c.id === componentId)
          ?.dependencies?.find((d) => d.key === dep.key)?.assetKey ?? null
        : null;

      // 2. Live preview.
      try {
        if (isOverride) {
          // Tell the engine to drop the override as well. Deleting it only from
          // the config leaves it live in LocalizationManager, so switching away
          // and back would re-apply the translation this just removed — and the
          // engine is also the only thing that knows which handle to put back.
          await sendCommand(props.iFrameId, {
            type: "playengine:dispatch-action",
            action: {
              target: "engine",
              name: "removeLocalizationOverrideDependency",
              args: { language: selectedLanguage, componentId, dependencyKey: dep.key },
            },
          });
        } else {
          await sendCommand(props.iFrameId, {
            type: "playengine:dispatch-action",
            action: {
              target: componentId,
              name: "changeDependency",
              args: { key: dep.key, handleKey: `${dep.type}:empty` },
            },
          });
        }
      } catch (err) {
        console.warn(`[handleClearComponentAsset] live-preview dispatch failed: ${err.message}`);
      }

      // 3. Update local component state. In override mode the slot is not empty
      //    — it is back on English — so saying "unassigned" here would show a
      //    blank picker over a component that is still rendering the base asset.
      setComponents((prev) =>
        prev.map((c) => {
          if (c.componentId !== componentId) return c;
          return {
            ...c,
            dependencies: (c.dependencies || []).map((d) => {
              if (d.key !== dep.key) return d;
              if (!isOverride) {
                return { ...d, assetKey: null, source: null, sourceRef: null, assetId: null, isSatisfied: false };
              }
              return {
                ...d,
                assetKey: englishAssetKey,
                assetId: englishAssetKey ? `${d.type}:${englishAssetKey}` : null,
                isSatisfied: Boolean(englishAssetKey),
              };
            }),
          };
        })
      );
    } catch (err) {
      console.error("handleClearComponentAsset failed:", err);
      props.createSnackbar?.(`Failed to clear asset: ${err.message}`, "error", false);
    } finally {
      setDepBusy(componentId, dep.key, false);
    }
  }

  /** Update an existing asset-map entry's source (no new key fork). */
  async function updateAssetKeySource(assetKey, type, record, pairGroup, pairConfig) {
    let sourceRef;
    let base64;
    if (pairConfig && pairGroup) {
      const [imageB64, rawMetaB64] = await Promise.all([
        assetFileToBase64(pairGroup.imageRecord.file, type),
        assetFileToBase64(pairGroup[pairConfig.metaRecordKey].file, type),
      ]);
      base64 = {
        image: imageB64,
        [pairConfig.metaKey]: pairConfig.rewriteMeta(rawMetaB64),
      };
      sourceRef = buildServerSource(type, String(pairGroup.imageRecord._id), base64, pairGroup);
    } else {
      base64 = await assetFileToBase64(record.file, type);
      sourceRef = buildServerSource(type, String(record._id), base64, null);
    }

    const config = _.cloneDeep(_.get(props, "selectedVersion.playEngineConfig") || {});
    if (!config.assets?.[assetKey]) return;
    config.assets[assetKey].sourceRef = sourceRef;
    config.assets[assetKey].source = sourceRef;
    props.setSelectedVersionPathValue({ path: "playEngineConfig", value: config });

    const timeout = isPairDepType(type) ? 15000 : 5000;
    await sendCommand(
      props.iFrameId,
      { type: "playengine:set-asset", assetId: `${type}:${assetKey}`, source: base64 },
      timeout
    );
  }

  /** Clear source bytes on an existing asset key and rewire bound handles to empty. */
  async function clearAssetKeySource(assetKey, type) {
    const config = _.cloneDeep(_.get(props, "selectedVersion.playEngineConfig") || {});
    if (!config.assets?.[assetKey]) return;
    config.assets[assetKey].sourceRef = null;
    config.assets[assetKey].source = null;
    props.setSelectedVersionPathValue({ path: "playEngineConfig", value: config });

    const usages = collectAssetKeyUsages(config, assetKey);
    const emptyHandle = `${type}:empty`;
    const timeout = isPairDepType(type) ? 15000 : 5000;
    for (const u of usages) {
      try {
        if (u.kind === "override") {
          await sendCommand(props.iFrameId, {
            type: "playengine:dispatch-action",
            action: {
              target: "engine",
              name: "addLocalizationOverrideDependency",
              args: {
                language: u.language,
                componentId: u.componentId,
                dependencyKey: u.depKey,
                handleKey: emptyHandle,
              },
            },
          }, timeout);
        } else {
          await sendCommand(props.iFrameId, {
            type: "playengine:dispatch-action",
            action: {
              target: u.componentId,
              name: "changeDependency",
              args: { key: u.depKey, handleKey: emptyHandle },
            },
          }, timeout);
        }
      } catch (err) {
        console.warn(`[clearAssetKeySource] rebind failed for "${u.componentId}": ${err.message}`);
      }
    }
  }

  /**
   * "Select existing asset" flow (non-atlas)
   */
  async function handleSelectAsset(componentId, dep, record) {
    const mongoId = String(record._id);
    setDepBusy(componentId, dep.key, true);
    try {
      const base64Data = await assetFileToBase64(record.file, dep.type);
      await applyAssetToDependency(componentId, dep.key, dep.type, mongoId, base64Data);
    } catch (err) {
      console.error("handleSelectAsset failed:", err);
      props.createSnackbar?.(`Failed to apply asset: ${err.message}`, "error", false);
    } finally {
      setDepBusy(componentId, dep.key, false);
    }
  }

  // ─── Atlas helpers ────────────────────────────────────────────────────────

  /**
   * Groups a flat list of atlas records (from props.atlases filtered by bucket)
   * into pairs keyed by animationSet.
   * Only returns complete pairs that have both an image AND a json record.
   */
  function buildPairGroups(records, config) {
    const groups = {};
    records.forEach((record) => {
      const set = record[config.groupField];
      if (!set) return;
      if (!groups[set]) {
        groups[set] = {
          [config.groupRecordKey]: set,
          imageRecord: null,
          [config.metaRecordKey]: null,
        };
      }
      const v = (record.variant || "").toLowerCase();
      if (v === "image") groups[set].imageRecord = record;
      else if (v === config.metaVariant) groups[set][config.metaRecordKey] = record;
    });
    return Object.values(groups).filter((g) => g.imageRecord && g[config.metaRecordKey]);
  }

  function buildAtlasGroups(atlasRecords) {
    return buildPairGroups(atlasRecords, getPairDepConfig("atlas"));
  }

  function stageAtlasFile(componentId, depKey, variant, file) {
    const key = `${componentId}:${depKey}`;
    setStagedAtlasFiles((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [variant]: file },
    }));
  }

  function getStagedAtlas(componentId, depKey) {
    return stagedAtlasFiles[`${componentId}:${depKey}`] || {};
  }

  function clearStagedAtlas(componentId, depKey) {
    const key = `${componentId}:${depKey}`;
    setStagedAtlasFiles((prev) => { const n = { ...prev }; delete n[key]; return n; });
  }

  /**
   * "Select existing atlas" flow — imageRecord + jsonRecord are both available.
   */
  async function handleSelectPair(componentId, dep, pairGroup, config) {
    setDepBusy(componentId, dep.key, true);
    try {
      const [imageBase64, rawMetaBase64] = await Promise.all([
        assetFileToBase64(pairGroup.imageRecord.file, dep.type),
        assetFileToBase64(pairGroup[config.metaRecordKey].file, dep.type),
      ]);
      const base64Data = {
        image: imageBase64,
        [config.metaKey]: config.rewriteMeta(rawMetaBase64),
      };
      await applyAssetToDependency(
        componentId, dep.key, dep.type,
        pairGroup[config.groupRecordKey], base64Data, pairGroup
      );
    } catch (err) {
      console.error("handleSelectPair failed:", err);
      props.createSnackbar?.(`Failed to apply ${dep.type}: ${err.message}`, "error", false);
    } finally {
      setDepBusy(componentId, dep.key, false);
    }
  }

  async function handleSelectAtlas(componentId, dep, atlasGroup) {
    return handleSelectPair(componentId, dep, atlasGroup, getPairDepConfig("atlas"));
  }

  /**
   * "Upload new atlas" flow — sends image + JSON together to atlas/uploadAtlas.
   */
  /**
   * Take an asset from the embedder's own library and bind it to a dependency.
   *
   * The provider hands back exactly what `applyAssetToDependency` stores: a data
   * URL for simple types, `{ image, xml }` for a bitmap font. Cancelling
   * resolves to null and nothing happens.
   */
  async function handlePickFromLibrary(componentId, dep) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }

    setDepBusy(componentId, dep.key, true);
    try {
      const source = await providerPickFromLibrary(dep.type);
      if (!source) return;
      await applyAssetToDependency(componentId, dep.key, dep.type, null, source);
    } catch (err) {
      console.error("handlePickFromLibrary failed:", err);
      props.createSnackbar?.(`Failed to apply ${dep.type}: ${err.message}`, "error", false);
    } finally {
      setDepBusy(componentId, dep.key, false);
    }
  }

  async function handleUploadPair(componentId, dep, imageFile, metaFile, config) {
    const bucket = bucketMap[componentId] || "nobucket";
    setDepBusy(componentId, dep.key, true);
    clearStagedAtlas(componentId, dep.key);
    try {
      // Same story as handleUploadAsset: with no library the two halves are
      // embedded directly rather than stored and referenced.
      if (providerUploadAssetPair) {
        const { image, meta } = await providerUploadAssetPair(imageFile, metaFile, dep.type);
        await applyAssetToDependency(componentId, dep.key, dep.type, null, {
          image,
          [config.metaKey]: config.rewriteMeta(meta),
        });
        return;
      }

      const formData = new FormData();
      formData.append("variant", "image");
      formData.append("files", imageFile);
      formData.append("variant", config.uploadMetaVariant);
      formData.append("files", metaFile);
      formData.append("componentType", null);
      formData.append("dependencyKey", dep.key);
      formData.append("bucket", bucket);
      formData.append("accessToken", localStorage.getItem("session.accessToken"));
      formData.append("uploadedFileName", imageFile.name.replace(/\.[^/.]+$/, ""));

      const resp = await fetch(getServerUri(config.uploadEndpoint), { method: "POST", body: formData });
      if (resp.status !== 200) {
        if (resp.status === 401) props.createSnackbar?.("Not authorized to upload.", "warning", false);
        throw new Error(`Upload failed (status ${resp.status})`);
      }

      const result = await resp.json();
      const newRecords = _.get(result, "records", []);
      if (newRecords.length < 2) throw new Error(`Expected 2 ${dep.type} records from upload`);

      config.insertRecords(newRecords);

      const imageRecord = newRecords.find((r) => (r.variant || "").toLowerCase() === "image");
      const metaRecord = newRecords.find((r) => (r.variant || "").toLowerCase() === config.metaVariant);
      const pairGroup = {
        [config.groupRecordKey]: _.get(result, config.uploadGroupIdKey),
        imageRecord,
        [config.metaRecordKey]: metaRecord,
      };

      const [imageBase64, rawMetaBase64] = await Promise.all([
        localFileToBase64(imageFile),
        localFileToBase64(metaFile),
      ]);
      const base64Data = {
        image: imageBase64,
        [config.metaKey]: config.rewriteMeta(rawMetaBase64),
      };
      await applyAssetToDependency(
        componentId, dep.key, dep.type,
        pairGroup[config.groupRecordKey], base64Data, pairGroup
      );
    } catch (err) {
      console.error("handleUploadPair failed:", err);
      props.createSnackbar?.(`Upload failed: ${err.message}`, "error", false);
    } finally {
      setDepBusy(componentId, dep.key, false);
    }
  }

  async function handleUploadAtlas(componentId, dep, imageFile, jsonFile) {
    return handleUploadPair(componentId, dep, imageFile, jsonFile, getPairDepConfig("atlas"));
  }

  /**
   * "Upload a plain font → auto-generate bitmap font" flow (bitmap-font deps).
   *
   *   1. Upload the raw TTF/OTF to the `font` collection (keeps the original so
   *      export can later regenerate a character subset → sourceFontId).
   *   2. Ask the server to render the master BMFont (PNG + XML) for all default
   *      languages, compress it, and persist it as a `bitmapFont` set.
   *   3. Wire the resulting bitmap-font asset onto the dependency + live preview.
   */
  async function handleGenerateBitmapFromFont(componentId, dep, fontFile) {
    const bucket = bucketMap[componentId] || "nobucket";
    setDepBusy(componentId, dep.key, true);
    try {
      const result = await uploadFontAndGenerateBitmapFont(fontFile, {
        bucket,
        onFontUploaded: (fontRecords) => props.insertFontPathValue?.(fontRecords),
      });

      const pairConfig = getPairDepConfig("bitmap-font");
      pairConfig.insertRecords(result.records);

      await applyAssetToDependency(
        componentId, dep.key, "bitmap-font",
        result.pairGroup[pairConfig.groupRecordKey], result.preview, result.pairGroup
      );

      if (result.warnings.length) {
        props.createSnackbar?.(result.warnings.join(" "), "warning", false);
      } else {
        props.createSnackbar?.("Bitmap font generated from font.", "success", false);
      }
    } catch (err) {
      console.error("handleGenerateBitmapFromFont failed:", err);
      if (err.status === 401) {
        props.createSnackbar?.("You are not authorized to upload.", "warning", false);
      } else {
        props.createSnackbar?.(`Generate failed: ${err.message}`, "error", false);
      }
    } finally {
      setDepBusy(componentId, dep.key, false);
    }
  }

  /**
   * "Upload new asset" flow
   */
  /** Map dep.type → { endpoint, insertRecord } */
  function getUploadConfig(depType) {
    switch (depType) {
      case "image":
        return {
          endpoint: "media/uploadImage",
          insertRecord: (records) => props.insertMediaPathValue?.({ path: "images", value: records }),
        };
      case "audio":
        return {
          endpoint: "sound/uploadSound",
          insertRecord: (records) => props.insertSoundPathValue?.(records),
        };
      case "font":
        return {
          endpoint: "font/uploadFont",
          insertRecord: (records) => props.insertFontPathValue?.(records),
        };
      case "video":
        return {
          endpoint: "video/uploadVideo",
          insertRecord: (records) => props.insertVideoPathValue?.(records),
        };
      default:
        return {
          endpoint: "media/uploadImage",
          insertRecord: (records) => props.insertMediaPathValue?.({ path: "images", value: records }),
        };
    }
  }

  async function handleUploadAsset(componentId, dep, file) {
    const bucket = bucketMap[componentId] || "nobucket";
    setDepBusy(componentId, dep.key, true);
    try {
      // A provider-supplied upload replaces the whole store-then-reference dance:
      // it hands back the bytes to embed and, optionally, a record id.
      if (providerUploadAsset) {
        const { mongoId = null, base64 } = await providerUploadAsset(file, dep.type, bucket);
        await applyAssetToDependency(componentId, dep.key, dep.type, mongoId, base64);
        return;
      }

      const { endpoint, insertRecord } = getUploadConfig(dep.type);

      // 1. Upload file to server
      const formData = new FormData();
      formData.append("files", file);
      formData.append("componentType", null);
      formData.append("dependencyKey", dep.key);
      formData.append("bucket", bucket);
      formData.append("accessToken", localStorage.getItem("session.accessToken"));
      formData.append("uploadedFileName", file.name.replace(/\.[^/.]+$/, ""));
      // fonts also need fontFamily (use filename without extension as fallback)
      if (dep.type === "font") {
        formData.append("fontFamily", file.name.replace(/\.[^/.]+$/, ""));
      }

      const resp = await fetch(getServerUri(endpoint), {
        method: "POST",
        body: formData,
      });

      if (resp.status !== 200) {
        if (resp.status === 401) {
          props.createSnackbar?.("You are not authorized to upload.", "warning", false);
        }
        throw new Error(`Upload failed (status ${resp.status})`);
      }

      const result = await resp.json();
      const newRecords = _.get(result, "records", []);
      if (newRecords.length === 0) throw new Error("No records returned from upload");

      // 2. Insert records into local state so they appear in dropdowns
      insertRecord(newRecords);

      // 3. Convert to base64 and apply to playEngine
      const mongoId = String(newRecords[0]._id);
      const base64Data = await localFileToBase64(file);
      await applyAssetToDependency(componentId, dep.key, dep.type, mongoId, base64Data);
    } catch (err) {
      console.error("handleUploadAsset failed:", err);
      props.createSnackbar?.(`Upload failed: ${err.message}`, "error", false);
    } finally {
      setDepBusy(componentId, dep.key, false);
    }
  }

  // ─── Bucket Helpers ──────────────────────────────────────────────────────

  /**
   * Change a component's bucket:
   *   1. Update local bucketMap so asset lists refresh immediately
   *   2. Send set-bucket to the live playEngine
   *   3. Persist bucket into playEngineConfig in the DB
   */
  /**
   * Change a component's bucket. Besides setting the bucket, each of the
   * component's asset dependencies is re-pointed to the first available asset of
   * the new bucket (a fresh forked asset key per dependency, whose bucket is the
   * new bucket). Dependencies with no matching asset in the new bucket are
   * cleared to empty. All changes are persisted in one shot and mirrored to the
   * live preview (set-bucket + set-asset + changeDependency).
   */
  async function handleBucketChange(componentId, newBucket) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }

    const bucket = newBucket.toLowerCase().trim() || "nobucket";
    if ((bucketMap[componentId] || "nobucket") === bucket) return; // no change

    // 1. Build one fresh config: set the bucket and re-bind every dependency.
    const config = _.cloneDeep(
      resolvePlayEngineConfig(props.selectedVersion, props.versions) || {}
    );
    if (!config.assets || Array.isArray(config.assets)) config.assets = {};
    const comp = (config.components || []).find((c) => c.id === componentId);
    if (comp) comp.bucket = bucket;

    const existingKeys = new Set(Object.keys(config.assets));
    const bindings = []; // { depKey, type, assetId, assetKey|null, record|null, group|null }

    for (const dep of comp ? comp.dependencies || [] : []) {
      const type = dep.type;
      const records = getAssetsForBucketAndType(bucket, type);
      let sourceRef = null;
      let record = null;
      let group = null;

      if (isPairDepType(type)) {
        const pcfg = getPairDepConfig(type);
        group = buildPairGroups(records, pcfg)[0] || null;
        if (group) {
          sourceRef = buildServerSource(type, String(group.imageRecord._id), null, group);
        }
      } else {
        record = records[0] || null;
        if (record) sourceRef = buildServerSource(type, String(record._id), null, null);
      }

      // Clear transitional / legacy binding fields on the dep either way.
      delete dep.source;
      delete dep.sourceRef;
      delete dep.assetId;
      delete dep.isSatisfied;

      if (sourceRef) {
        const assetKey = genAssetKey(type, existingKeys);
        existingKeys.add(assetKey);
        config.assets[assetKey] = { type, bucket, sourceRef, source: sourceRef };
        dep.assetKey = assetKey;
        bindings.push({ depKey: dep.key, type, assetId: `${type}:${assetKey}`, assetKey, record, group });
      } else {
        delete dep.assetKey;
        bindings.push({ depKey: dep.key, type, assetId: `${type}:empty`, assetKey: null, record: null, group: null });
      }
    }

    // 2. Local state + single persist.
    setBucketMap((prev) => ({ ...prev, [componentId]: bucket }));
    props.setSelectedVersionPathValue({ path: "playEngineConfig", value: config });

    // 3. Live preview: bucket first, then each dependency.
    try {
      await sendCommand(props.iFrameId, { type: "playengine:set-bucket", componentId, bucket });
    } catch (err) {
      console.warn(`[handleBucketChange] live set-bucket failed (config saved): ${err.message}`);
    }

    for (const b of bindings) {
      const timeout = isPairDepType(b.type) ? 15000 : 5000;
      try {
        if (b.assetKey) {
          let base64;
          if (isPairDepType(b.type)) {
            const pcfg = getPairDepConfig(b.type);
            const [img, rawMeta] = await Promise.all([
              assetFileToBase64(b.group.imageRecord.file, b.type),
              assetFileToBase64(b.group[pcfg.metaRecordKey].file, b.type),
            ]);
            base64 = { image: img, [pcfg.metaKey]: pcfg.rewriteMeta(rawMeta) };
          } else {
            base64 = await assetFileToBase64(b.record.file, b.type);
          }
          await sendCommand(props.iFrameId, { type: "playengine:set-asset", assetId: b.assetId, source: base64 }, timeout);
        }
        await sendCommand(props.iFrameId, {
          type: "playengine:dispatch-action",
          action: { target: componentId, name: "changeDependency", args: { key: b.depKey, handleKey: b.assetId } },
        }, timeout);
      } catch (err) {
        console.warn(`[handleBucketChange] live rebind failed for "${b.depKey}" (config saved): ${err.message}`);
      }
    }

    // 4. Reflect new bindings in local component state.
    setComponents((prev) =>
      prev.map((c) => {
        if (c.componentId !== componentId) return c;
        return {
          ...c,
          dependencies: (c.dependencies || []).map((d) => {
            const b = bindings.find((x) => x.depKey === d.key);
            if (!b) return d;
            return b.assetKey
              ? { ...d, assetId: b.assetId, assetKey: b.assetKey, sourceRef: null, isSatisfied: true }
              : { ...d, assetId: null, assetKey: null, source: null, sourceRef: null, isSatisfied: false };
          }),
        };
      })
    );
  }


  // ─── Config Helpers ──────────────────────────────────────────────────────

  /** Config keys that should not be editable (identity / structural fields) */
  const READ_ONLY_CONFIG_KEYS = new Set(["id", "type"]);
  const HIDDEN_CONFIG_KEYS = new Set(["visible", "id", "type"]);
  const ALWAYS_RESPONSIVE_KEYS = new Set(["position", "scale"]);
  // Transform config keys pulled into their own section above Config, in this order.
  const TRANSFORM_KEYS = ["position", "scale", "rotation", "depth", "opacity"];
  // The two halves of a responsive value; also the branches a composite edit
  // path can address.
  const ORIENTATION_KEYS = ["portrait", "landscape"];

  function isOrientationMap(value) {
    return (
      typeof value === "object" &&
      value !== null &&
      !Array.isArray(value) &&
      ("portrait" in value || "landscape" in value)
    );
  }

  /** Normalize position/scale to always use { portrait, landscape } shape. */
  function toAlwaysResponsiveValue(key, value) {
    if (!ALWAYS_RESPONSIVE_KEYS.has(key)) return value;
    if (isOrientationMap(value)) return value;
    if (
      key === "position" &&
      typeof value === "object" &&
      value !== null &&
      "x" in value &&
      "y" in value
    ) {
      return { portrait: { x: value.x, y: value.y }, landscape: { x: value.x, y: value.y } };
    }
    if (key === "scale" && typeof value === "number") {
      return { portrait: value, landscape: value };
    }
    return value;
  }

  function normalizeComponentConfig(config) {
    const next = { ...config };
    const migratedKeys = [];
    for (const key of ALWAYS_RESPONSIVE_KEYS) {
      if (!(key in next)) continue;
      const normalized = toAlwaysResponsiveValue(key, next[key]);
      if (normalized !== next[key]) {
        next[key] = normalized;
        migratedKeys.push(key);
      }
    }
    return { config: next, migratedKeys };
  }

  /**
   * Map of { componentId → { fieldKey → templateString } } built from the
   * DB-stored snapshot. Any field whose stored value is a {{global.*}} template
   * string is treated as variable-bound and rendered read-only.
   */
  const templateMap = useMemo(() => {
    const storedComponents = _.get(props, "selectedVersion.playEngineConfig.components", []);
    const map = {};
    for (const comp of storedComponents) {
      if (!comp?.config) continue;
      for (const [key, val] of Object.entries(comp.config)) {
        if (typeof val === "string" && val.includes("{{global.")) {
          if (!map[comp.id]) map[comp.id] = {};
          map[comp.id][key] = val;
        }
      }
    }
    return map;
  }, [_.get(props, "selectedVersion._id")]);

  /**
   * Detect the editor type for a config value.
   * Returns: "boolean" | "number" | "color" | "vec2" | "object" | "responsive-vec2" | ...
   */
  function isPlainConfigObject(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
    if ("portrait" in value || "landscape" in value) return false;
    if ("x" in value && "y" in value && Object.keys(value).length === 2) return false;
    return true;
  }

  /** Default item shape when adding a new entry to known array config keys. */
  const ARRAY_ITEM_TEMPLATES = {
    wordStyles: { index: 0, tint: "#ffffff" },
    wordGroups: { id: "group1", words: [0], breakAfter: false },
    words: 0,
  };

  function getArrayItemTemplate(key, existingArray) {
    if (existingArray.length > 0) {
      return JSON.parse(JSON.stringify(existingArray[existingArray.length - 1]));
    }
    if (ARRAY_ITEM_TEMPLATES[key]) {
      return JSON.parse(JSON.stringify(ARRAY_ITEM_TEMPLATES[key]));
    }
    return { portrait: { x: 0.5, y: 0.5 }, landscape: { x: 0.5, y: 0.5 } };
  }

  function detectFieldType(key, value) {
    // Check for orientation-aware (responsive) values first
    if (
      typeof value === "object" &&
      value !== null &&
      ("portrait" in value || "landscape" in value)
    ) {
      const inner = value.portrait ?? value.landscape;
      if (typeof inner === "number") return "responsive-number";
      if (typeof inner === "boolean") return "responsive-boolean";
      if (typeof inner === "string") return "responsive-string";
      if (
        typeof inner === "object" &&
        inner !== null &&
        "x" in inner &&
        "y" in inner
      )
        return "responsive-vec2";
    }
    if (typeof value === "boolean") return "boolean";
    if (typeof value === "number") return "number";
    if (typeof value === "string" && /^#[0-9a-f]{3,8}$/i.test(value)) return "color";
    if (
      typeof value === "object" &&
      value !== null &&
      "x" in value &&
      "y" in value &&
      Object.keys(value).length === 2
    )
      return "vec2";
    if (typeof value === "string") return "string";
    if (Array.isArray(value)) return "array";
    if (isPlainConfigObject(value)) return "object";
    return null; // unsupported — skip
  }

  /**
   * Schema types like "number" must not mask an already-split orientation map.
   * Otherwise NumberConfigField receives an object and displays "[object Object]".
   */
  function resolveFieldType(key, effectiveValue, schemaType) {
    // emitter-op deliberately spans number / array / object — its own editor
    // switches between those shapes, so detection must not override it.
    if (schemaType === "emitter-op") return "emitter-op";

    // A shadow is a string to everything except the person editing it.
    //
    // Matched on the field NAME as well as on the schema because the two travel
    // separately: the schema is read out of the engine bundle at runtime, so a
    // component still declaring `boxShadow` as a plain string — every one of
    // them until the engine that says otherwise is deployed — would get the
    // text box back. The editor falls back to that text box by itself for any
    // value it cannot represent, so recognising the name costs nothing.
    if (schemaType === "box-shadow") return "box-shadow";
    if (key === "boxShadow" && typeof effectiveValue === "string") return "box-shadow";

    const detected = detectFieldType(key, effectiveValue);
    if (isOrientationMap(effectiveValue) && detected?.startsWith("responsive-")) {
      return detected;
    }
    // Prefer detected object/array over a mismatched schema (e.g. shadow typed as "string").
    if (detected === "object" || detected === "array") {
      return detected;
    }
    return schemaType || detected;
  }

  // Generic ordering so the Config section groups fields by control type
  // (vec2 → number → emitter-op → color → toggle → string → dropdown → array →
  // object → template-bound), instead of arbitrary object key order.
  const CONFIG_TYPE_ORDER = {
    vec2: 0, "responsive-vec2": 0,
    number: 1, "responsive-number": 1,
    "emitter-op": 2,
    color: 3,
    "box-shadow": 3,
    boolean: 4, "responsive-boolean": 4,
    string: 5, "responsive-string": 5,
    dropdown: 6,
    array: 7,
    object: 8,
  };

  function configFieldRank(componentId, key, value, configSchema) {
    // Global-variable-bound fields are locked boxes — sink them to the bottom.
    if (templateMap[componentId]?.[key]) return 9;
    const fieldSchema = configSchema?.[key];
    if (fieldSchema?.options && Array.isArray(fieldSchema.options) && fieldSchema.options.length > 0) {
      return CONFIG_TYPE_ORDER.dropdown;
    }
    let effectiveValue = value;
    if (ALWAYS_RESPONSIVE_KEYS.has(key) || fieldSchema?.alwaysResponsive === true) {
      effectiveValue = toAlwaysResponsiveValue(key, value);
    }
    let type = resolveFieldType(key, effectiveValue, fieldSchema?.type);
    // A responsive-string holding a hex color renders as a color picker.
    if (type === "responsive-string") {
      const inner = effectiveValue?.portrait ?? effectiveValue?.landscape;
      if (fieldSchema?.type === "color" || (typeof inner === "string" && /^#[0-9a-f]{3,8}$/i.test(inner))) {
        type = "color";
      }
    }
    return CONFIG_TYPE_ORDER[type] ?? 10;
  }

  /** Config keys of one component (transform keys excluded), ordered by control type. */
  function sortedConfigKeys(comp) {
    return Object.keys(comp.config)
      .filter((key) => key !== "idleAnimation" && !TRANSFORM_KEYS.includes(key))
      .sort(
        (ka, kb) =>
          configFieldRank(comp.componentId, ka, comp.config[ka], comp.configSchema) -
          configFieldRank(comp.componentId, kb, comp.config[kb], comp.configSchema)
      );
  }

  /**
   * Handle a config field change:
   *   - Local UI updates synchronously
   *   - Live preview dispatches immediately (color fields debounce via ColorConfigField)
   *   - Persist debounced 200ms (all field types)
   */
  function schedulePersistConfigChange(timerKey, entry) {
    pendingConfigRef.current.set(timerKey, entry);
    const persistTimers = configPersistTimersRef.current;
    if (persistTimers.has(timerKey)) clearTimeout(persistTimers.get(timerKey));
    persistTimers.set(
      timerKey,
      setTimeout(() => {
        persistTimers.delete(timerKey);
        const pending = pendingConfigRef.current.get(timerKey);
        if (!pending) return;
        persistConfigChange(
          pending.componentId,
          pending.key,
          pending.value,
          pending.isOverride,
          pending.language
        );
      }, PLAY_ENGINE_SYNC_DEBOUNCE_MS)
    );
  }

  function handleConfigChange(componentId, key, value) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }

    const language = selectedLanguage;
    const isOverride = language && language !== "en";
    const timerKey = `${componentId}::${key}`;
    const entry = { componentId, key, value, isOverride, language };

    // 1. Update local component state synchronously.
    setComponents((prev) =>
      prev.map((c) =>
        c.componentId === componentId
          ? { ...c, config: { ...c.config, [key]: value } }
          : c
      )
    );

    // 2. Debounced persist (all field types).
    schedulePersistConfigChange(timerKey, entry);

    // 3. Live preview — immediate (color fields use ColorConfigField + commitConfigChange).
    previewConfigChange(componentId, key, value, isOverride, language);
  }

  /**
   * Commit a color change after ColorConfigField's debounce (local UI already
   * updated). Persist + preview fire immediately here — no second delay.
   */
  function commitConfigChange(componentId, key, value) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }

    const language = selectedLanguage;
    const isOverride = language && language !== "en";

    setComponents((prev) =>
      prev.map((c) =>
        c.componentId === componentId
          ? { ...c, config: { ...c.config, [key]: value } }
          : c
      )
    );
    persistConfigChange(componentId, key, value, isOverride, language);
    previewConfigChange(componentId, key, value, isOverride, language);
  }

  /**
   * Persist a config edit to the DB using targeted sub-paths.
   * Never replaces the entire playEngineConfig at once: replacing the whole
   * config reads from the stale React selectedVersion state, which causes rapid
   * multi-field edits to overwrite each other's pending changes — visible as
   * input reversion on production (slow-network) environments.
   */
  function persistConfigChange(componentId, key, value, isOverride, language) {
    const endPersist = perfStart("persist:setSelectedVersionPathValue (sync)");
    try {
      persistConfigChangeInner(componentId, key, value, isOverride, language);
    } finally {
      endPersist();
    }
  }

  function persistConfigChangeInner(componentId, key, value, isOverride, language) {
    if (isOverride) {
      // Persist as localization override using a deep but specific path.
      // We must ensure the full override nesting exists; build a minimal patch that
      // setSelectedVersionPathValue can merge at the right sub-path.
      const overridePath = `playEngineConfig.localization.overrides.${language}.${componentId}.configOverrides`;
      // Ensure the localization shell is present in the persisted version if missing.
      const existingLocalization = _.get(props, "selectedVersion.playEngineConfig.localization");
      if (!existingLocalization) {
        // First override for this snapshot: write the whole localization block once.
        const locBlock = _.cloneDeep(_.get(props, "selectedVersion.playEngineConfig.localization") || {
          currentLanguage: language,
          availableLanguages: ["en", language],
          overrides: {},
        });
        if (!locBlock.overrides) locBlock.overrides = {};
        if (!locBlock.overrides[language]) locBlock.overrides[language] = {};
        if (!locBlock.overrides[language][componentId]) locBlock.overrides[language][componentId] = {};
        if (!locBlock.overrides[language][componentId].configOverrides) locBlock.overrides[language][componentId].configOverrides = {};
        _.set(locBlock.overrides[language][componentId].configOverrides, key, value);
        props.setSelectedVersionPathValue({ path: "playEngineConfig.localization", value: locBlock });
      } else {
        props.setSelectedVersionPathValue({
          path: overridePath, value: _.set(
            _.cloneDeep(_.get(props, `selectedVersion.${overridePath}`) || {}),
            key,
            value
          )
        });
      }
    } else {
      // Standard path: use a targeted path into the specific component's config entry.
      // Fall back to the full-config approach only when the component index can't be found
      // (should not happen in practice).
      const components = _.get(props, "selectedVersion.playEngineConfig.components");
      const compIdx = components ? components.findIndex((c) => c.id === componentId) : -1;
      if (compIdx !== -1) {
        // Specific path — does NOT touch any other part of playEngineConfig.
        props.setSelectedVersionPathValue({
          path: `playEngineConfig.components.${compIdx}.config.${key}`,
          value,
        });
      } else {
        // Fallback (component not found in DB snapshot yet — e.g. just added).
        const dbConfig = _.cloneDeep(_.get(props, "selectedVersion.playEngineConfig") || {});
        if (dbConfig.components) {
          const comp = dbConfig.components.find((c) => c.id === componentId);
          if (comp) _.set(comp.config, key, value);
        }
        props.setSelectedVersionPathValue({ path: "playEngineConfig", value: dbConfig });
      }
    }
  }

  /**
   * Dispatch the live-preview action for a config edit.
   *
   * Queued rather than awaited. Each of these used to be a round trip that
   * registered a `message` listener and armed a five-second timeout, per
   * keystroke — none of which is needed to move a sprite. The queue coalesces a
   * burst on one field into a single message and posts it on the next frame.
   */
  function previewConfigChange(componentId, key, value, isOverride, language) {
    if (isOverride) {
      // Applies immediately when the engine's current language matches.
      previewQueue.push(
        props.iFrameId,
        previewQueue.previewKey.configOverride(language, componentId, key),
        {
          type: "playengine:dispatch-action",
          action: {
            target: "engine",
            name: "addLocalizationOverrideConfig",
            args: { language, componentId, path: key, value },
          },
        }
      );
      return;
    }

    const partial = {};
    _.set(partial, key, value);
    previewQueue.push(props.iFrameId, previewQueue.previewKey.config(componentId, key), {
      type: "playengine:dispatch-action",
      action: { target: componentId, name: "updateConfig", args: partial },
    });
  }

  /** Persist idle toggle, then replay idle animation immediately when re-enabled. */
  async function handleIdleAnimationToggle(componentId, enabled, replayArgs) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }

    const language = selectedLanguage;
    const isOverride = language && language !== "en";
    const value = { enabled };

    // Toggles must apply immediately and in order (not debounced), otherwise the
    // playAnimation dispatch below could race the idleAnimation config update.
    setComponents((prev) =>
      prev.map((c) =>
        c.componentId === componentId
          ? { ...c, config: { ...c.config, idleAnimation: value } }
          : c
      )
    );

    const timerKey = `${componentId}::idleAnimation`;
    const entry = { componentId, key: "idleAnimation", value, isOverride, language };

    const persistTimers = configPersistTimersRef.current;
    if (persistTimers.has(timerKey)) {
      clearTimeout(persistTimers.get(timerKey));
      persistTimers.delete(timerKey);
    }

    schedulePersistConfigChange(timerKey, entry);
    previewConfigChange(componentId, "idleAnimation", value, isOverride, language);
    // playAnimation has to arrive after the config change it depends on, so the
    // queued command goes out before it rather than racing the next frame.
    previewQueue.flush(props.iFrameId);

    if (!enabled || !replayArgs || !props.iFrameId) return;
    try {
      await sendCommand(props.iFrameId, {
        type: "playengine:dispatch-action",
        action: {
          target: componentId,
          name: "playAnimation",
          args: replayArgs,
        },
      });
    } catch (err) {
      console.warn(`[handleIdleAnimationToggle] playAnimation failed: ${err.message}`);
    }
  }

  /**
   * Renders an appropriate input widget for a single config field.
   * @param {string} componentId
   * @param {string} key - config field name
   * @param {*} value - current value
   * @param {object} [configSchema] - optional schema with field metadata
   * @param {function} [onChangeOverride] - if provided, called instead of handleConfigChange (used by array items)
   */
  function renderConfigField(componentId, key, value, configSchema, onChangeOverride, opts) {
    if (HIDDEN_CONFIG_KEYS.has(key)) return null;
    const readOnly = READ_ONLY_CONFIG_KEYS.has(key);
    const displayKey = humanizeKey(key);
    // Multi-edit: apply every mutation to all target components (defaults to the
    // single `componentId`). `mixed` marks a field whose value differs across them.
    const targetIds = opts?.targetIds || [componentId];
    const mixed = !!opts?.mixed;

    const fieldSchema = configSchema?.[key];
    const alwaysResponsive =
      ALWAYS_RESPONSIVE_KEYS.has(key) || fieldSchema?.alwaysResponsive === true;

    let effectiveValue = value;
    if (alwaysResponsive) {
      effectiveValue = toAlwaysResponsiveValue(key, value);
    }

    // Apply a control's new value to every target component, merging only the
    // sub-values that changed so each component keeps its own untouched parts
    // (e.g. editing x of a vec2 doesn't overwrite the others' y). For a single
    // target this reduces to applying `val` as-is.
    const applyToTargets = (applyFn, val) => {
      targetIds.forEach((id) => {
        const comp = components.find((c) => c.componentId === id);
        if (!comp) return;
        const curRaw = comp.config?.[key];
        const cur = alwaysResponsive ? toAlwaysResponsiveValue(key, curRaw) : curRaw;
        applyFn(id, key, applyConfigDelta(cur, effectiveValue, val));
      });
    };

    /**
     * The same, for a control that edits ONE part of a composite value — an axis
     * of a vec2, one orientation of a responsive value. `subPath` says which
     * part, and it is written into each component's OWN value.
     *
     * Composing the whole value here and letting `applyConfigDelta` work out
     * what moved is not enough with several components selected: the control
     * shows the first one's value, so every part the user did not touch arrives
     * carrying that component's number. Where the others' value is shaped even
     * slightly differently — a missing orientation, a key the delta cannot line
     * up — those parts land on them too, and dragging x quietly pulls their y
     * onto the first component's. Writing only the edited path cannot do that.
     *
     * `options.gesture` marks a drag or an arrow step, which reads as "move by"
     * rather than "set to". With more than one component selected each then
     * moves by what the control moved, keeping the spacing between them —
     * dragging x on two components slides both instead of stacking them on the
     * first one's x. Typed values carry no flag and land as written.
     *
     * An empty `subPath` addresses the value itself, for a plain number field.
     */
    /**
     * Whether the selected components disagree on ONE part of the value.
     *
     * The row's own `mixed` flag covers the whole field, which is too coarse for
     * a control per axis: two components at the same y and different x would
     * mark both as differing. Resolved exactly as the writer does, so what a
     * field shows and what an edit writes cannot drift apart.
     */
    const isMixedAt = (subPath) => {
      if (targetIds.length < 2) return false;
      const partOf = (id) => {
        const comp = components.find((c) => c.componentId === id);
        const curRaw = comp?.config?.[key];
        const cur = alwaysResponsive ? toAlwaysResponsiveValue(key, curRaw) : curRaw;
        return subPath.length ? _.get(cur, subPath) : cur;
      };
      const first = partOf(targetIds[0]);
      return !targetIds.every((id) => _.isEqual(partOf(id), first));
    };

    const applyToTargetsAtPath = (applyFn, subPath, subValue, options = {}) => {
      const displayed = subPath.length ? _.get(effectiveValue, subPath) : effectiveValue;

      // The control reports the value it holds, which is not always a change:
      // a scrub that has not crossed a step, or a number typed back as it was.
      // Writing it anyway would put the displayed component's number on all the
      // others — the whole point of editing per component is not to do that.
      if (_.isEqual(subValue, displayed)) return;

      const isRelative =
        Boolean(options.gesture) &&
        targetIds.length > 1 &&
        Number.isFinite(subValue) &&
        Number.isFinite(displayed);

      targetIds.forEach((id) => {
        const comp = components.find((c) => c.componentId === id);
        if (!comp) return;
        const curRaw = comp.config?.[key];
        const cur = alwaysResponsive ? toAlwaysResponsiveValue(key, curRaw) : curRaw;

        // Each component's own number moves by the same amount the control did.
        // Off-grid values stay off-grid: the offset is what must survive, so
        // only the float noise of the addition is rounded away.
        const own = subPath.length ? _.get(cur, subPath) : cur;
        const value =
          isRelative && Number.isFinite(own)
            ? roundFloatNoise(own + (subValue - displayed))
            : subValue;

        if (subPath.length === 0) {
          applyFn(id, key, value);
          return;
        }

        // Only fall back to the displayed value's shape when this component has
        // nothing of its own to build on.
        const next = _.cloneDeep(_.isPlainObject(cur) ? cur : effectiveValue);
        if (!_.isPlainObject(next)) return;
        // `_.set` would build a missing branch as an empty object, leaving a
        // component whose value carries only one orientation with nothing but
        // the edited axis in the other one — its y gone. Seed the branch from
        // this component's OWN opposite orientation; the displayed value is the
        // last resort, since taking it is what pulls one component's y onto the
        // rest.
        const [branch] = subPath;
        if (subPath.length > 1 && !_.isPlainObject(next[branch])) {
          const ownSibling = ORIENTATION_KEYS.find(
            (k) => k !== branch && _.isPlainObject(next[k])
          );
          const seed = ownSibling ? next[ownSibling] : _.get(effectiveValue, [branch]);
          next[branch] = _.isPlainObject(seed) ? _.cloneDeep(seed) : {};
        }
        _.set(next, subPath, value);
        applyFn(id, key, next);
      });
    };

    // Check if this field is bound to a global variable in the stored snapshot
    const templateString = templateMap[componentId]?.[key];
    const isTemplateBound = !!templateString;
    if (isTemplateBound) {
      const varPath = templateString.match(/\{\{global\.([^}]+)\}\}/)?.[1] ?? templateString;
      return (
        <div
          key={key}
          style={{
            marginBottom: "8px",
            backgroundColor: "#141824",
            border: "1px solid #2a3555",
            borderRadius: "4px",
            padding: "6px 8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px", flexWrap: "wrap" }}>
            <Typography variant="caption" style={{ color: "#555" }}>{displayKey}</Typography>
            <Tooltip title="Initial value controlled by custom variable — edit in the Custom Variables tab." arrow>
              <Chip
                label={`{{global.${varPath}}}`}
                size="small"
                style={{
                  backgroundColor: "#1a2e4a",
                  color: "#64b5f6",
                  height: "16px",
                  fontSize: "10px",
                  fontFamily: "monospace",
                  cursor: "default",
                }}
              />
            </Tooltip>
          </div>
          <TextField
            multiline
            minRows={1}
            maxRows={3}
            size="small"
            fullWidth
            disabled
            value={String(value ?? "")}
            InputProps={{ style: { color: "#444", fontSize: "12px", backgroundColor: "#0e1220" } }}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>
      );
    }

    const onChange = onChangeOverride
      ? (val) => onChangeOverride(val)
      : (val) => applyToTargets(handleConfigChange, val);

    /**
     * For controls that edit one part of a composite value. `fullValue` is that
     * value composed as it always was — array items render through
     * `onChangeOverride`, which owns the whole value and knows nothing about
     * sub-paths, so they keep taking it.
     */
    const onChangeAt = (subPath, subValue, fullValue, options) =>
      onChangeOverride
        ? onChangeOverride(fullValue)
        : applyToTargetsAtPath(handleConfigChange, subPath, subValue, options);

    const commitAt = (subPath, subValue, fullValue, options) =>
      onChangeOverride
        ? onChangeOverride(fullValue)
        : applyToTargetsAtPath(commitConfigChange, subPath, subValue, options);

    const fieldType = resolveFieldType(key, effectiveValue, fieldSchema?.type);
    const isCurrentlyResponsive =
      alwaysResponsive ||
      fieldType === "responsive-number" ||
      fieldType === "responsive-boolean" ||
      fieldType === "responsive-vec2" ||
      fieldType === "responsive-string" ||
      isOrientationMap(effectiveValue);
    const SPLITTABLE_BASE_TYPES = ["number", "boolean", "vec2", "string", "color"];
    const canToggleResponsive =
      !readOnly &&
      !alwaysResponsive &&
      fieldSchema?.canBeResponsive !== false &&
      fieldType !== "array" &&
      (isCurrentlyResponsive || SPLITTABLE_BASE_TYPES.includes(fieldType));

    const splitMergeBtn = canToggleResponsive ? (
      <Tooltip
        title={
          isCurrentlyResponsive
            ? "Merge to single value"
            : "Split by orientation (portrait / landscape)"
        }
        arrow
      >
        <IconButton
          size="small"
          style={{ padding: "0 2px", marginLeft: "2px", color: isCurrentlyResponsive ? "#64b5f6" : "#555" }}
          onClick={() => {
            if (isCurrentlyResponsive) {
              onChange(effectiveValue.portrait ?? effectiveValue.landscape);
            } else {
              onChange({ portrait: effectiveValue, landscape: effectiveValue });
            }
          }}
        >
          {isCurrentlyResponsive
            ? <CallMergeIcon style={{ fontSize: 13 }} />
            : <CallSplitIcon style={{ fontSize: 13 }} />}
        </IconButton>
      </Tooltip>
    ) : null;


    const orientationLabel = props.newSetPortrait !== false ? "Portrait" : "Landscape";

    // Small orientation badge shown above the control for responsive fields.
    const orientationTag = (
      <div style={{ fontSize: "9px", color: "#8a8d93", textTransform: "uppercase", letterSpacing: "0.4px", marginBottom: "2px" }}>
        {orientationLabel}
      </div>
    );

    // Dropdown fields — support optional orientation split
    if (fieldSchema?.options && Array.isArray(fieldSchema.options) && fieldSchema.options.length > 0) {
      if (isCurrentlyResponsive) {
        const isPortrait = props.newSetPortrait !== false;
        const portraitVal = effectiveValue.portrait != null ? effectiveValue.portrait : effectiveValue;
        const landscapeVal = effectiveValue.landscape != null ? effectiveValue.landscape : effectiveValue;
        const currentVal = isPortrait ? portraitVal : landscapeVal;
        return (
          <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
            {orientationTag}
            <Select
              size="small"
              fullWidth
              value={currentVal}
              disabled={readOnly}
              style={{ color: "#fff", fontSize: "12px" }}
              MenuProps={{
                PaperProps: {
                  style: { backgroundColor: CARD_BG, maxHeight: "300px" },
                },
              }}
              onChange={(e) => {
                const newPortrait = isPortrait ? e.target.value : portraitVal;
                const newLandscape = !isPortrait ? e.target.value : landscapeVal;
                onChangeAt([isPortrait ? "portrait" : "landscape"], e.target.value, {
                  portrait: newPortrait,
                  landscape: newLandscape,
                });
              }}
            >
              {fieldSchema.options.map((option) => (
                <MenuItem key={option} value={option} style={{ color: "#eee", fontSize: "12px" }}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FieldRow>
        );
      }
      return (
        <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
          <Select
            size="small"
            fullWidth
            value={effectiveValue}
            disabled={readOnly}
            style={{ color: "#fff", fontSize: "12px" }}
            MenuProps={{
              PaperProps: {
                style: { backgroundColor: CARD_BG, maxHeight: "300px" },
              },
            }}
            onChange={(e) => onChange(e.target.value)}
          >
            {fieldSchema.options.map((option) => (
              <MenuItem key={option} value={option} style={{ color: "#eee", fontSize: "12px" }}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FieldRow>
      );
    }

    // Otherwise, fall back to auto-detection (schema type takes precedence if provided)
    if (!fieldType) return null;

    switch (fieldType) {
      case "boolean":
        return (
          <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
            <Switch
              size="small"
              checked={effectiveValue}
              disabled={readOnly}
              onChange={(e) => onChange(e.target.checked)}
              color="primary"
            />
          </FieldRow>
        );

      case "number":
        return (
          <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
            <NumberConfigField
              value={effectiveValue}
              disabled={readOnly}
              mixed={isMixedAt([])}
              onChange={(num, options) => onChangeAt([], num, num, options)}
            />
          </FieldRow>
        );

      case "color":
        return (
          <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
            <ColorConfigField
              value={effectiveValue}
              disabled={readOnly}
              onCommit={(val) => applyToTargets(commitConfigChange, val)}
            />
          </FieldRow>
        );

      case "vec2":
        return (
          <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
            <div style={{ display: "flex", gap: "6px" }}>
              <NumberConfigField
                value={effectiveValue.x}
                disabled={readOnly}
                label="x"
                mixed={isMixedAt(["x"])}
                onChange={(num, options) =>
                  onChangeAt(["x"], num, { x: num, y: effectiveValue.y }, options)
                }
              />
              <NumberConfigField
                value={effectiveValue.y}
                disabled={readOnly}
                label="y"
                mixed={isMixedAt(["y"])}
                onChange={(num, options) =>
                  onChangeAt(["y"], num, { x: effectiveValue.x, y: num }, options)
                }
              />
            </div>
          </FieldRow>
        );

      case "responsive-vec2": {
        const isPortrait = props.newSetPortrait !== false;
        const portraitVec = effectiveValue.portrait != null ? effectiveValue.portrait : effectiveValue;
        const landscapeVec = effectiveValue.landscape != null ? effectiveValue.landscape : effectiveValue;
        const currentVec = isPortrait ? portraitVec : landscapeVec;
        const handleVecAxis = (axis, num, options) => {
          const newPortrait = isPortrait
            ? { ...portraitVec, [axis]: num }
            : portraitVec;
          const newLandscape = !isPortrait
            ? { ...landscapeVec, [axis]: num }
            : landscapeVec;
          onChangeAt(
            [isPortrait ? "portrait" : "landscape", axis],
            num,
            { portrait: newPortrait, landscape: newLandscape },
            options
          );
        };
        return (
          <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
            {orientationTag}
            <div style={{ display: "flex", gap: "6px" }}>
              <NumberConfigField
                value={currentVec.x}
                disabled={readOnly}
                label="x"
                mixed={isMixedAt([isPortrait ? "portrait" : "landscape", "x"])}
                onChange={(num, options) => handleVecAxis("x", num, options)}
              />
              <NumberConfigField
                value={currentVec.y}
                disabled={readOnly}
                label="y"
                mixed={isMixedAt([isPortrait ? "portrait" : "landscape", "y"])}
                onChange={(num, options) => handleVecAxis("y", num, options)}
              />
            </div>
          </FieldRow>
        );
      }

      case "responsive-number": {
        const isPortrait = props.newSetPortrait !== false;
        const portraitNum = effectiveValue.portrait != null ? effectiveValue.portrait : effectiveValue;
        const landscapeNum = effectiveValue.landscape != null ? effectiveValue.landscape : effectiveValue;
        const currentNum = isPortrait ? portraitNum : landscapeNum;
        return (
          <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
            {orientationTag}
            <NumberConfigField
              value={currentNum}
              disabled={readOnly}
              mixed={isMixedAt([isPortrait ? "portrait" : "landscape"])}
              onChange={(num, options) => {
                const newPortrait = isPortrait ? num : portraitNum;
                const newLandscape = !isPortrait ? num : landscapeNum;
                onChangeAt(
                  [isPortrait ? "portrait" : "landscape"],
                  num,
                  { portrait: newPortrait, landscape: newLandscape },
                  options
                );
              }}
            />
          </FieldRow>
        );
      }

      case "responsive-boolean": {
        const isPortrait = props.newSetPortrait !== false;
        const portraitBool = effectiveValue.portrait != null ? effectiveValue.portrait : !!effectiveValue;
        const landscapeBool = effectiveValue.landscape != null ? effectiveValue.landscape : !!effectiveValue;
        const currentBool = isPortrait ? portraitBool : landscapeBool;
        return (
          <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
            {orientationTag}
            <div style={{ display: "flex", alignItems: "center" }}>
              <Switch
                size="small"
                checked={currentBool}
                disabled={readOnly}
                onChange={(e) => {
                  const newPortrait = isPortrait ? e.target.checked : portraitBool;
                  const newLandscape = !isPortrait ? e.target.checked : landscapeBool;
                  onChangeAt([isPortrait ? "portrait" : "landscape"], e.target.checked, {
                    portrait: newPortrait,
                    landscape: newLandscape,
                  });
                }}
                color="primary"
              />
              <Typography variant="caption" style={{ color: readOnly ? "#666" : "#ccc", marginLeft: "2px" }}>
                {currentBool ? "true" : "false"}
              </Typography>
            </div>
          </FieldRow>
        );
      }

      case "responsive-string": {
        const isPortrait = props.newSetPortrait !== false;
        const portraitStr = effectiveValue.portrait != null ? effectiveValue.portrait : effectiveValue;
        const landscapeStr = effectiveValue.landscape != null ? effectiveValue.landscape : effectiveValue;
        const currentStr = isPortrait ? portraitStr : landscapeStr;
        const isColorField = fieldSchema?.type === "color" ||
          (typeof currentStr === "string" && /^#[0-9a-f]{3,8}$/i.test(currentStr));
        // Splitting a shadow by orientation must not cost the editor for it —
        // that would make the split button a downgrade to a text box.
        const isShadowField = fieldSchema?.type === "box-shadow" || key === "boxShadow";
        if (isShadowField) {
          return (
            <React.Fragment key={key}>
              <BoxShadowConfigField
                value={currentStr}
                disabled={readOnly}
                action={splitMergeBtn}
                tag={orientationTag}
                onChange={(val) => {
                  const newPortrait = isPortrait ? val : portraitStr;
                  const newLandscape = !isPortrait ? val : landscapeStr;
                  onChangeAt([isPortrait ? "portrait" : "landscape"], val, {
                    portrait: newPortrait,
                    landscape: newLandscape,
                  });
                }}
              />
            </React.Fragment>
          );
        }
        if (isColorField) {
          return (
            <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
              {orientationTag}
              <ColorConfigField
                value={currentStr}
                disabled={readOnly}
                onCommit={(val) => {
                  const newPortrait = isPortrait ? val : portraitStr;
                  const newLandscape = !isPortrait ? val : landscapeStr;
                  commitAt([isPortrait ? "portrait" : "landscape"], val, {
                    portrait: newPortrait,
                    landscape: newLandscape,
                  });
                }}
              />
            </FieldRow>
          );
        }
        return (
          <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
            {orientationTag}
            <StringConfigField
              value={currentStr}
              disabled={readOnly}
              onChange={(val) => {
                const newPortrait = isPortrait ? val : portraitStr;
                const newLandscape = !isPortrait ? val : landscapeStr;
                onChangeAt([isPortrait ? "portrait" : "landscape"], val, {
                  portrait: newPortrait,
                  landscape: newLandscape,
                });
              }}
            />
          </FieldRow>
        );
      }

      case "string":
        return (
          <FieldRow key={key} label={displayKey} readOnly={readOnly} action={splitMergeBtn} mixed={mixed}>
            <StringConfigField
              value={effectiveValue}
              disabled={readOnly}
              onChange={(val) => onChange(val)}
            />
          </FieldRow>
        );

      // The control renders its own FieldRows — one per property, in the panel's
      // own geometry — so this adds nothing but a key. The section header
      // (CONFIG_GROUP_BY_KEY) is what names the group they belong to.
      case "box-shadow":
        return (
          <React.Fragment key={key}>
            <BoxShadowConfigField
              value={effectiveValue}
              disabled={readOnly}
              onChange={(val) => onChange(val)}
              action={splitMergeBtn}
            />
          </React.Fragment>
        );

      case "array": {
        if (!Array.isArray(effectiveValue)) return null;
        return (
          <div
            key={key}
            style={{
              display: "grid",
              gridTemplateColumns: "96px 1fr",
              columnGap: "8px",
              padding: "4px 6px",
              alignItems: "start",
            }}
          >
            <div
              title={displayKey}
              style={{
                fontSize: "12px",
                color: readOnly ? "#6f7278" : FIELD_LABEL,
                paddingTop: "5px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayKey}
            </div>
            <div style={{ minWidth: 0 }}>
            {effectiveValue.length === 0 && (
              <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: "4px", fontSize: "10px" }}>
                {key === "wordStyles" || key === "wordGroups"
                  ? "Requires splitMode \"words\" or \"groups\" to take effect."
                  : null}
              </Typography>
            )}
            {effectiveValue.map((item, idx) => (
              <div key={idx} style={{ display: "flex", alignItems: "flex-start", gap: "4px", marginBottom: "4px" }}>
                <div style={{ flex: 1, paddingLeft: "8px", borderLeft: "2px solid #444" }}>
                  <Typography variant="caption" style={{ color: "#666", display: "block", marginBottom: "2px", fontSize: "10px" }}>
                    [{idx}]
                  </Typography>
                  {renderConfigField(
                    componentId,
                    key,
                    item,
                    undefined,
                    (newItem) => {
                      const next = [...effectiveValue];
                      next[idx] = newItem;
                      onChange(next);
                    }
                  )}
                </div>
                <IconButton
                  size="small"
                  disabled={readOnly}
                  style={{ color: "#666", padding: "2px", marginTop: "16px" }}
                  onClick={() => {
                    const next = [...effectiveValue];
                    next.splice(idx, 1);
                    onChange(next);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </div>
            ))}
            {!readOnly && (
              <Button
                size="small"
                variant="outlined"
                style={{ color: "#aaa", borderColor: "#555", fontSize: "11px", marginTop: "4px" }}
                onClick={() => {
                  const template = getArrayItemTemplate(key, effectiveValue);
                  onChange([...effectiveValue, template]);
                }}
              >
                + Add
              </Button>
            )}
            </div>
          </div>
        );
      }

      case "emitter-op": {
        const mode = emitterOpMode(effectiveValue);
        const obj =
          effectiveValue && typeof effectiveValue === "object" && !Array.isArray(effectiveValue)
            ? effectiveValue
            : {};
        // Normalise before patching so an incomplete stored object (a legacy
        // snapshot, a hand-edited value) is written back whole — the engine
        // treats a half-shape like {min} as an unrecognised form and falls
        // back to a constant.
        const patch = (changes) =>
          onChange({ ...convertEmitterOp(effectiveValue, mode), ...changes });

        const easeOptions =
          typeof obj.ease === "string" && !EMITTER_OP_EASES.includes(obj.ease)
            ? [obj.ease, ...EMITTER_OP_EASES]
            : EMITTER_OP_EASES;

        const subLabel = (text) => (
          <div style={{ fontSize: "10px", color: "#7f838b", marginBottom: "2px" }}>{text}</div>
        );

        return (
          <div
            key={key}
            style={{
              display: "grid",
              gridTemplateColumns: "96px 1fr",
              columnGap: "8px",
              padding: "4px 6px",
              alignItems: "start",
            }}
          >
            <div
              title={displayKey}
              style={{
                fontSize: "12px",
                color: readOnly ? "#6f7278" : FIELD_LABEL,
                paddingTop: "5px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayKey}
            </div>

            <div style={{ minWidth: 0 }}>
              <Select
                value={mode}
                disabled={readOnly}
                onChange={(e) => onChange(convertEmitterOp(effectiveValue, e.target.value))}
                style={{ fontSize: "12px", width: "100%", marginBottom: "4px" }}
              >
                {EMITTER_OP_MODES.map((m) => (
                  <MenuItem key={m.value} value={m.value} style={{ fontSize: "12px" }}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>

              {mode === "constant" && (
                <NumberConfigField
                  value={typeof effectiveValue === "number" ? effectiveValue : 0}
                  disabled={readOnly}
                  onChange={(num) => onChange(num)}
                />
              )}

              {mode === "range" && (
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                  <NumberConfigField
                    value={obj.min ?? 0}
                    disabled={readOnly}
                    label="min"
                    onChange={(num) => patch({ min: num })}
                  />
                  <NumberConfigField
                    value={obj.max ?? 0}
                    disabled={readOnly}
                    label="max"
                    onChange={(num) => patch({ max: num })}
                  />
                  <Tooltip title="Round to whole numbers" arrow>
                    <Switch
                      size="small"
                      checked={obj.int === true}
                      disabled={readOnly}
                      onChange={(e) => patch({ int: e.target.checked })}
                      color="primary"
                    />
                  </Tooltip>
                </div>
              )}

              {mode === "curve" && (
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                    <NumberConfigField
                      value={obj.start ?? 0}
                      disabled={readOnly}
                      label="start"
                      onChange={(num) => patch({ start: num })}
                    />
                    <NumberConfigField
                      value={obj.end ?? 0}
                      disabled={readOnly}
                      label="end"
                      onChange={(num) => patch({ end: num })}
                    />
                  </div>
                  <div>
                    {subLabel("ease")}
                    <Select
                      value={obj.ease ?? "none"}
                      disabled={readOnly}
                      onChange={(e) => patch({ ease: e.target.value })}
                      style={{ fontSize: "12px", width: "100%" }}
                    >
                      {easeOptions.map((ease) => (
                        <MenuItem key={ease} value={ease} style={{ fontSize: "12px" }}>
                          {ease}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                    <Tooltip title="Each particle starts at a random point between start and end" arrow>
                      <Switch
                        size="small"
                        checked={obj.random === true}
                        disabled={readOnly}
                        onChange={(e) => patch({ random: e.target.checked })}
                        color="primary"
                      />
                    </Tooltip>
                    <span style={{ fontSize: "10px", color: "#7f838b" }}>random start</span>
                  </div>
                </div>
              )}

              {mode === "list" && (
                <div>
                  {(Array.isArray(effectiveValue) ? effectiveValue : []).map((item, idx) => (
                    <div key={idx} style={{ display: "flex", alignItems: "center", gap: "4px", marginBottom: "4px" }}>
                      <NumberConfigField
                        value={typeof item === "number" ? item : 0}
                        disabled={readOnly}
                        onChange={(num) => {
                          const next = [...effectiveValue];
                          next[idx] = num;
                          onChange(next);
                        }}
                      />
                      <IconButton
                        size="small"
                        disabled={readOnly || effectiveValue.length <= 1}
                        style={{ color: "#666", padding: "2px" }}
                        onClick={() => onChange(effectiveValue.filter((_, i) => i !== idx))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                  {!readOnly && (
                    <Button
                      size="small"
                      variant="outlined"
                      style={{ color: "#aaa", borderColor: "#555", fontSize: "11px" }}
                      onClick={() => {
                        const arr = Array.isArray(effectiveValue) ? effectiveValue : [];
                        onChange([...arr, arr.length > 0 ? arr[arr.length - 1] : 0]);
                      }}
                    >
                      + Add
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      }

      case "object": {
        if (!isPlainConfigObject(effectiveValue)) return null;
        return (
          <div key={key} style={{ marginBottom: "4px" }}>
            {Object.entries(effectiveValue).map(([subKey, subVal]) => (
              <div key={subKey}>
                {renderConfigField(
                  componentId,
                  subKey,
                  subVal,
                  undefined,
                  (newSubVal) => onChange({ ...effectiveValue, [subKey]: newSubVal })
                )}
              </div>
            ))}
          </div>
        );
      }

      default:
        return null;
    }
  }

  // ─── Parent-child tree helpers ───────────────────────────────────────────

  const getComponentId = useCallback((comp) => comp.componentId, []);

  /** Map from parentId → array of child components (sorted when order is stored) */
  const childrenMap = useMemo(() => {
    const map = {};
    components.forEach((comp) => {
      const pid = comp.parentId;
      if (pid) {
        if (!map[pid]) map[pid] = [];
        map[pid].push(comp);
      }
    });
    Object.keys(map).forEach((pid) => {
      map[pid] = sortSiblings(
        map[pid],
        effectiveOrderMap,
        orderKeyForParent(pid),
        getComponentId
      );
    });
    return map;
  }, [components, effectiveOrderMap, getComponentId]);

  /** Only root-level components (no parent) rendered at the top of the tree */
  const rootComponents = useMemo(() => {
    const roots = components.filter((comp) => !comp.parentId);
    return sortSiblings(roots, effectiveOrderMap, "root", getComponentId);
  }, [components, effectiveOrderMap, getComponentId]);

  function getSortedSiblingsForOrderScope(scopeKey, orderMap = effectiveOrderMap) {
    if (scopeKey === "root") {
      const uncategorized = components.filter(
        (c) => !c.parentId && !componentCategoriesMap[c.componentId]
      );
      return sortSiblings(uncategorized, orderMap, scopeKey, getComponentId);
    }
    if (scopeKey.startsWith("category:")) {
      const categoryPath = scopeKey.slice("category:".length);
      const siblings = components.filter(
        (c) => !c.parentId && (componentCategoriesMap[c.componentId] || "") === categoryPath
      );
      return sortSiblings(siblings, orderMap, scopeKey, getComponentId);
    }
    if (scopeKey.startsWith("parent:")) {
      const parentId = scopeKey.slice("parent:".length);
      const children = components.filter((c) => c.parentId === parentId);
      return sortSiblings(children, orderMap, scopeKey, getComponentId);
    }
    return [];
  }

  function beginComponentDrag(reorderScope, componentId, pointerEvent, canCategorize = false) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const container = document.querySelector(`[data-playengine-sort-list="${reorderScope}"]`);
    const sortLookupId = reorderScope === ROOT_LAYOUT_SCOPE
      ? rootLayoutEntryForComponent(componentId)
      : componentId;
    const row =
      container?.querySelector(`[data-sort-id="${sortLookupId}"]`) ||
      pointerEvent.currentTarget.closest("[data-sort-id]");
    if (!row) return;
    const rowRect = row.getBoundingClientRect();
    const labelEl = row.querySelector("[data-sort-label]") || row;
    const labelRect = labelEl.getBoundingClientRect();
    const labelCs = window.getComputedStyle(labelEl);
    const labelTextStyle = {
      fontSize: labelCs.fontSize,
      fontFamily: labelCs.fontFamily,
      fontWeight: labelCs.fontWeight,
      lineHeight: labelCs.lineHeight,
      color: labelCs.color,
      letterSpacing: labelCs.letterSpacing,
    };
    const ids = reorderScope === ROOT_LAYOUT_SCOPE
      ? [...effectiveRootLayoutOrder]
      : getSortedSiblingsForOrderScope(reorderScope).map((c) => c.componentId);
    const grabOffsetY = pointerEvent.clientY - labelRect.top;
    try {
      pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
    } catch (_) { /* ignore */ }
    const session = {
      dragKind: "component",
      scopeKey: reorderScope,
      draggedId: reorderScope === ROOT_LAYOUT_SCOPE
        ? rootLayoutEntryForComponent(componentId)
        : componentId,
      componentId,
      previewIds: ids,
      initialIds: ids,
      pointerX: pointerEvent.clientX,
      pointerY: pointerEvent.clientY,
      grabOffsetY,
      canCategorize,
      floatTop: labelRect.top,
      floatLeft: rowRect.left,
      floatWidth: rowRect.width,
      slotHeight: Math.max(20, Math.round(labelRect.height)),
      labelTextStyle,
      pointerId: pointerEvent.pointerId,
      captureTarget: pointerEvent.currentTarget,
    };
    dragSessionRef.current = session;
    isDraggingSortRef.current = true;
    setDragSession(session);
  }

  // Fixed-width column so every row's grip sits in the same place right after
  // the expand/collapse arrow (folder + component, any nesting depth's own arrow).
  const ROW_GRIP_STYLE = {
    width: 14,
    height: 16,
    marginRight: 2,
    padding: 0,
    color: "#777",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    touchAction: "none",
    boxSizing: "border-box",
  };

  function renderCategoryFolderLabel(name, categoryPath, options = {}) {
    const { bold = false, small = false, parentPath = "", canReorder = false } = options;
    const isDropTarget = categoryDropTarget === categoryPath;
    const canManage = props.userPermissions?.update !== false;
    const canDrag = canReorder && canManage;
    const gripInteractive = canDrag && !dragSession;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          minHeight: bold ? "24px" : "22px",
        }}
      >
        <div
          data-category-drop={categoryPath}
          data-sort-label
          style={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            borderRadius: "3px",
            boxSizing: "border-box",
            border: isDropTarget ? "1px solid #90caf9" : "1px solid transparent",
            backgroundColor: isDropTarget ? "rgba(144, 202, 249, 0.12)" : undefined,
          }}
        >
          <span
            data-reorder-handle={gripInteractive ? "" : undefined}
            title={canDrag ? "Drag to reorder category" : undefined}
            className="proto-row-grip"
            onPointerDown={gripInteractive ? (e) => {
              e.stopPropagation();
              e.preventDefault();
              beginCategoryDrag(parentPath, name, categoryPath, e);
            } : undefined}
            style={{
              ...ROW_GRIP_STYLE,
              cursor: gripInteractive ? "grab" : "default",
              visibility: canDrag ? "visible" : "hidden",
            }}
          >
            <DragIndicatorIcon style={{ fontSize: 13 }} />
          </span>
          <FolderIcon
            style={{
              fontSize: small ? 14 : 15,
              marginRight: "6px",
              color: "#c9a25b",
              flexShrink: 0,
            }}
          />
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: "13px",
              fontWeight: bold ? 600 : 500,
              color: "#d6d6d6",
            }}
          >
            {name}
          </span>
        </div>
        {canManage && (
          <span className="proto-cat-actions" style={{ display: "flex", flexShrink: 0, marginLeft: "4px" }}>
            <IconButton
              size="small"
              title="Rename category"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setRenameCategoryName(name);
                setRenameCategoryState({ categoryPath, name });
              }}
              style={{ color: "#999", padding: 2 }}
            >
              <EditIcon style={{ fontSize: 14 }} />
            </IconButton>
            <IconButton
              size="small"
              title="Delete category"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setDeleteCategoryState({ categoryPath, name });
              }}
              style={{ color: "#999", padding: 2 }}
            >
              <DeleteIcon style={{ fontSize: 14 }} />
            </IconButton>
          </span>
        )}
      </div>
    );
  }

  function renderComponentTreeLabel(comp, reorderScope, isChild, canCategorize = false) {
    const canDrag =
      props.userPermissions?.update !== false && (!!reorderScope || canCategorize);
    const gripInteractive = canDrag && !dragSession;
    const isActiveDrag = dragSession?.draggedId === comp.componentId;

    return (
      <div
        data-sort-label
        style={{
          display: "flex",
          alignItems: "center",
          minHeight: "22px",
          flex: 1,
          minWidth: 0,
        }}
      >
        <span
          data-reorder-handle={gripInteractive ? "" : undefined}
          title={canDrag
            ? (canCategorize ? "Drag to reorder or move to category" : "Drag to reorder")
            : undefined}
          className="proto-row-grip"
          onPointerDown={gripInteractive ? (e) => {
            e.stopPropagation();
            e.preventDefault();
            beginComponentDrag(reorderScope, comp.componentId, e, canCategorize);
          } : undefined}
          style={{
            ...ROW_GRIP_STYLE,
            cursor: gripInteractive ? (isActiveDrag ? "grabbing" : "grab") : "default",
            color: isActiveDrag ? "#90caf9" : "#777",
            visibility: canDrag ? "visible" : "hidden",
          }}
        >
          <DragIndicatorIcon style={{ fontSize: 13 }} />
        </span>
        <img
          src={InnerComponentIcon}
          alt="component"
          style={{ width: "13px", height: "13px", marginRight: "6px", flexShrink: 0, opacity: 0.85 }}
        />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "13px" }}>
          <span style={{ fontWeight: 600, color: "#e0e0e0" }}>{comp.componentId}</span>
          <span style={{ color: "#7d7d7d" }}> ({comp.componentType})</span>
          {isChild && (
            <span style={{ color: "#6f6f6f", fontSize: "10px", marginLeft: "6px" }}>child</span>
          )}
        </span>
      </div>
    );
  }

  const {
    categoryTree,
    uncategorizedRootComponents: rawUncategorizedRoots,
    topLevelFolderOrder,
  } = useMemo(
    () => buildCategoryTree(rootComponents, componentCategoriesMap, componentCategoryFolders),
    [rootComponents, componentCategoriesMap, componentCategoryFolders]
  );

  const componentSearchMatches = useCallback((component) =>
    matchesTabSearch(component.componentId, categorySearch) ||
    Object.keys(component.config || {}).some((key) =>
      !HIDDEN_CONFIG_KEYS.has(key) &&
      (matchesTabSearch(key, categorySearch) || matchesTabSearch(humanizeKey(key), categorySearch))
    ), [categorySearch]);

  const settingsKeyMatches = (component, key) =>
    !categorySearch.trim() || matchesTabSearch(component.componentId, categorySearch) ||
    matchesTabSearch(key, categorySearch) || matchesTabSearch(humanizeKey(key), categorySearch);

  const matchingComponentIds = useMemo(() => {
    if (!categorySearch.trim()) return new Set(components.map((component) => component.componentId));
    const ids = new Set();
    function visit(component) {
      const childMatches = (childrenMap[component.componentId] || []).map(visit).some(Boolean);
      const matches = componentSearchMatches(component) || childMatches;
      if (matches) ids.add(component.componentId);
      return matches;
    }
    rootComponents.forEach(visit);
    return ids;
  }, [categorySearch, childrenMap, componentSearchMatches, components, rootComponents]);

  const searchedCategoryTree = useMemo(() => {
    if (!categorySearch.trim()) return categoryTree;
    function filterNodes(nodes, ancestorMatches = false) {
      const result = {};
      Object.entries(nodes || {}).forEach(([name, node]) => {
        const matches = ancestorMatches || matchesTabSearch(name, categorySearch);
        const children = filterNodes(node.children, matches);
        const components = matches ? node.components : node.components.filter((component) =>
          matchingComponentIds.has(component.componentId)
        );
        if (matches || Object.keys(children).length || components.length) {
          result[name] = { ...node, children, components };
        }
      });
      return result;
    }
    return filterNodes(categoryTree);
  }, [categoryTree, categorySearch, matchingComponentIds]);

  const searchExpandedItems = useMemo(() => {
    if (!categorySearch.trim()) return expandedItems;
    const ids = new Set(expandedItems);
    function visit(nodes, prefix = "") {
      Object.entries(nodes || {}).forEach(([name, node]) => {
        const path = prefix ? `${prefix}-${name}` : name;
        ids.add(`cat-${path}`);
        visit(node.children, path);
      });
    }
    visit(searchedCategoryTree);
    function expandComponents(component) {
      if (!matchingComponentIds.has(component.componentId)) return;
      ids.add(component.componentId);
      (childrenMap[component.componentId] || []).forEach(expandComponents);
    }
    rootComponents.forEach(expandComponents);
    return Array.from(ids);
  }, [expandedItems, searchedCategoryTree, categorySearch, matchingComponentIds, rootComponents, childrenMap]);

  const uncategorizedRootComponents = useMemo(
    () => sortSiblings(rawUncategorizedRoots, effectiveOrderMap, "root", getComponentId),
    [rawUncategorizedRoots, effectiveOrderMap, getComponentId]
  );

  function sortComponentsInCategory(node, categoryPath) {
    return sortSiblings(
      node.components,
      effectiveOrderMap,
      orderKeyForCategory(categoryPath),
      getComponentId
    );
  }

  function getCategoryNodeChildren(parentPath) {
    if (!parentPath) return categoryTree;
    const segments = parseCategoryPath(parentPath);
    let children = categoryTree;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const node = children?.[seg];
      if (!node) return {};
      if (i === segments.length - 1) return node.children || {};
      children = node.children;
    }
    return {};
  }

  function sortCategoryChildNames(parentPath, names) {
    return sortSiblings(
      names,
      categoryChildOrderScopeMap,
      orderKeyForCategoryFolderSiblings(parentPath),
      (name) => name
    );
  }

  function getSortedCategoryFolderSiblings(parentPath) {
    if (!parentPath) return effectiveTopLevelFolderOrder;
    return sortCategoryChildNames(parentPath, Object.keys(getCategoryNodeChildren(parentPath)));
  }

  function beginCategoryDrag(parentPath, categoryName, categoryPath, pointerEvent) {
    if (props.userPermissions?.update === false) {
      props.createSnackbar?.(
        "You do not have permission to update, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    const scopeKey = parentPath ? orderKeyForCategoryFolderSiblings(parentPath) : ROOT_LAYOUT_SCOPE;
    const sortLookupId = parentPath ? categoryName : rootLayoutEntryForFolder(categoryName);
    const container = document.querySelector(`[data-playengine-sort-list="${scopeKey}"]`);
    const row =
      container?.querySelector(`[data-sort-id="${sortLookupId}"]`) ||
      pointerEvent.currentTarget.closest("[data-sort-id]");
    if (!row) return;
    const rowRect = row.getBoundingClientRect();
    const labelEl = row.querySelector("[data-sort-label]") || row;
    const labelRect = labelEl.getBoundingClientRect();
    const labelCs = window.getComputedStyle(labelEl);
    const labelTextStyle = {
      fontSize: labelCs.fontSize,
      fontFamily: labelCs.fontFamily,
      fontWeight: labelCs.fontWeight,
      lineHeight: labelCs.lineHeight,
      color: labelCs.color,
      letterSpacing: labelCs.letterSpacing,
    };
    const ids = parentPath
      ? getSortedCategoryFolderSiblings(parentPath)
      : [...effectiveRootLayoutOrder];
    const grabOffsetY = pointerEvent.clientY - labelRect.top;
    try {
      pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId);
    } catch (_) { /* ignore */ }
    const session = {
      dragKind: "category",
      scopeKey,
      draggedId: sortLookupId,
      name: categoryName,
      categoryPath,
      categoryParentPath: parentPath,
      previewIds: ids,
      initialIds: ids,
      pointerX: pointerEvent.clientX,
      pointerY: pointerEvent.clientY,
      grabOffsetY,
      canCategorize: false,
      floatTop: labelRect.top,
      floatLeft: rowRect.left,
      floatWidth: rowRect.width,
      slotHeight: Math.max(20, Math.round(labelRect.height)),
      labelTextStyle,
      pointerId: pointerEvent.pointerId,
      captureTarget: pointerEvent.currentTarget,
    };
    dragSessionRef.current = session;
    isDraggingSortRef.current = true;
    setDragSession(session);
  }

  function canDeleteAssets() {
    return props.userPermissions?.delete !== false;
  }

  function openDropdownDelete(e, opener) {
    e.preventDefault();
    e.stopPropagation();
    if (!canDeleteAssets()) {
      props.createSnackbar?.(
        "You do not have permission to delete, contact your administrator.",
        "warning",
        false
      );
      return;
    }
    opener();
  }

  function openMediaDelete(kind, record, selectedId, groupId = null, rebindContext = null) {
    mediaDeleteRebindRef.current = rebindContext;
    setMediaDeleteDialog({ kind, open: true, record, selectedId, groupId });
  }

  function closeMediaDeleteDialog() {
    mediaDeleteRebindRef.current = null;
    setMediaDeleteDialog({
      kind: null,
      open: false,
      record: null,
      selectedId: null,
      groupId: null,
    });
  }

  async function handleMediaDeletedAfter() {
    const ctx = mediaDeleteRebindRef.current;
    mediaDeleteRebindRef.current = null;
    if (!ctx?.wasSelected) return;


    const { componentId, dep, bucket, deletedId, deletedGroupId, pairConfig, boundAssetKey } = ctx;
    if (!componentId || !dep) return;

    setDepBusy(componentId, dep.key, true);
    try {
      const bucketAssets = getAssetsForBucketAndType(bucket, dep.type);
      if (boundAssetKey) {
        if (pairConfig) {
          const groups = buildPairGroups(bucketAssets, pairConfig).filter(
            (g) => g[pairConfig.groupRecordKey] !== deletedGroupId
          );
          if (groups[0]) await updateAssetKeySource(boundAssetKey, dep.type, null, groups[0], pairConfig);
          else await clearAssetKeySource(boundAssetKey, dep.type);
        } else {
          const remaining = bucketAssets.filter((m) => String(m._id) !== String(deletedId));
          if (remaining[0]) await updateAssetKeySource(boundAssetKey, dep.type, remaining[0], null, null);
          else await clearAssetKeySource(boundAssetKey, dep.type);
        }
        return;
      }

      if (pairConfig) {
        const groups = buildPairGroups(bucketAssets, pairConfig).filter(
          (g) => g[pairConfig.groupRecordKey] !== deletedGroupId
        );
        if (groups[0]) await handleSelectPair(componentId, dep, groups[0], pairConfig);
        else await handleClearComponentAsset(componentId, dep);
        return;
      }

      const remaining = bucketAssets.filter((m) => String(m._id) !== String(deletedId));
      if (remaining[0]) await handleSelectAsset(componentId, dep, remaining[0]);
      else await handleClearComponentAsset(componentId, dep);
    } catch (err) {
      console.error("handleMediaDeletedAfter failed:", err);
      props.createSnackbar?.(`Failed to rebind asset: ${err.message}`, "error", false);
    } finally {
      setDepBusy(componentId, dep.key, false);
    }
  }

  function mediaDeleteKindForDepType(depType) {
    switch (depType) {
      case "audio": return "sound";
      case "font": return "font";
      case "video": return "video";
      case "atlas": return "atlas";
      case "bitmap-font": return "bitmap-font";
      default: return "image";
    }
  }

  // ─── Render ──────────────────────────────────────────────────────────────

  if (_.isNil(props.selectedVersion) || _.isNil(props.selectedBase)) return null;

  const supportedLanguages = _.get(props, "selectedBase.supportedLanguages", {});
  const languageEntries = Object.entries(supportedLanguages);
  const addedLanguages = new Set(
    _.get(props, "selectedVersion.playEngineConfig.localization.availableLanguages") || ["en"]
  );

  // Resolve the selected tree items to actual components (drops category-folder
  // ids like "cat-…" that carry no config).
  const selectedComps = useMemo(
    () => components.filter((c) => selectedComponentIds.includes(c.componentId)),
    [components, selectedComponentIds]
  );

  // Prune stale ids when components disappear (version switch, preset apply, re-fetch).
  useEffect(() => {
    setSelectedComponentIds((prev) => {
      const next = prev.filter(
        (id) => id.startsWith("cat-") || components.some((c) => c.componentId === id)
      );
      return next.length === prev.length ? prev : next;
    });
  }, [components]);

  // Measure a definite panel height so the flex split has something to divide.
  // We size to the nearest scrollable ancestor's *visible* height (the TabPanel)
  // rather than the viewport, so the container fits exactly and that ancestor
  // never scrolls — otherwise an outer scrollbar would drag both panels at once.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    function findScrollParent(node) {
      let p = node.parentElement;
      while (p) {
        const oy = window.getComputedStyle(p).overflowY;
        if (oy === "auto" || oy === "scroll") return p;
        p = p.parentElement;
      }
      return null;
    }
    const sp = findScrollParent(el);
    function measure() {
      const rect = el.getBoundingClientRect();
      if (rect.top === 0 && rect.height === 0) return; // hidden / not laid out yet
      const bottom = sp
        ? sp.getBoundingClientRect().top + sp.clientHeight
        : window.innerHeight - 8;
      const next = Math.max(240, Math.floor(bottom - rect.top));
      setAvailHeight((prev) => (prev === next ? prev : next));
    }
    measure();
    // Layout often isn't settled on first open (SwipeableViews/tab panel sizing),
    // which previously left the 80vh fallback until a manual resize. Re-measure
    // across a couple of frames and whenever the scroll parent's size changes.
    const raf1 = requestAnimationFrame(measure);
    const raf2 = requestAnimationFrame(() => requestAnimationFrame(measure));
    window.addEventListener("resize", measure);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      if (sp) ro.observe(sp);
      if (el.parentElement) ro.observe(el.parentElement);
    }
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      window.removeEventListener("resize", measure);
      if (ro) ro.disconnect();
    };
  }, []);

  // Drag the divider between the outliner and properties panels; the ratio is
  // clamped and persisted to localStorage on release.
  function startDividerDrag(e) {
    e.preventDefault();
    const container = containerRef.current;
    if (!container) return;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.userSelect = "none";
    function onMove(ev) {
      const rect = container.getBoundingClientRect();
      if (rect.height <= 0) return;
      const ratio = Math.max(0.2, Math.min(0.8, (ev.clientY - rect.top) / rect.height));
      setTreeRatio(ratio);
    }
    function onUp() {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.body.style.userSelect = prevUserSelect;
      setTreeRatio((r) => {
        window.localStorage.setItem("playEngineVersionSettingsSplit", String(r));
        return r;
      });
    }
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
  }

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        flexDirection: "column",
        height: availHeight ? `${availHeight}px` : "80vh",
        minHeight: 0,
        overflow: "hidden",
        backgroundColor: PANEL_BG,
      }}
    >
      <PlayEngineTabSearch value={categorySearch} onChange={setCategorySearch} />
      {/* Language selector — pinned toolbar, does not scroll with the tree */}
      {languageEntries.length > 0 && (
        <div
          style={{
            flex: "0 0 auto",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "10px 12px",
            backgroundColor: BAR_BG,
            borderBottom: `1px solid ${SECTION_DIVIDER}`,
          }}
        >
          <Typography style={{ color: "#a7abb2", flexShrink: 0, fontSize: "13px", fontWeight: 500 }}>
            Language
          </Typography>
          <TextField
            select
            fullWidth
            size="small"
            value={selectedLanguage}
            onChange={(e) => handleLanguageSwitch(e.target.value)}
            InputProps={{ style: { color: "#fff", fontSize: "13px", backgroundColor: "#212226", borderRadius: 4, padding: "2px 4px" } }}
            SelectProps={{ MenuProps: { PaperProps: { style: { backgroundColor: CARD_BG } } } }}
          >
            {languageEntries.map(([key, caption]) => {
              const isAdded = key === "en" || addedLanguages.has(key);
              return (
                <MenuItem key={key} value={key} style={{
                  color: isAdded ? "#90caf9" : "#666",
                  fontWeight: isAdded ? 600 : 400,
                }}>
                  {caption}{isAdded ? " ●" : ""}
                </MenuItem>
              );
            })}
          </TextField>
        </div>
      )}

      {/* Outliner (component tree) — top resizable panel */}
      <div style={{ flexGrow: treeRatio, flexShrink: 1, flexBasis: 0, minHeight: 0, overflowY: "auto" }}>

      {/* Loading spinner */}
      {loading && (
        <Card style={{ margin: "5px", backgroundColor: CARD_BG }}>
          <CardContent style={{ display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {!loading && error && (
        <Card style={{ margin: "5px", backgroundColor: CARD_BG }}>
          <CardContent>
            <Typography variant="body2" color="error">{error}</Typography>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!loading && !error && components.length === 0 && (
        <Card style={{ margin: "5px", backgroundColor: CARD_BG }}>
          <CardContent>
            <Typography variant="body2" style={{ color: "#aaa" }}>
              No components found. Make sure the preview is loaded.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Component tree */}
      {!loading && components.length > 0 && (
        <>
          <div style={{ display: "flex", justifyContent: "flex-start", padding: "4px 5px 4px 10px", marginTop: "8px" }}>
            <Button
              size="small"
              disabled={props.userPermissions?.update === false}
              onClick={() => {
                setNewCategoryName("");
                setCreateCategoryOpen(true);
              }}
              startIcon={<AddIcon style={{ fontSize: 14 }} />}
              style={{
                color: "#aaa",
                fontSize: "11px",
                textTransform: "none",
                minWidth: 0,
                padding: "2px 8px",
                backgroundColor: BAR_BG,
                borderRadius: 4,
              }}
            >
              Category
            </Button>
          </div>

          {categorySearch.trim() && Object.keys(searchedCategoryTree).length === 0 &&
            !uncategorizedRootComponents.some((component) => matchingComponentIds.has(component.componentId)) && (
            <Typography style={{ color: "#888", padding: "8px 12px", fontSize: 12 }}>
              No components or knobs match this search.
            </Typography>
          )}

          <SimpleTreeView
            aria-label="playEngine-components"
            multiSelect
            expandedItems={searchExpandedItems}
            onExpandedItemsChange={(event, itemIds) => setExpandedItems(itemIds)}
            selectedItems={selectedComponentIds}
            onSelectedItemsChange={(event, itemIds) => setSelectedComponentIds(itemIds)}
            expansionTrigger="iconContainer"
            slots={{
              expandIcon: ArrowRightIcon,
              collapseIcon: ArrowDropDownIcon,
            }}
            sx={{
              overflowX: "hidden",
              flexGrow: 1,
              // Compact, Unity/Blender-style rows: tight height, full-width
              // hover + selection highlight, subtle indentation guides.
              "& .MuiTreeItem-content": {
                padding: "0 4px",
                borderRadius: 0,
                minHeight: "22px",
                // Keep expand arrow and drag grip flush; default MUI gap pushed grips around.
                gap: 0,
              },
              "& .MuiTreeItem-content:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.055)",
              },
              "& .MuiTreeItem-content.Mui-selected, & .MuiTreeItem-content.Mui-selected.Mui-focused": {
                backgroundColor: "rgba(144, 202, 249, 0.20)",
              },
              "& .MuiTreeItem-content.Mui-selected:hover": {
                backgroundColor: "rgba(144, 202, 249, 0.26)",
              },
              "& .MuiTreeItem-content.Mui-focused": {
                backgroundColor: "transparent",
              },
              "& .MuiTreeItem-iconContainer": {
                width: "16px",
                marginRight: 0,
                flexShrink: 0,
                "& svg": { fontSize: 18, color: "#8a8a8a" },
              },
              "& .MuiTreeItem-label": {
                paddingLeft: 0,
              },
              "& .MuiTreeItem-group": {
                marginLeft: "9px",
                paddingLeft: "6px",
                borderLeft: "1px solid rgba(255, 255, 255, 0.09)",
              },
            }}
          >
            <div
              data-playengine-root-zone
              data-category-drop="uncategorized"
              data-playengine-sort-list={ROOT_LAYOUT_SCOPE}
              style={{
                minHeight: dragSession?.canCategorize ? 56 : undefined,
                margin: "2px 4px 4px",
                borderRadius: "4px",
                boxSizing: "border-box",
                border: categoryDropTarget === "uncategorized"
                  ? "1px solid #90caf9"
                  : "1px solid transparent",
                backgroundColor: categoryDropTarget === "uncategorized"
                  ? "rgba(144, 202, 249, 0.06)"
                  : "transparent",
                ...(dragSession ? { overflowAnchor: "none" } : {}),
              }}
            >
              {effectiveRootLayoutOrder.map((entry) => {
                const parsed = parseRootLayoutEntry(entry);
                if (!parsed) return null;

                if (parsed.type === "component") {
                  const comp = components.find((c) => c.componentId === parsed.id);
                  if (!comp) return null;
                  if (categorySearch.trim() && !matchingComponentIds.has(comp.componentId)) return null;
                  return renderComponentNode(comp, {
                    wrapInCard: true,
                    reorderScope: ROOT_LAYOUT_SCOPE,
                    canCategorize: true,
                    sortId: entry,
                  });
                }

                const groupName = parsed.id;
                const groupNode = searchedCategoryTree[groupName];
                if (!groupNode) return null;
                const categoryPath = groupName;
                const categoryReorderScope = orderKeyForCategory(categoryPath);
                const sortedCategoryComponents = sortComponentsInCategory(groupNode, categoryPath);
                const isCatDraggedAway =
                  dragSession?.dragKind === "category" &&
                  dragSession?.scopeKey === ROOT_LAYOUT_SCOPE &&
                  dragSession?.draggedId === entry;
                if (isCatDraggedAway) {
                  return (
                    <div
                      key={`cat-slot-${groupName}`}
                      data-sort-id={entry}
                      style={{
                        height: dragSession.slotHeight,
                        margin: "5px",
                        borderRadius: "4px",
                        border: "1px dashed rgba(144, 202, 249, 0.35)",
                        backgroundColor: "rgba(144, 202, 249, 0.06)",
                        boxSizing: "border-box",
                      }}
                      aria-hidden
                    />
                  );
                }
                const sortedChildNames = sortCategoryChildNames(
                  categoryPath,
                  Object.keys(groupNode.children || {})
                );
                return (
                  <div key={`cat-${groupName}`} data-sort-id={entry}>
                    <TreeItem
                      itemId={`cat-${groupName}`}
                      label={renderCategoryFolderLabel(groupName, categoryPath, {
                        bold: true,
                        parentPath: "",
                        canReorder: !categorySearch.trim(),
                      })}
                    >
                      <div
                        data-playengine-sort-list={categoryReorderScope}
                        style={dragSession ? { overflowAnchor: "none" } : undefined}
                      >
                        {sortedCategoryComponents.map((comp) =>
                          renderComponentNode(comp, {
                            reorderScope: categoryReorderScope,
                            canCategorize: true,
                            searchAncestorMatches: matchesTabSearch(categoryPath, categorySearch),
                          })
                        )}
                      </div>
                      <div
                        data-playengine-sort-list={orderKeyForCategoryFolderSiblings(categoryPath)}
                        style={dragSession ? { overflowAnchor: "none" } : undefined}
                      >
                        {sortedChildNames.map((childName) =>
                          renderCategorySubTree(
                            childName,
                            groupNode.children[childName],
                            `${groupName}-${childName}`,
                            `${groupName}:${childName}`,
                            categoryPath
                          )
                        )}
                      </div>
                    </TreeItem>
                  </div>
                );
              })}
            </div>
          </SimpleTreeView>
        </>
      )}
      </div>

      {/* Draggable divider between the outliner and the properties panel */}
      <div
        onPointerDown={startDividerDrag}
        style={{
          flex: "0 0 auto",
          height: "6px",
          cursor: "row-resize",
          backgroundColor: "#2b2b2b",
          borderTop: "1px solid #1f1f1f",
          borderBottom: "1px solid #1f1f1f",
        }}
      />

      {/* Properties panel — settings for the selected component */}
      <div
        className="proto-props-panel"
        style={{
          flexGrow: 1 - treeRatio,
          flexShrink: 1,
          flexBasis: 0,
          minHeight: 0,
          overflowY: "auto",
          backgroundColor: PANEL_BG,
        }}
      >
        <style>{`
          .proto-props-panel .MuiInputBase-root {
            background-color: #212226;
            border: 1px solid #3a3a3f;
            border-radius: 4px;
            min-height: 24px;
          }
          .proto-props-panel .MuiInputBase-root.Mui-focused {
            border-color: #64b5f6;
          }
          .proto-props-panel .MuiInputBase-root.Mui-disabled {
            background-color: #1c1d20;
            border-color: #2f3033;
          }
          .proto-props-panel .MuiInputBase-input {
            padding: 3px 6px;
            font-size: 12px;
          }
          .proto-props-panel .MuiInput-underline:before,
          .proto-props-panel .MuiInput-underline:after {
            display: none;
          }
          .proto-props-panel .MuiSelect-select {
            padding: 3px 24px 3px 6px;
            background: transparent;
          }
          .proto-props-panel .MuiSelect-icon {
            color: #8a8d93;
          }
        `}</style>
        {selectedComps.length === 0 ? (
          <Typography
            variant="body2"
            style={{ color: "#777", textAlign: "center", padding: "24px 16px", display: "block" }}
          >
            Select a component to edit its settings
          </Typography>
        ) : selectedComps.length === 1 ? (
          renderComponentSettings(selectedComps[0])
        ) : (
          renderMultiComponentSettings(selectedComps)
        )}
      </div>

      <Dialog
        open={createCategoryOpen}
        onClose={() => setCreateCategoryOpen(false)}
        PaperProps={{ style: { backgroundColor: CARD_BG, minWidth: 320 } }}
      >
        <DialogTitle style={{ color: "#eee" }}>Create category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Category name"
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleCreateCategory();
            }}
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "#888" } }}
            helperText="Single folder name — no colons"
            FormHelperTextProps={{ style: { color: "#666" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCategoryOpen(false)} style={{ color: "#aaa" }}>
            Cancel
          </Button>
          <Button onClick={handleCreateCategory} color="primary" variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!renameCategoryState}
        onClose={() => {
          setRenameCategoryState(null);
          setRenameCategoryName("");
        }}
        PaperProps={{ style: { backgroundColor: CARD_BG, minWidth: 320 } }}
      >
        <DialogTitle style={{ color: "#eee" }}>Rename category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Category name"
            value={renameCategoryName}
            onChange={(e) => setRenameCategoryName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleRenameCategory();
            }}
            InputProps={{ style: { color: "#fff" } }}
            InputLabelProps={{ style: { color: "#888" } }}
            helperText="Single folder name — no colons"
            FormHelperTextProps={{ style: { color: "#666" } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRenameCategoryState(null);
              setRenameCategoryName("");
            }}
            style={{ color: "#aaa" }}
          >
            Cancel
          </Button>
          <Button onClick={handleRenameCategory} color="primary" variant="contained">
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteCategoryState}
        onClose={() => setDeleteCategoryState(null)}
        PaperProps={{ style: { backgroundColor: CARD_BG, minWidth: 320 } }}
      >
        <DialogTitle style={{ color: "#eee" }}>Delete category</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ color: "#ccc" }}>
            {(() => {
              if (!deleteCategoryState) return "";
              const count = countComponentsInCategory(
                componentCategoriesMap,
                deleteCategoryState.categoryPath
              );
              if (count === 0) {
                return `Delete "${deleteCategoryState.name}"? This folder is empty.`;
              }
              return `Delete "${deleteCategoryState.name}"? ${count} component${count === 1 ? "" : "s"} will move to the root list.`;
            })()}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCategoryState(null)} style={{ color: "#aaa" }}>
            Cancel
          </Button>
          <Button onClick={handleDeleteCategory} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {dragSession && ReactDOM.createPortal(
        (() => {
          const ghostShellStyle = {
            position: "fixed",
            top: dragSession.floatTop,
            left: dragSession.floatLeft,
            width: dragSession.floatWidth,
            height: dragSession.slotHeight,
            zIndex: 10000,
            pointerEvents: "none",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.5)",
            borderRadius: "3px",
            backgroundColor: BAR_BG,
            border: "1px solid rgba(144, 202, 249, 0.55)",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            padding: "0 6px",
            willChange: "top",
          };

          if (dragSession.dragKind === "category") {
            return (
              <div ref={dragGhostRef} style={ghostShellStyle}>
                <DragIndicatorIcon style={{ fontSize: 14, color: "#90caf9", marginRight: 2, flexShrink: 0 }} />
                <FolderIcon style={{ fontSize: 15, color: "#c9a25b", marginRight: 6, flexShrink: 0 }} />
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#d6d6d6",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                  }}
                >
                  {dragSession.name}
                </span>
              </div>
            );
          }

          const dragged = components.find((c) => c.componentId === dragSession.componentId);
          if (!dragged) return null;
          return (
            <div ref={dragGhostRef} style={ghostShellStyle}>
              <DragIndicatorIcon style={{ fontSize: 14, color: "#90caf9", marginRight: 2, flexShrink: 0 }} />
              <img
                src={InnerComponentIcon}
                alt=""
                style={{ width: "13px", height: "13px", marginRight: "6px", flexShrink: 0, opacity: 0.85 }}
              />
              <span
                style={{
                  fontSize: "13px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                }}
              >
                <span style={{ fontWeight: 600, color: "#e0e0e0" }}>{dragged.componentId}</span>
                <span style={{ color: "#7d7d7d" }}> ({dragged.componentType})</span>
              </span>
            </div>
          );
        })(),
        document.body
      )}


      {/* Deleting the underlying library record only means something when there
          is a library; a snapshot's assets are its own bytes. */}
      {canDeleteFromLibrary && (
        <>
      <DialogImageDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "image"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        imageToDelete={mediaDeleteDialog.record}
        selectedImageID={mediaDeleteDialog.selectedId}
        images={_.get(props, "media.images", [])}
        setImages={() => { }}
        selectImage={() => { }}
        deleteImageFromMedia={props.deleteImageFromMedia}
        onAfterDelete={handleMediaDeletedAfter}
      />
      <DialogSoundDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "sound"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        soundToDelete={mediaDeleteDialog.record}
        selectedSoundID={mediaDeleteDialog.selectedId}
        sounds={props.sounds || []}
        setSounds={() => { }}
        selectSound={() => { }}
        deleteSoundFromSounds={props.deleteSoundFromSounds}
        onAfterDelete={handleMediaDeletedAfter}
      />
      <DialogVideoDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "video"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        videoToDelete={mediaDeleteDialog.record}
        selectedVideoID={mediaDeleteDialog.selectedId}
        videos={props.videos || []}
        setVideos={() => { }}
        selectVideo={() => { }}
        deleteVideoFromVideos={props.deleteVideoFromVideos}
        onAfterDelete={handleMediaDeletedAfter}
      />
      <DialogFontDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "font"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        fontToDelete={mediaDeleteDialog.record}
        selectedFontID={mediaDeleteDialog.selectedId}
        fonts={props.fonts || []}
        setFonts={() => { }}
        selectFont={() => { }}
        deleteFontFromFonts={props.deleteFontFromFonts}
        onAfterDelete={handleMediaDeletedAfter}
      />
      <DialogAtlasDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "atlas"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        animationSetToDeleteID={mediaDeleteDialog.groupId}
        atlases={props.atlases || []}
        setAtlases={() => { }}
        deleteAnimationSetFromAtlases={props.deleteAnimationSetFromAtlases}
        onAfterDelete={handleMediaDeletedAfter}
      />
      <DialogBitmapFontDelete
        isOpen={mediaDeleteDialog.open && mediaDeleteDialog.kind === "bitmap-font"}
        setIsOpen={(value) => { if (!value) closeMediaDeleteDialog(); }}
        createSnackbar={props.createSnackbar}
        fontSetToDeleteID={mediaDeleteDialog.groupId}
        bitmapFonts={props.bitmapFonts || []}
        setBitmapFonts={() => { }}
        deleteFontSetFromBitmapFonts={props.deleteFontSetFromBitmapFonts}
        onAfterDelete={handleMediaDeletedAfter}
      />
        </>
      )}

    </div>
  );

  /**
   * Recursively renders a component TreeItem plus its children.
   * Root-level uncategorized components are wrapped in an outer Card.
   *
   * @param {object} comp
   * @param {{ wrapInCard?: boolean, reorderScope?: string | null, canCategorize?: boolean }} [options]
   */
  function renderComponentNode(comp, options = {}) {
    const { wrapInCard = false, reorderScope = null, canCategorize = false, sortId = null,
      searchAncestorMatches = false } = options;
    const nameMatches = searchAncestorMatches || matchesTabSearch(comp.componentId, categorySearch);
    const childComps = (childrenMap[comp.componentId] || []).filter((child) =>
      !categorySearch.trim() || nameMatches ||
      matchingComponentIds.has(child.componentId)
    );
    const childReorderScope = orderKeyForParent(comp.componentId);
    const rowSortId = sortId || comp.componentId;

    const isDraggedAway =
      dragSession?.dragKind !== "category" &&
      dragSession?.scopeKey === reorderScope &&
      (dragSession?.draggedId === comp.componentId || dragSession?.draggedId === rowSortId);

    if (isDraggedAway) {
      return (
        <div
          key={comp.componentId}
          data-sort-id={rowSortId}
          style={{
            height: dragSession.slotHeight,
            borderRadius: "3px",
            border: "1px dashed rgba(144, 202, 249, 0.35)",
            backgroundColor: "rgba(144, 202, 249, 0.06)",
            boxSizing: "border-box",
          }}
          aria-hidden
        />
      );
    }

    const treeItem = (
      <TreeItem
        key={comp.componentId}
        itemId={comp.componentId}
        label={renderComponentTreeLabel(
          comp,
          reorderScope,
          !wrapInCard && !!comp.parentId,
          canCategorize
        )}
      >
        {/* ── Child components ─────────────────────────────── */}
        {childComps.length > 0 && (
          <div
            data-playengine-sort-list={childReorderScope}
            style={dragSession ? { overflowAnchor: "none" } : undefined}
          >
            {childComps.map((child) =>
              renderComponentNode(child, { reorderScope: childReorderScope, searchAncestorMatches: nameMatches })
            )}
          </div>
        )}
      </TreeItem>
    );

    return (
      <div key={comp.componentId} data-sort-id={rowSortId}>
        {treeItem}
      </div>
    );
  }

  /**
   * Renders the settings for a single component — bucket picker, asset
   * dependencies, config fields, and idle-animation toggle. Shown in the bottom
   * "properties" panel for the component currently selected in the tree.
   */
  function renderComponentSettings(comp) {
    // Count buckets only for the asset types this component depends on, broken
    // down per type (e.g. "image: 12 · sound: 3"). Sorting/empty handling in the
    // picker uses the total across these types.
    const depTypes = ASSET_TYPES.filter((t) =>
      (comp.dependencies || []).some((d) => d?.type === t)
    );
    const bucketNames = [...allBuckets, NO_BUCKET];
    const bucketCountDetails = {};
    for (const name of bucketNames) {
      const parts = depTypes.map((t) => ({ type: t, count: bucketCountsByType[t]?.[name] || 0 }));
      const total = parts.reduce((sum, p) => sum + p.count, 0);
      bucketCountDetails[name] = { total, parts };
    }

    return (
      <div style={{ paddingBottom: "8px" }}>
        {/* Sticky header — which component is being edited */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 10px",
            borderBottom: `1px solid ${SECTION_DIVIDER}`,
            backgroundColor: BAR_BG,
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <img
            src={InnerComponentIcon}
            alt="component"
            style={{ width: "16px", height: "16px", marginRight: "8px", flexShrink: 0, opacity: 0.85 }}
          />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#e6e6e6", fontSize: "13px" }}>
            <span style={{ fontWeight: 600 }}>{comp.componentId}</span>
            <span style={{ color: "#8a8d93" }}> · {comp.componentType}</span>
          </span>
        </div>

        <SectionHeader title="Assets" />
        <div style={{ padding: "0 6px" }}>
          {/* Bucket label — click to pick an existing bucket or add a new one */}
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
            {/* Buckets group library records; without a library there is
                nothing for them to group. */}
            {hasAssetLibrary && (
              <BucketPicker
                bucket={bucketMap[comp.componentId] || "nobucket"}
                buckets={allBuckets}
                bucketCountDetails={bucketCountDetails}
                onBucketChange={(val) => handleBucketChange(comp.componentId, val)}
              />
            )}
          </div>
            {(comp.dependencies ?? []).length === 0 ? (
              <Typography variant="body2" style={{ color: "#888" }}>
                No asset dependencies
              </Typography>
            ) : (
              (comp.dependencies ?? []).map((dep) => {
                const bucket = bucketMap[comp.componentId] || "nobucket";
                const bucketAssets = getAssetsForBucketAndType(bucket, dep.type);
                const busy = isDepBusy(comp.componentId, dep.key);
                // Resolve binding from saved config first (stays correct after rekeys).
                const boundAssetKey = resolveBoundAssetKey(
                  playEngineConfig,
                  comp.componentId,
                  dep.key,
                  dep,
                  selectedLanguage
                );
                const boundAssetEntry =
                  boundAssetKey ? assetsMap[boundAssetKey] : null;
                const rawMongoId = boundAssetEntry
                  ? mongoIdFromAssetEntry(boundAssetEntry)
                  : dep.sourceRef
                    ? dep.sourceRef.replace(/^@server:/, "")
                    : "";
                const currentMongoId = isPairDepType(dep.type)
                  ? rawMongoId
                  : (rawMongoId && bucketAssets.some((a) => String(a._id) === rawMongoId))
                    ? rawMongoId
                    : "";
                const inputId = `upload-${comp.componentId}-${dep.key}`;

                const pairConfig = isPairDepType(dep.type) ? getPairDepConfig(dep.type) : null;
                const pairGroups = pairConfig ? buildPairGroups(bucketAssets, pairConfig) : [];
                const currentPairGroup = pairConfig
                  ? (pairGroups.find((g) => g[pairConfig.groupRecordKey] === currentMongoId) ||
                    pairGroups.find((g) => String(g.imageRecord._id) === currentMongoId))
                  : null;
                const currentPairId = currentPairGroup?.[pairConfig?.groupRecordKey] ?? "";
                const staged = pairConfig ? getStagedAtlas(comp.componentId, dep.key) : {};
                const pairReady = pairConfig ? !!(staged.imageFile && staged[pairConfig.metaFileKey]) : false;
                const pairLabel = dep.type === "bitmap-font" ? "bitmap font" : "atlas";
                const assetKeyOptions = assetsByType[dep.type] || [];
                const showAssetKeyRow = assetKeyOptions.length > 0 || !!boundAssetKey;
                const assetKeySelectRow = showAssetKeyRow ? (
                  <div style={{ display: "flex", alignItems: "center", marginBottom: "6px" }}>
                    {assetKeyOptions.length > 0 ? (
                      <Select
                        style={{ width: "100%" }}
                        value={boundAssetKey || ""}
                        displayEmpty
                        disabled={busy}
                        onChange={(event) => {
                          if (props.userPermissions?.update === false) {
                            props.createSnackbar?.(
                              "You do not have permission to update, contact your administrator.",
                              "warning",
                              false
                            );
                            return;
                          }
                          const key = event.target.value;
                          if (key) bindDependencyToExistingAssetKey(comp.componentId, dep, key);
                        }}
                      >
                        <MenuItem value="" disabled>
                          <em>— set asset key —</em>
                        </MenuItem>
                        {boundAssetKey && !assetKeyOptions.some((o) => o.assetKey === boundAssetKey) && (
                          <MenuItem value={boundAssetKey}>
                            <code style={{ color: "#9ad6c4" }}>{boundAssetKey}</code>
                          </MenuItem>
                        )}
                        {assetKeyOptions.map(({ assetKey, entry }) => (
                          <MenuItem key={assetKey} value={assetKey}>
                            <Typography variant="body2" noWrap style={{ maxWidth: "220px" }}>
                              <code style={{ color: "#9ad6c4" }}>{assetKey}</code>
                              {entry.bucket && entry.bucket !== "nobucket" ? (
                                <span style={{ color: "#888", marginLeft: 6 }}>({entry.bucket})</span>
                              ) : null}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    ) : (
                      <Typography variant="caption" style={{ flex: 1, fontSize: "11px", color: "#9ad6c4" }}>
                        <code>{boundAssetKey}</code>
                      </Typography>
                    )}
                  </div>
                ) : null;

                const existingAssetKeySelect = assetKeySelectRow;

                return (
                  <div key={dep.key} style={{ marginBottom: "12px" }}>
                    <Typography
                      style={{ display: "block", color: "#e6e6e6", marginBottom: "4px", fontSize: "14px", fontWeight: 600 }}
                    >
                      {dep.key}&nbsp;
                      <span style={{ color: "#8a8d93", fontStyle: "italic", fontSize: "12px", fontWeight: 400 }}>
                        [{dep.type}]
                      </span>
                      {(dep.isSatisfied || !!dep.source || !!boundAssetKey) && (
                        <span style={{ color: "#4caf50", marginLeft: "6px" }}>✓</span>
                      )}
                    </Typography>
                    {boundAssetKey && (
                      <Typography
                        variant="caption"
                        style={{ display: "block", marginBottom: "4px", fontSize: "11px" }}
                      >
                        <span
                          role="link"
                          tabIndex={0}
                          title="Open this asset in the Assets tab"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            props.onNavigateToAsset?.(boundAssetKey);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              props.onNavigateToAsset?.(boundAssetKey);
                            }
                          }}
                          style={{
                            cursor: "pointer",
                            color: "#7e9bd6",
                            textDecoration: "underline",
                            textDecorationColor: "rgba(126, 155, 214, 0.5)",
                          }}
                        >
                          → asset{" "}
                          <code style={{ color: "#9ad6c4", textDecoration: "none" }}>
                            {boundAssetKey}
                          </code>
                        </span>
                      </Typography>
                    )}

                    {pairConfig ? (
                      /* Bitmap fonts get their own row ONLY when there is a
                         library to pick from and a font-generation endpoint to
                         call. Without one (the snapshot editor) every
                         control in that row is hidden and the dependency became
                         un-fillable — so fall through to the pair-upload row
                         below, which is already generic over `pairConfig` and
                         asks for the image + XML halves directly. */
                      dep.type === "bitmap-font" && hasAssetLibrary ? (
                        /* ── Bitmap font: select + generate-from-font (+) + delete (image-style row) ── */
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          {existingAssetKeySelect}
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {hasAssetLibrary && (
                            <Select
                              style={{ width: "100%" }}
                              value={currentPairId}
                              displayEmpty
                              disabled={busy}
                              renderValue={(value) => renderPairSelectValue(
                                value,
                                pairGroups,
                                pairConfig,
                                `— select ${pairLabel} —`
                              )}
                              onChange={(event) => {
                                if (props.userPermissions?.update === false) {
                                  props.createSnackbar?.(
                                    "You do not have permission to update, contact your administrator.",
                                    "warning", false
                                  );
                                  return;
                                }
                                const group = pairGroups.find((g) => g[pairConfig.groupRecordKey] === event.target.value);
                                if (group) handleSelectPair(comp.componentId, dep, group, pairConfig);
                              }}
                            >
                              <MenuItem value="" disabled>
                                <em>{`— select ${pairLabel} —`}</em>
                              </MenuItem>
                              {pairGroups.map((g) => (
                                <MenuItem
                                  key={g[pairConfig.groupRecordKey]}
                                  value={g[pairConfig.groupRecordKey]}
                                  style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                    {g.imageRecord.thumbFile && (
                                      <img
                                        src={`${pairConfig.staticFolder}/${g.imageRecord.thumbFile}`}
                                        alt=""
                                        style={{ height: "30px", maxWidth: "60px", objectFit: "contain", marginRight: "8px" }}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                      />
                                    )}
                                    <Typography variant="body2" noWrap style={{ maxWidth: "180px" }}>
                                      {g.imageRecord.uploadedFileName || g.imageRecord.file}
                                    </Typography>
                                  </div>
                                  <DropdownRemoveButton
                                    onClick={(e) => openDropdownDelete(e, () => openMediaDelete(
                                      mediaDeleteKindForDepType(dep.type),
                                      g.imageRecord,
                                      currentPairId,
                                      g[pairConfig.groupRecordKey],
                                      {
                                        scope: "dependency",
                                        componentId: comp.componentId,
                                        dep,
                                        bucket,
                                        boundAssetKey,
                                        deletedId: g.imageRecord._id,
                                        deletedGroupId: g[pairConfig.groupRecordKey],
                                        pairConfig,
                                        wasSelected: currentPairId === g[pairConfig.groupRecordKey],
                                      }
                                    ))}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                            )}

                            {/* Generating a bitmap font renders it server-side and
                                stores it in the library, so it only exists where
                                there is one. Elsewhere bitmap fonts are picked
                                from ready .bm.png/.bm.xml pairs in the Assets tab. */}
                            {hasAssetLibrary && (
                            <>
                            {/* Hidden font input → auto-generate a bitmap font */}
                            <input
                              id={`${inputId}-genfont`}
                              type="file"
                              hidden
                              accept=".ttf,.otf,.woff,.woff2"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleGenerateBitmapFromFont(comp.componentId, dep, f);
                                e.target.value = "";
                              }}
                            />
                            <IconButton
                              size="small"
                              disabled={busy}
                              title="Upload a font (TTF/OTF/WOFF) and auto-generate a bitmap font"
                              style={{ marginLeft: "4px", padding: "4px" }}
                              onClick={() => {
                                if (props.userPermissions?.insert === false) {
                                  props.createSnackbar?.("You are not authorized to upload.", "warning", false);
                                  return;
                                }
                                document.getElementById(`${inputId}-genfont`)?.click();
                              }}
                            >
                              {busy ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />}
                            </IconButton>
                            </>
                            )}
                          </div>
                        </div>
                      ) : (
                        /* ── Atlas: grouped select + two-file upload ── */
                        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                          {existingAssetKeySelect}
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {hasAssetLibrary && (
                            <Select
                              style={{ width: "100%" }}
                              value={currentPairId}
                              displayEmpty
                              disabled={busy}
                              renderValue={(value) => renderPairSelectValue(
                                value,
                                pairGroups,
                                pairConfig,
                                `— select ${pairLabel} —`
                              )}
                              onChange={(event) => {
                                if (props.userPermissions?.update === false) {
                                  props.createSnackbar?.(
                                    "You do not have permission to update, contact your administrator.",
                                    "warning", false
                                  );
                                  return;
                                }
                                const group = pairGroups.find((g) => g[pairConfig.groupRecordKey] === event.target.value);
                                if (group) handleSelectPair(comp.componentId, dep, group, pairConfig);
                              }}
                            >
                              <MenuItem value="" disabled>
                                <em>{`— select ${pairLabel} —`}</em>
                              </MenuItem>
                              {pairGroups.map((g) => (
                                <MenuItem
                                  key={g[pairConfig.groupRecordKey]}
                                  value={g[pairConfig.groupRecordKey]}
                                  style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                                >
                                  <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                    {g.imageRecord.thumbFile && (
                                      <img
                                        src={`${pairConfig.staticFolder}/${g.imageRecord.thumbFile}`}
                                        alt=""
                                        style={{ height: "30px", maxWidth: "60px", objectFit: "contain", marginRight: "8px" }}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                      />
                                    )}
                                    <Typography variant="body2" noWrap style={{ maxWidth: "180px" }}>
                                      {g.imageRecord.uploadedFileName || g.imageRecord.file}
                                    </Typography>
                                  </div>
                                  <DropdownRemoveButton
                                    onClick={(e) => openDropdownDelete(e, () => openMediaDelete(
                                      mediaDeleteKindForDepType(dep.type),
                                      g.imageRecord,
                                      currentPairId,
                                      g[pairConfig.groupRecordKey],
                                      {
                                        scope: "dependency",
                                        componentId: comp.componentId,
                                        dep,
                                        bucket,
                                        boundAssetKey,
                                        deletedId: g.imageRecord._id,
                                        deletedGroupId: g[pairConfig.groupRecordKey],
                                        pairConfig,
                                        wasSelected: currentPairId === g[pairConfig.groupRecordKey],
                                      }
                                    ))}
                                  />
                                </MenuItem>
                              ))}
                            </Select>
                            )}
                          </div>

                          <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
                            <input
                              id={`${inputId}-img`}
                              type="file"
                              hidden
                              accept=".png,.jpg,.jpeg"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) stageAtlasFile(comp.componentId, dep.key, "imageFile", f);
                                e.target.value = "";
                              }}
                            />
                            <input
                              id={`${inputId}-${pairConfig.metaKey}`}
                              type="file"
                              hidden
                              accept={pairConfig.metaAccept}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) stageAtlasFile(comp.componentId, dep.key, pairConfig.metaFileKey, f);
                                e.target.value = "";
                              }}
                            />
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              onClick={() => {
                                if (props.userPermissions?.insert === false) {
                                  props.createSnackbar?.("You are not authorized to upload.", "warning", false);
                                  return;
                                }
                                document.getElementById(`${inputId}-img`)?.click();
                              }}
                              style={{
                                fontSize: "10px", padding: "2px 6px", minWidth: 0,
                                color: staged.imageFile ? "#4caf50" : undefined,
                                borderColor: staged.imageFile ? "#4caf50" : undefined,
                              }}
                            >
                              {staged.imageFile ? "✓ IMG" : "+ IMG"}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              onClick={() => {
                                if (props.userPermissions?.insert === false) {
                                  props.createSnackbar?.("You are not authorized to upload.", "warning", false);
                                  return;
                                }
                                document.getElementById(`${inputId}-${pairConfig.metaKey}`)?.click();
                              }}
                              style={{
                                fontSize: "10px", padding: "2px 6px", minWidth: 0,
                                color: staged[pairConfig.metaFileKey] ? "#4caf50" : undefined,
                                borderColor: staged[pairConfig.metaFileKey] ? "#4caf50" : undefined,
                              }}
                            >
                              {staged[pairConfig.metaFileKey] ? `✓ ${pairConfig.metaLabel}` : `+ ${pairConfig.metaLabel}`}
                            </Button>
                            <IconButton
                              size="small"
                              disabled={busy || !pairReady}
                              title={pairReady ? `Upload ${pairLabel}` : `Select both image and ${pairConfig.metaLabel} first`}
                              style={{ marginLeft: "2px", padding: "4px" }}
                              onClick={() => {
                                if (props.userPermissions?.insert === false) {
                                  props.createSnackbar?.("You are not authorized to upload.", "warning", false);
                                  return;
                                }
                                handleUploadPair(
                                  comp.componentId,
                                  dep,
                                  staged.imageFile,
                                  staged[pairConfig.metaFileKey],
                                  pairConfig
                                );
                              }}
                            >
                              {busy ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />}
                            </IconButton>
                            {providerPickFromLibrary && (
                              <Button
                                size="small"
                                variant="outlined"
                                disabled={busy}
                                title={`Pick a ${pairLabel} from the asset library`}
                                onClick={() => handlePickFromLibrary(comp.componentId, dep)}
                                style={{ fontSize: "10px", padding: "2px 6px", minWidth: 0, marginLeft: "2px" }}
                              >
                                Library
                              </Button>
                            )}
                          </div>

                          {(staged.imageFile || staged[pairConfig.metaFileKey]) && (
                            <Typography variant="caption" style={{ color: "#888", fontSize: "10px" }}>
                              {staged.imageFile?.name ?? "—"} + {staged[pairConfig.metaFileKey]?.name ?? "—"}
                              {pairReady && " · click ＋ to upload"}
                            </Typography>
                          )}
                        </div>
                      )
                    ) : (
                      /* ── Non-atlas: bind existing key, then fork via media select + upload ── */
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}>
                        {existingAssetKeySelect}
                        <div style={{ display: "flex", alignItems: "center" }}>
                          {hasAssetLibrary && (
                          <Select
                            style={{ width: "100%" }}
                            value={currentMongoId}
                            displayEmpty
                            disabled={busy}
                            renderValue={(value) => renderMediaSelectValue(
                              value,
                              bucketAssets,
                              getAssetFolder(dep.type),
                              "— select asset —"
                            )}
                            onChange={(event) => {
                              if (props.userPermissions?.update === false) {
                                props.createSnackbar?.(
                                  "You do not have permission to update, contact your administrator.",
                                  "warning",
                                  false
                                );
                                return;
                              }
                              const selectedId = event.target.value;
                              const record = bucketAssets.find(
                                (img) => String(img._id) === selectedId
                              );
                              if (record) {
                                handleSelectAsset(comp.componentId, dep, record);
                              }
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>— select asset —</em>
                            </MenuItem>
                            {bucketAssets.map((img) => (
                              <MenuItem
                                key={String(img._id)}
                                value={String(img._id)}
                                style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
                              >
                                <div style={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                  {img.thumbFile && (
                                    <img
                                      src={`${getAssetFolder(dep.type)}/${img.thumbFile}`}
                                      alt=""
                                      style={{
                                        height: "30px",
                                        maxWidth: "60px",
                                        objectFit: "contain",
                                        marginRight: "8px",
                                      }}
                                      onError={(e) => { e.target.style.display = "none"; }}
                                    />
                                  )}
                                  <Typography variant="body2" noWrap style={{ maxWidth: "180px" }}>
                                    {img.uploadedFileName || img.fontFamily || img.name || img.file}
                                  </Typography>
                                </div>
                                <DropdownRemoveButton
                                  onClick={(e) => openDropdownDelete(e, () => openMediaDelete(
                                    mediaDeleteKindForDepType(dep.type),
                                    img,
                                    currentMongoId,
                                    null,
                                    {
                                      scope: "dependency",
                                      componentId: comp.componentId,
                                      dep,
                                      bucket,
                                      boundAssetKey,
                                      deletedId: img._id,
                                      deletedGroupId: null,
                                      pairConfig: null,
                                      wasSelected: String(currentMongoId) === String(img._id),
                                    }
                                  ))}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                          )}

                          {/* Hidden file input */}
                          <input
                            id={inputId}
                            type="file"
                            hidden
                            accept={getAcceptString(dep.type)}
                            onChange={(event) => {
                              const file = event.target.files?.[0];
                              if (!file) return;
                              handleUploadAsset(comp.componentId, dep, file);
                              event.target.value = "";
                            }}
                          />

                          <IconButton
                            size="small"
                            disabled={busy}
                            style={{ marginLeft: "4px", padding: "4px" }}
                            onClick={() => {
                              if (props.userPermissions?.insert === false) {
                                props.createSnackbar?.(
                                  "You are not authorized to upload.",
                                  "warning",
                                  false
                                );
                                return;
                              }
                              document.getElementById(inputId)?.click();
                            }}
                          >
                            {busy ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />}
                          </IconButton>

                          {providerPickFromLibrary && (
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              title={`Pick a ${dep.type} from the asset library`}
                              onClick={() => handlePickFromLibrary(comp.componentId, dep)}
                              style={{ fontSize: "10px", padding: "2px 6px", minWidth: 0, marginLeft: "4px" }}
                            >
                              Library
                            </Button>
                          )}

                          {/* ── Sound preview button ── */}
                          {dep.type === "audio" && currentMongoId && (() => {
                            const audioRecord = bucketAssets.find((a) => String(a._id) === currentMongoId);
                            if (!audioRecord) return null;
                            const depPlayKey = `${comp.componentId}:${dep.key}`;
                            const isPlaying = playingDepKey === depPlayKey;
                            return (
                              <IconButton
                                size="small"
                                title={isPlaying ? "Stop preview" : "Preview sound"}
                                style={{ marginLeft: "2px", padding: "4px", color: isPlaying ? "#ef5350" : "#90caf9" }}
                                onClick={() => handleToggleSoundPreview(comp.componentId, dep, audioRecord)}
                              >
                                {isPlaying ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                              </IconButton>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
        </div>

        {/* ── Transform (position / scale / rotation) ─────── */}
        {comp.config && TRANSFORM_KEYS.some((k) => k in comp.config && settingsKeyMatches(comp, k)) && (
          <>
            <SectionHeader title="Transform" />
            <div>
              {TRANSFORM_KEYS
                .filter((k) => k in comp.config && settingsKeyMatches(comp, k))
                .map((key) =>
                  renderConfigField(comp.componentId, key, comp.config[key], comp.configSchema)
                )}
            </div>
          </>
        )}

        {/* ── Config fields — "Config" plus one section per schema group ── */}
        {comp.config &&
          renderConfigSections(
            sortedConfigKeys(comp).filter((key) => settingsKeyMatches(comp, key)),
            comp.configSchema,
            (key) => renderConfigField(comp.componentId, key, comp.config[key], comp.configSchema)
          )}

        {shouldShowIdleAnimationToggle(
          _.get(playEngineConfig, "eventBindings"),
          playEngineConfig,
          comp.componentId
        ) && (
          <>
            <SectionHeader title="Idle" />
            {(() => {
              const idleInfo = idleAnimationByComponent[comp.componentId];
              const idleLabel = idleInfo?.label ? ` (${idleInfo.label})` : "";
              const idleEnabled = getIdleAnimationEnabled(playEngineConfig, comp.componentId);
              const idleReadOnly = props.userPermissions?.update === false;
              return (
                <FieldRow label={`Idle animation${idleLabel}`} readOnly={idleReadOnly}>
                  <Switch
                    size="small"
                    checked={idleEnabled}
                    disabled={idleReadOnly}
                    onChange={(e) => {
                      handleIdleAnimationToggle(
                        comp.componentId,
                        e.target.checked,
                        idleInfo?.replayArgs
                      );
                    }}
                    color="primary"
                  />
                </FieldRow>
              );
            })()}
          </>
        )}
      </div>
    );
  }

  /**
   * Multi-select properties: shows only the config/transform fields common to
   * ALL selected components; editing any field applies to every selection.
   */
  function renderMultiComponentSettings(comps) {
    const ids = comps.map((c) => c.componentId);
    const schema = comps[0].configSchema;

    // Resolved field type for a key on a component (normalizes always-responsive).
    const keyFieldType = (comp, key) => {
      const fs = comp.configSchema?.[key];
      const always = ALWAYS_RESPONSIVE_KEYS.has(key) || fs?.alwaysResponsive === true;
      const eff = always ? toAlwaysResponsiveValue(key, comp.config[key]) : comp.config[key];
      return resolveFieldType(key, eff, fs?.type);
    };

    // A key is editable-in-common when every selected component has it and all
    // resolve to the same field type (so one control is valid for all).
    const isCommonKey = (key) => {
      if (!comps.every((c) => c.config && key in c.config)) return false;
      const t0 = keyFieldType(comps[0], key);
      return !!t0 && comps.every((c) => keyFieldType(c, key) === t0);
    };

    const commonKeys = Object.keys(comps[0].config || {}).filter(
      (k) => k !== "idleAnimation" && !HIDDEN_CONFIG_KEYS.has(k) && isCommonKey(k)
    );
    const transformKeys = TRANSFORM_KEYS.filter((k) => commonKeys.includes(k));
    const configKeys = commonKeys
      .filter((k) => !TRANSFORM_KEYS.includes(k))
      .sort(
        (a, b) =>
          configFieldRank(comps[0].componentId, a, comps[0].config[a], schema) -
          configFieldRank(comps[0].componentId, b, comps[0].config[b], schema)
      );

    const renderKey = (key) => {
      const values = comps.map((c) => c.config[key]);
      const mixed = !values.every((v) => _.isEqual(v, values[0]));
      return renderConfigField(comps[0].componentId, key, values[0], schema, undefined, {
        targetIds: ids,
        mixed,
      });
    };

    return (
      <div style={{ paddingBottom: "8px" }}>
        {/* Sticky header — how many components are being edited */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 10px",
            borderBottom: `1px solid ${SECTION_DIVIDER}`,
            backgroundColor: "#3a3a41",
            position: "sticky",
            top: 0,
            zIndex: 1,
          }}
        >
          <img
            src={InnerComponentIcon}
            alt="components"
            style={{ width: "16px", height: "16px", marginRight: "8px", flexShrink: 0, opacity: 0.85 }}
          />
          <span style={{ color: "#e6e6e6", fontSize: "13px", fontWeight: 600 }}>
            {comps.length} components selected
          </span>
        </div>

        {transformKeys.length > 0 && (
          <>
            <SectionHeader title="Transform" />
            <div>{transformKeys.map(renderKey)}</div>
          </>
        )}

        {renderConfigSections(configKeys, schema, renderKey)}

        {transformKeys.length === 0 && configKeys.length === 0 && (
          <Typography
            variant="body2"
            style={{ color: "#777", textAlign: "center", padding: "24px 16px", display: "block" }}
          >
            No common editable fields across the selected components
          </Typography>
        )}
      </div>
    );
  }

  /**
   * Recursively renders a category sub-tree node as a TreeItem.
   * Called for every level below the top-level Card-wrapped group.
   *
   * @param {string} name       - Display name for this node
   * @param {object} node       - { children: {...}, components: [] }
   * @param {string} pathPrefix - Unique prefix for itemId (prevents collisions across branches)
   * @param {string} categoryPath - Colon path used for drop targets and reorder scope
   * @param {string} parentPath - Parent folder path for sibling reorder scope
   */
  function renderCategorySubTree(name, node, pathPrefix, categoryPath, parentPath) {
    const itemId = `cat-${pathPrefix}`;
    const categoryReorderScope = orderKeyForCategory(categoryPath);
    const sortedComponents = sortComponentsInCategory(node, categoryPath);
    const folderReorderScope = orderKeyForCategoryFolderSiblings(parentPath);
    const isDraggedAway =
      dragSession?.dragKind === "category" &&
      dragSession.scopeKey === folderReorderScope &&
      dragSession.draggedId === name;

    if (isDraggedAway) {
      return (
        <div
          key={`${itemId}-slot`}
          data-sort-id={name}
          style={{
            height: dragSession.slotHeight,
            margin: "2px 0",
            borderRadius: "4px",
            border: "1px dashed rgba(144, 202, 249, 0.35)",
            backgroundColor: "rgba(144, 202, 249, 0.06)",
            boxSizing: "border-box",
          }}
          aria-hidden
        />
      );
    }

    const sortedChildNames = sortCategoryChildNames(categoryPath, Object.keys(node.children || {}));

    return (
      <div key={itemId} data-sort-id={name}>
        <TreeItem
          itemId={itemId}
          label={renderCategoryFolderLabel(name, categoryPath, {
            small: true,
            parentPath,
            canReorder: !categorySearch.trim(),
          })}
        >
          <div
            data-playengine-sort-list={categoryReorderScope}
            style={dragSession ? { overflowAnchor: "none" } : undefined}
          >
            {sortedComponents.map((comp) =>
              renderComponentNode(comp, {
                reorderScope: categoryReorderScope,
                canCategorize: true,
                searchAncestorMatches: matchesTabSearch(categoryPath, categorySearch),
              })
            )}
          </div>
          <div
            data-playengine-sort-list={orderKeyForCategoryFolderSiblings(categoryPath)}
            style={dragSession ? { overflowAnchor: "none" } : undefined}
          >
            {sortedChildNames.map((childName) =>
              renderCategorySubTree(
                childName,
                node.children[childName],
                `${pathPrefix}-${childName}`,
                `${categoryPath}:${childName}`,
                categoryPath
              )
            )}
          </div>
        </TreeItem>
      </div>
    );
  }
}

// The inspector's building blocks, for panels that must look like this one
// (the pe3d editor, components/pe3d/pe3dPanelKit.js). Behaviour is unchanged.
export { SectionHeader, FieldRow, StringConfigField, ColorConfigField, NumberConfigField };
